import { useState, useCallback, useRef, useEffect } from "react";
import "@/App.css";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar as CalendarComponent } from "@/components/ui/calendar";
import { Search, Upload, Download, FileSpreadsheet, Image, CheckCircle, XCircle, Loader2, Barcode, Sparkles, ZoomIn, Palette, X, Wand2, History, Trash2, Clipboard, Copy, Check, Calendar, LayoutTemplate, Plus, Edit, FolderOpen, DollarSign, TrendingDown, Store, Truck, ShoppingCart, Building, Package, Percent, FileText, ArrowRight, RefreshCw, AlertCircle, ChevronDown, ChevronUp, MapPin, BarChart3, MessageSquare, Send, TrendingUp, Menu, ChevronLeft, ChevronRight, PanelLeftClose, PanelLeft, AlertTriangle, Eye, ArrowDownCircle, Key, Wallet, Lock, Database, Camera, Clock, User, CreditCard, GripVertical, QrCode, Calculator, Scale } from "lucide-react";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, BarChart, Bar, ComposedChart } from 'recharts';

// Drag and Drop
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { BACKEND_URL as __BACKEND_URL__ } from "./lib/backend-url";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// Refactored Components
import { StockPage } from "@/pages/StockPage";
import { PnLPage } from "@/pages/PnL";
import { CobrancasPage } from "@/pages/Cobrancas";
import { StockDualValue } from "@/components/Stock/StockDualValue";
import { CashControlPage } from "@/pages/CashControl";

// New Modular Components
import { Sidebar } from "@/components/layout";
import { PasswordModal } from "@/components/auth";
import { LoadingSpinner, DateRangeFilter } from "@/components/common";
import { ExpiringProductsModal, ImageViewerModal, NfeHistoryModal } from "@/components/modals";
import { 
  ImportTab, 
  ProductAnalysis, 
  TopProducts, 
  SalesChart, 
  DashboardMetrics,
  AlertsSection,
  SalesHeatmap,
  PurchasingTab,
  PurchaseOrdersTab
} from "@/pages/Dashboard";
import { PaymentMethodsCard, PeriodFilter } from "@/components/Dashboard";

const BACKEND_URL = __BACKEND_URL__;
const API = `${BACKEND_URL}/api`;

// Helper for dashboard API with v3 -> v2 -> v1 fallback (production-safe)
const fetchDashboardWithFallback = async (params, signal = null) => {
  const config = signal ? { signal } : {};
  // Use v4 only (PostgreSQL Direct - Source of Truth)
  try {
    const response = await axios.get(`${API}/sales/v4/dashboard?${params.toString()}`, config);
    console.log('[Dashboard] Using v4 API (PostgreSQL Direct - Source of Truth)');
    return response;
  } catch (e) {
    if (axios.isCancel(e)) throw e;
    console.error('[Dashboard] V4 API error:', e.message);
    throw e;
  }
};

// Helper function to convert image URL to base64 using canvas
const imageUrlToBase64 = async (imageUrl) => {
  if (!imageUrl) return null;
  
  return new Promise((resolve) => {
    const img = new window.Image();
    img.crossOrigin = "anonymous"; // Try to request CORS
    
    img.onload = () => {
      try {
        const canvas = document.createElement("canvas");
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0);
        const dataUrl = canvas.toDataURL("image/png");
        resolve(dataUrl);
      } catch (e) {
        console.log("Canvas conversion failed (CORS):", e);
        resolve(null);
      }
    };
    
    img.onerror = () => {
      console.log("Image load failed");
      resolve(null);
    };
    
    // Add timestamp to bypass cache and trigger fresh CORS check
    img.src = imageUrl + (imageUrl.includes('?') ? '&' : '?') + '_t=' + Date.now();
    
    // Timeout fallback
    setTimeout(() => resolve(null), 10000);
  });
};

// Sortable Card Component for Dashboard
const SortableCard = ({ id, children }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 1000 : 'auto',
  };

  return (
    <div ref={setNodeRef} style={style} className="group relative">
      <div
        {...attributes}
        {...listeners}
        className="absolute left-2 top-2 p-1.5 rounded-md bg-slate-100/80 hover:bg-slate-200 text-slate-400 hover:text-slate-600 cursor-grab active:cursor-grabbing z-20 opacity-0 group-hover:opacity-100 transition-opacity"
        title="Arraste para reordenar"
      >
        <GripVertical className="w-4 h-4" />
      </div>
      {children}
    </div>
  );
};

function App() {
  // Sidebar navigation - Entrada de Mercadoria is now default
  const [activeSection, setActiveSection] = useState("precificacao"); // dashboards, precificacao
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  // Universal password for protected sections
  const PROTECTED_PASSWORD = "1Q2w3e4r5t@";
  
  // Dashboard authentication
  const [dashboardAuthenticated, setDashboardAuthenticated] = useState(false);
  const [dashboardPassword, setDashboardPassword] = useState("");
  const [dashboardAuthError, setDashboardAuthError] = useState("");
  
  // Dashboard tabs
  const [dashboardTab, setDashboardTab] = useState("vendas_diarias"); // vendas_diarias, importar
  
  // Store selection - uses API (persisted in localStorage to survive re-renders/scroll)
  const [selectedStoreId, setSelectedStoreId] = useState(""); // empty = all stores
  const [selectedStoreIds, setSelectedStoreIds] = useState(() => {
    try {
      const saved = localStorage.getItem('selectedStoreIds');
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });
  const [stores, setStores] = useState([]);
  const [refreshTrigger, setRefreshTrigger] = useState(0); // Force refresh trigger

  // Persist selectedStoreIds to localStorage so the dashboard never loses selection
  useEffect(() => {
    try {
      localStorage.setItem('selectedStoreIds', JSON.stringify(selectedStoreIds));
    } catch (e) {
      // localStorage may be disabled - silently ignore
    }
  }, [selectedStoreIds]);
  
  // Store comparison chart data
  const [storeComparisonData, setStoreComparisonData] = useState([]);
  const [loadingStoreComparison, setLoadingStoreComparison] = useState(false);
  
  // Import store selection
  const [importStoreId, setImportStoreId] = useState("vencidinho_cangaiba");
  const [importingFromDb, setImportingFromDb] = useState(false);
  const [syncingAllStores, setSyncingAllStores] = useState(false);
  const [lastSyncTime, setLastSyncTime] = useState(null);
  const [pgStores, setPgStores] = useState([]);
  
  // Helper to toggle store selection (checkbox behavior)
  const toggleStoreSelection = useCallback((storeId) => {
    setSelectedStoreIds(prev => {
      if (prev.includes(storeId)) {
        return prev.filter(id => id !== storeId);
      } else {
        return [...prev, storeId];
      }
    });
  }, []);
  
  // Select all stores
  const selectAllStores = useCallback(async () => {
    // If stores are not loaded yet, try to load them first
    if (stores.length === 0) {
      try {
        const response = await axios.get(`${API}/stores`);
        const loadedStores = response.data.stores || [];
        if (loadedStores.length > 0) {
          setStores(loadedStores);
          const allStoreIds = loadedStores.map(s => s.id);
          setSelectedStoreIds(allStoreIds);
          return;
        }
      } catch (error) {
        console.error('[selectAllStores] Error loading stores:', error);
      }
      return;
    }
    
    const allStoreIds = stores.map(s => s.id);
    if (allStoreIds.length === 0) return;
    setSelectedStoreIds(allStoreIds);
  }, [stores]);
  
  // Clear all store selections
  const clearStoreSelection = useCallback(() => {
    setSelectedStoreIds([]);
  }, []);
  
  // Check if all stores are selected
  const isAllStoresSelected = selectedStoreIds.length === stores.length && stores.length > 0;
  
  // Daily comparison data
  const [dailyComparison, setDailyComparison] = useState(null);
  const [loadingComparison, setLoadingComparison] = useState(false);
  const [dailyComparisonDate, setDailyComparisonDate] = useState(null); // Selected date for daily comparison
  
  // Markup metrics
  const [markupMetrics, setMarkupMetrics] = useState(null);
  const [loadingMarkup, setLoadingMarkup] = useState(false);
  
  // Selected product for ticket analysis
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [productTicket, setProductTicket] = useState(null);
  const [loadingProductTicket, setLoadingProductTicket] = useState(false);
  const [selectedProductMarkup, setSelectedProductMarkup] = useState(null);
  const [loadingSelectedProductMarkup, setLoadingSelectedProductMarkup] = useState(false);
  const [selectedProductStock, setSelectedProductStock] = useState(null);
  const [loadingProductStock, setLoadingProductStock] = useState(false);
  const [basketAnalysis, setBasketAnalysis] = useState(null);
  const [loadingBasketAnalysis, setLoadingBasketAnalysis] = useState(false);
  // Daily sales for selected product
  const [productDailySales, setProductDailySales] = useState(null);
  const [loadingProductDailySales, setLoadingProductDailySales] = useState(false);
  const [productDailyPeriod, setProductDailyPeriod] = useState("30");
  const [productDailyCustomStart, setProductDailyCustomStart] = useState(null);
  const [productDailyCustomEnd, setProductDailyCustomEnd] = useState(null);
  const [topMarkupProducts, setTopMarkupProducts] = useState([]);
  const [loadingTopMarkupProducts, setLoadingTopMarkupProducts] = useState(false);
  const [markupSortAsc, setMarkupSortAsc] = useState(false); // false = best markup first, true = worst markup first
  const [markupProductsLimit, setMarkupProductsLimit] = useState(10);
  
  // Revenue projection
  const [revenueProjection, setRevenueProjection] = useState(null);
  const [loadingRevenueProjection, setLoadingRevenueProjection] = useState(false);
  
  // Selected ticket range for Pareto analysis
  const [selectedTicketRange, setSelectedTicketRange] = useState(null);
  const [ticketRangeProducts, setTicketRangeProducts] = useState(null);
  const [loadingTicketRangeProducts, setLoadingTicketRangeProducts] = useState(false);
  
  // Payment methods breakdown
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [loadingPaymentMethods, setLoadingPaymentMethods] = useState(false);
  
  // Stock management states
  const [stockProducts, setStockProducts] = useState([]);
  const [loadingStock, setLoadingStock] = useState(false);
  const [stockSearch, setStockSearch] = useState("");
  const [showLowStockOnly, setShowLowStockOnly] = useState(false);
  const [lowStockProducts, setLowStockProducts] = useState([]);
  const [expiringProducts, setExpiringProducts] = useState([]);
  const [slowSellingProducts, setSlowSellingProducts] = useState([]);
  const [loadingSlowSelling, setLoadingSlowSelling] = useState(false);
  const [selectedStockProduct, setSelectedStockProduct] = useState(null);
  const [stockProductDetail, setStockProductDetail] = useState(null);
  const [loadingStockDetail, setLoadingStockDetail] = useState(false);
  const [editingMinStock, setEditingMinStock] = useState(null);
  const [newMinStock, setNewMinStock] = useState("");
  const [stockStoreId, setStockStoreId] = useState("vencidinho_cangaiba"); // Default to Vencidinho Cangaíba
  const [editingProductImage, setEditingProductImage] = useState(null); // EAN of product being edited
  const [showExpiringModal, setShowExpiringModal] = useState(false); // Modal for expiring products
  
  // Dashboard card order (drag and drop)
  const DASHBOARD_STORAGE_KEY = 'dashboard_card_order';
  const DEFAULT_CARD_ORDER = [
    'faturamento',
    'markup',
    'payment-methods',
    'chart',
    'heatmap',
    'peak-hours',
    'week-summary',
    'ticket-analysis',
    'detailed-analysis',
    'top-products',
    'store-comparison',
    'slow-selling',
    'expiring',
    'sales-by-day',
    'ai-chat',
    'import-history',
    'generated-arts'
  ];
  
  const [dashboardCardOrder, setDashboardCardOrder] = useState(() => {
    const saved = localStorage.getItem(DASHBOARD_STORAGE_KEY);
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch {
        return DEFAULT_CARD_ORDER;
      }
    }
    return DEFAULT_CARD_ORDER;
  });
  
  // Drag and drop sensors
  const dndSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );
  
  const handleDashboardDragEnd = (event) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      setDashboardCardOrder((items) => {
        const oldIndex = items.indexOf(active.id);
        const newIndex = items.indexOf(over.id);
        const newOrder = arrayMove(items, oldIndex, newIndex);
        localStorage.setItem(DASHBOARD_STORAGE_KEY, JSON.stringify(newOrder));
        return newOrder;
      });
    }
  };

  // Sales target settings
  const [salesTargetSettings, setSalesTargetSettings] = useState({ sell_through_days: 7, alert_consecutive_days: 2 });
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  
  // Top products limit
  const [topProductsLimit, setTopProductsLimit] = useState(10);
  
  // Individual card date filters (days back from today)
  const [topProductsDays, setTopProductsDays] = useState(7);
  const [ticketDays, setTicketDays] = useState(7);
  const [heatmapDays, setHeatmapDays] = useState(30);
  const [chartDays, setChartDays] = useState(30);
  
  // Chart data
  const [chartData, setChartData] = useState([]);
  const [loadingChart, setLoadingChart] = useState(false);
  
  // Ticket analysis data
  const [ticketAnalysis, setTicketAnalysis] = useState(null);
  const [loadingTicketAnalysis, setLoadingTicketAnalysis] = useState(false);
  
  // Product search
  const [productSearch, setProductSearch] = useState("");
  const [showProductSearch, setShowProductSearch] = useState(false);
  
  const [singleEan, setSingleEan] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploadedItems, setUploadedItems] = useState([]);
  const [processingProgress, setProcessingProgress] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  
  // Scanner mode detection
  const [scannerMode, setScannerMode] = useState(false);
  const lastKeyTime = useRef(0);
  const keyBuffer = useRef("");
  const [searchingImage, setSearchingImage] = useState(null); // EAN being searched for image
  
  // Image viewer modal
  const [viewerOpen, setViewerOpen] = useState(false);
  const [viewerImage, setViewerImage] = useState(null);
  const [viewerProduct, setViewerProduct] = useState(null);
  const [zoomLevel, setZoomLevel] = useState(1);
  
  // Price Search
  const [showPriceModal, setShowPriceModal] = useState(false);
  const [selectedProductForPrice, setSelectedProductForPrice] = useState(null);
  const [priceMarketTypes, setPriceMarketTypes] = useState([]);
  const [markupPercentage, setMarkupPercentage] = useState("");
  const [productCost, setProductCost] = useState("");
  const [sellingPrice, setSellingPrice] = useState("");
  const [priceInputMode, setPriceInputMode] = useState("markup"); // "markup" or "price"
  const [searchingPrices, setSearchingPrices] = useState(false);
  const [priceResults, setPriceResults] = useState(null);
  const [selectedStoreLocation, setSelectedStoreLocation] = useState("vencidinho_cangaiba"); // bestby_sjc, bestby_jacarei, vencidinho_cangaiba - default to Vencidinho
  
  // Purchase Simulation (in Price Modal) - Auto-loaded from last NF-e
  const [purchaseSimulation, setPurchaseSimulation] = useState(null);
  const [loadingPurchaseSimulation, setLoadingPurchaseSimulation] = useState(false);
  
  // Store locations for price search - Vencidinho first as default
  const STORE_LOCATIONS = [
    { id: "vencidinho_cangaiba", name: "Vencidinho Cangaíba", address: "Av. Cangaíba, 2100", cnpj: "33398343000105" },
    { id: "bestby_sjc", name: "Best By SJC", address: "Av. Iguape, 460 - Jardim Satélite, São José dos Campos - SP, 12230-720", cnpj: "52968282000208" },
    { id: "bestby_jacarei", name: "Best By Jacareí", address: "Rodovia Geraldo Scavone 1155, Jacareí 12305490", cnpj: "52968282000119" },
    { id: "bestby_panamby", name: "Best By Panamby", address: "São Paulo - SP", cnpj: "" }
  ];

  // NF-e Import
  const [nfeFiles, setNfeFiles] = useState([]); // Changed to array for multiple files
  const [uploadingNfe, setUploadingNfe] = useState(false);
  const [nfeData, setNfeData] = useState(null);
  const [nfeProducts, setNfeProducts] = useState([]);
  const [selectedNfeProducts, setSelectedNfeProducts] = useState([]);
  const [showNfeHistory, setShowNfeHistory] = useState(false);
  const [nfeHistory, setNfeHistory] = useState([]);
  const [loadingNfeHistory, setLoadingNfeHistory] = useState(false);
  const [nfeImportStoreId, setNfeImportStoreId] = useState("vencidinho_cangaiba"); // Selected store for NF-e import - default to Vencidinho
  const [nfeUploadProgress, setNfeUploadProgress] = useState({ current: 0, total: 0 }); // Progress for multiple files
  const [nfeImportStockType, setNfeImportStockType] = useState("regular"); // "fifo" or "regular"
  const [nfeSearchResults, setNfeSearchResults] = useState([]); // Results from Bluesoft search pending confirmation
  const [savingProduct, setSavingProduct] = useState(null); // EAN being saved
  const [savingToStock, setSavingToStock] = useState(false); // Saving all products to stock
  const [nfeHistorySearch, setNfeHistorySearch] = useState(""); // Search filter for NF-e history
  const [nfeHistoryDateFrom, setNfeHistoryDateFrom] = useState(""); // Date from filter
  const [nfeHistoryDateTo, setNfeHistoryDateTo] = useState(""); // Date to filter
  const [syncingNfes, setSyncingNfes] = useState(false); // Syncing NF-es from PostgreSQL
  const [importedEans, setImportedEans] = useState({}); // Map of EAN -> product name for products already in stock
  const [nfeImportTab, setNfeImportTab] = useState("all"); // "all" | "pending" (not imported yet)
  const [nfeSourceTab, setNfeSourceTab] = useState("arquivo"); // "arquivo" | "sefaz"
  const nfeInputRef = useRef(null);
  
  // Product name editing during NF-e import
  const [editingNfeProductIndex, setEditingNfeProductIndex] = useState(null);
  const [editedNfeProductName, setEditedNfeProductName] = useState("");
  
  // NF-e Delivery/Receiving Info
  const [showDeliveryForm, setShowDeliveryForm] = useState(false);
  const [deliveryInfo, setDeliveryInfo] = useState({
    vehicle_plate: "",
    driver_name: "",
    arrival_time: "",
    release_time: "",
    vehicle_photo: null,
    products_photo: null,
    notes: ""
  });
  const [savingDeliveryInfo, setSavingDeliveryInfo] = useState(false);
  const vehiclePhotoRef = useRef(null);
  const productsPhotoRef = useRef(null);
  
  // Company and Certificate management
  const [companies, setCompanies] = useState([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState("");
  const [loadingCompanies, setLoadingCompanies] = useState(false);
  const [showAddCompany, setShowAddCompany] = useState(false);
  const [newCompanyName, setNewCompanyName] = useState("");
  const [newCompanyCnpj, setNewCompanyCnpj] = useState("");
  const [addingCompany, setAddingCompany] = useState(false);
  const [uploadingCert, setUploadingCert] = useState(false);
  const [certPassword, setCertPassword] = useState("");
  const [certFile, setCertFile] = useState(null);
  const [isDraggingCert, setIsDraggingCert] = useState(false);
  const certInputRef = useRef(null);
  
  // Consulta por chave
  const [chaveNfe, setChaveNfe] = useState("");
  const [loadingChave, setLoadingChave] = useState(false);
  const [nfeByChave, setNfeByChave] = useState(null);
  const [chaveError, setChaveError] = useState(null);

  // Bluesoft Quota
  const [bluesoftQuota, setBluesoftQuota] = useState({ keys: [], total_remaining: 75, total_limit: 75 });
  const [selectedBluesoftKey, setSelectedBluesoftKey] = useState(null); // null = auto select

  // Sales Import & Dashboard
  const [salesPreview, setSalesPreview] = useState(null);
  const [uploadingSales, setUploadingSales] = useState(false);
  const [importingSales, setImportingSales] = useState(false);
  const [salesDashboard, setSalesDashboard] = useState(null);
  const [loadingDashboard, setLoadingDashboard] = useState(false);
  const [salesDateFrom, setSalesDateFrom] = useState("");
  const [salesDateTo, setSalesDateTo] = useState("");
  const [salesGroupBy, setSalesGroupBy] = useState("day");
  const [salesImportHistory, setSalesImportHistory] = useState([]);
  const salesFileRef = useRef(null);
  // Sales AI Chat
  const [salesChatMessages, setSalesChatMessages] = useState([]);
  const [salesChatInput, setSalesChatInput] = useState("");
  const [salesChatLoading, setSalesChatLoading] = useState(false);

  // Load art history
  // Removed: loadArtHistory, loadTemplates, saveTemplate, deleteTemplate, startEditTemplate, resetTemplateForm,
  // handleTemplateFormImageUpload, selectTemplateForArt - all art-related functions removed during white-labeling cleanup.

  // Price Search Functions
  const MARKET_TYPES = [
    { id: "supermercado", label: "Supermercado", icon: ShoppingCart },
    { id: "atacado", label: "Atacado", icon: Building },
    { id: "atacarejo", label: "Atacarejo", icon: Store },
    { id: "mercado_bairro", label: "Mercado de Bairro", icon: Store },
    { id: "distribuidor", label: "Distribuidor", icon: Truck },
    { id: "delivery", label: "App de Delivery", icon: Package },
    { id: "emporio", label: "Empório", icon: Store }
  ];

  const openPriceModal = async (product) => {
    setSelectedProductForPrice(product);
    setPriceMarketTypes([]);
    setMarkupPercentage("");
    setSellingPrice("");
    setPriceInputMode("markup");
    // Pre-fill cost from NF-e if available
    setProductCost(product.nfe_unit_cost ? product.nfe_unit_cost.toFixed(2) : (product.cost_price?.toFixed(2) || ""));
    setPriceResults(null);
    // Clear and load purchase simulation
    setPurchaseSimulation(null);
    setShowPriceModal(true);
    
    // Load purchase simulation from last NF-e
    if (product.ean) {
      setLoadingPurchaseSimulation(true);
      try {
        const response = await axios.get(`${API}/stock/products/${product.ean}/purchase-simulation`);
        if (response.data.success && response.data.has_data) {
          setPurchaseSimulation(response.data);
        }
      } catch (error) {
        console.error("Error loading purchase simulation:", error);
      } finally {
        setLoadingPurchaseSimulation(false);
      }
    }
  };

  // Bidirectional price calculation functions
  const handleMarkupChange = (value) => {
    setMarkupPercentage(value);
    setPriceInputMode("markup");
    const cost = parseFloat(productCost) || 0;
    const markup = parseFloat(value) || 0;
    if (cost > 0) {
      const calculatedPrice = cost * (1 + markup / 100);
      setSellingPrice(calculatedPrice.toFixed(2));
    }
  };

  const handleSellingPriceChange = (value) => {
    setSellingPrice(value);
    setPriceInputMode("price");
    const cost = parseFloat(productCost) || 0;
    const price = parseFloat(value) || 0;
    if (cost > 0 && price > 0) {
      const calculatedMarkup = ((price - cost) / cost) * 100;
      setMarkupPercentage(calculatedMarkup.toFixed(1));
    }
  };

  const handleCostChange = (value) => {
    setProductCost(value);
    const cost = parseFloat(value) || 0;
    if (cost > 0) {
      if (priceInputMode === "markup") {
        const markup = parseFloat(markupPercentage) || 0;
        const calculatedPrice = cost * (1 + markup / 100);
        setSellingPrice(calculatedPrice.toFixed(2));
      } else if (priceInputMode === "price") {
        const price = parseFloat(sellingPrice) || 0;
        if (price > 0) {
          const calculatedMarkup = ((price - cost) / cost) * 100;
          setMarkupPercentage(calculatedMarkup.toFixed(1));
        }
      }
    }
  };

  const toggleMarketType = (typeId) => {
    setPriceMarketTypes(prev => 
      prev.includes(typeId) 
        ? prev.filter(t => t !== typeId)
        : [...prev, typeId]
    );
  };

  const searchPrices = async () => {
    if (priceMarketTypes.length === 0) {
      toast.error("Selecione pelo menos um tipo de mercado");
      return;
    }
    
    const cost = parseFloat(productCost) || 0;
    const markup = parseFloat(markupPercentage) || 0;
    
    if (cost <= 0) {
      toast.error("Informe o custo do produto");
      return;
    }

    setSearchingPrices(true);
    setPriceResults(null);

    try {
      const selectedStore = STORE_LOCATIONS.find(s => s.id === selectedStoreLocation);
      const response = await axios.post(`${API}/price-search`, {
        ean: selectedProductForPrice.ean,
        product_name: selectedProductForPrice.product_name,
        market_types: priceMarketTypes,
        markup_percentage: markup,
        product_cost: cost,
        store_location: {
          id: selectedStoreLocation,
          name: selectedStore?.name,
          address: selectedStore?.address
        }
      });

      if (response.data.success) {
        // Calculate selling price and discount locally
        const data = response.data.data;
        const sellingPrice = cost * (1 + markup / 100);
        const discountVsAverage = data.average_price > 0 
          ? ((data.average_price - sellingPrice) / data.average_price) * 100 
          : 0;
        
        setPriceResults({
          ...data,
          product_cost: cost,
          markup_percentage: markup,
          selling_price: sellingPrice,
          discount_vs_average: discountVsAverage
        });
        toast.success("Pesquisa de preços concluída!");
      } else {
        toast.error("Erro na pesquisa de preços");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao pesquisar preços");
    } finally {
      setSearchingPrices(false);
    }
  };

  const getMarketIcon = (marketType) => {
    const market = MARKET_TYPES.find(m => m.id === marketType);
    return market?.icon || Store;
  };

  // NF-e Functions
  const UNIT_OPTIONS = [
    { id: "UN", label: "Unidade (UN)" },
    { id: "CX", label: "Caixa (CX)" },
    { id: "PCT", label: "Pacote (PCT)" },
    { id: "KG", label: "Quilograma (KG)" },
    { id: "G", label: "Grama (G)" },
    { id: "L", label: "Litro (L)" },
    { id: "ML", label: "Mililitro (ML)" },
    { id: "FD", label: "Fardo (FD)" },
    { id: "DZ", label: "Dúzia (DZ)" }
  ];

  const handleNfeFileSelect = (e) => {
    const files = Array.from(e.target.files);
    const xmlFiles = files.filter(file => file.name.toLowerCase().endsWith('.xml'));
    
    if (xmlFiles.length === 0) {
      toast.error("Selecione arquivo(s) XML válido(s)");
      return;
    }
    
    if (xmlFiles.length < files.length) {
      toast.warning(`${files.length - xmlFiles.length} arquivo(s) ignorado(s) (não são XML)`);
    }
    
    setNfeFiles(xmlFiles);
    setNfeData(null);
    setNfeProducts([]);
    setSelectedNfeProducts([]);
    
    if (xmlFiles.length === 1) {
      toast.info(`1 arquivo selecionado`);
    } else {
      toast.info(`${xmlFiles.length} arquivos selecionados`);
    }
  };

  const uploadNfe = async () => {
    if (nfeFiles.length === 0) {
      toast.error("Selecione um ou mais arquivos XML");
      return;
    }

    setUploadingNfe(true);
    setNfeUploadProgress({ current: 0, total: nfeFiles.length });
    
    let successCount = 0;
    let errorCount = 0;
    let cnpjMismatchCount = 0;
    let lastSuccessfulNfe = null;
    let allProducts = [];
    
    // Get selected store location CNPJ for validation
    const selectedStoreLocation = STORE_LOCATIONS.find(s => s.id === nfeImportStoreId);
    const expectedCnpj = selectedStoreLocation?.cnpj || "";
    
    try {
      for (let i = 0; i < nfeFiles.length; i++) {
        const file = nfeFiles[i];
        setNfeUploadProgress({ current: i + 1, total: nfeFiles.length });
        
        try {
          const formData = new FormData();
          formData.append('file', file);

          // Build URL with expected_cnpj parameter based on selected store
          let uploadUrl = `${API}/nfe/upload`;
          if (expectedCnpj) {
            uploadUrl += `?expected_cnpj=${encodeURIComponent(expectedCnpj)}`;
          }

          const response = await axios.post(uploadUrl, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });

          if (response.data.success) {
            successCount++;
            lastSuccessfulNfe = response.data.nfe;
            
            // Accumulate products from all NF-es
            const nfeNumber = response.data.nfe.nfe_number;
            const products = response.data.nfe.products.map((p, idx) => {
              // Create unique product_key for tracking products without EAN
              const productKey = `${nfeNumber}:${idx}:${(p.product_name || '').substring(0, 30).replace(/[^a-zA-Z0-9]/g, '')}`;
              
              return {
                ...p,
                nfe_number: nfeNumber,
                nfe_id: response.data.nfe.id,
                product_key: productKey,
                selected: true,
                origin_qty: p.origin_qty || 1,
                dest_qty: p.dest_qty || 1,
                batches: [{ 
                  batch_number: '0001', 
                  quantity: p.quantity || 1, 
                  unit: p.unit || 'UN', 
                  expiry_date: '' 
                }]
              };
            });
            allProducts = [...allProducts, ...products];
          } else if (response.data.cnpj_mismatch) {
            cnpjMismatchCount++;
            toast.error(`${file.name}: CNPJ da nota (${response.data.recipient_cnpj}) não corresponde à empresa selecionada`);
          } else if (response.data.already_imported) {
            toast.warning(`NF-e ${file.name}: já importada anteriormente`);
          }
        } catch (error) {
          errorCount++;
          const errorMsg = error.response?.data?.detail || error.message;
          toast.error(`Erro em ${file.name}: ${errorMsg}`);
        }
      }
      
      // If we have successful imports, show the last one (or combined view)
      if (successCount > 0 && allProducts.length > 0) {
        // If multiple NF-es, create a combined view
        if (successCount > 1) {
          setNfeData({
            ...lastSuccessfulNfe,
            nfe_number: `${successCount} NF-es importadas`,
            supplier_name: "Múltiplos fornecedores",
            products_count: allProducts.length,
            combined: true
          });
        } else {
          setNfeData(lastSuccessfulNfe);
        }
        
        setNfeProducts(allProducts);
        
        // Select only products with valid EAN
        const validIndices = allProducts
          .map((p, i) => ({ index: i, hasEan: p.ean && p.ean.length >= 8 }))
          .filter(item => item.hasEan)
          .map(item => item.index);
        setSelectedNfeProducts(validIndices);
        
        // Check which products are already imported
        const eans = allProducts.map(p => p.ean).filter(e => e && e.length >= 8);
        const originalEans = allProducts.map(p => p.original_ean).filter(e => e && e.length >= 8);
        const productKeys = allProducts.map(p => p.product_key).filter(Boolean);
        if (eans.length > 0 || originalEans.length > 0 || productKeys.length > 0) {
          // Pass the NF-e number for more accurate import checking
          const nfeNumberForCheck = successCount === 1 ? lastSuccessfulNfe?.nfe_number : null;
          checkImportedEans(eans, nfeNumberForCheck, originalEans, productKeys);
        }
        
        if (successCount === 1) {
          toast.success(`NF-e importada! ${allProducts.length} produtos encontrados`);
        } else {
          toast.success(`${successCount} NF-es importadas! ${allProducts.length} produtos no total`);
        }
      }
      
      if (errorCount > 0) {
        toast.error(`${errorCount} arquivo(s) com erro`);
      }
      
      if (cnpjMismatchCount > 0) {
        toast.warning(`${cnpjMismatchCount} nota(s) rejeitada(s) por CNPJ diferente da empresa selecionada`);
      }
      
      // Refresh NF-e history
      loadNfeHistory();
      
    } finally {
      setUploadingNfe(false);
      setNfeUploadProgress({ current: 0, total: 0 });
      setNfeFiles([]); // Clear files after processing
    }
  };

  // Check which EANs are already imported into stock
  const checkImportedEans = async (eans, nfeNumber = null, originalEans = [], productKeys = []) => {
    try {
      // Normalize EANs - remove leading zeros and ensure string format
      // Include both current EANs and original EANs for proper matching
      const allEans = [...eans, ...originalEans];
      const normalizedEans = allEans.map(e => {
        if (!e) return null;
        // Keep as string but trim
        return String(e).trim();
      }).filter(Boolean);
      
      // Remove duplicates
      const uniqueEans = [...new Set(normalizedEans)];
      
      // Normalize product keys
      const normalizedKeys = productKeys.map(k => k ? String(k).trim() : null).filter(Boolean);
      
      // Build URL with optional nfe_number and product_keys parameters
      let url = `${API}/stock/check-imported`;
      const params = new URLSearchParams();
      if (nfeNumber) params.append('nfe_number', nfeNumber);
      if (normalizedKeys.length > 0) {
        normalizedKeys.forEach(k => params.append('product_keys', k));
      }
      if (params.toString()) url += `?${params.toString()}`;
      
      const response = await axios.post(url, uniqueEans);
      if (response.data.success) {
        setImportedEans(response.data.imported_eans || {});
      }
    } catch (error) {
      console.error("Error checking imported EANs:", error);
    }
  };

  // Helper function to check if a product is imported (considers EAN, original EAN, and product_key)
  const isProductImported = (product) => {
    if (!product) return false;
    // Check current EAN
    if (product.ean && importedEans[product.ean]) return true;
    // Check original EAN (for cases where user changed EAN before import)
    if (product.original_ean && importedEans[product.original_ean]) return true;
    // Check product_key (for products without EAN)
    if (product.product_key && importedEans[product.product_key]) return true;
    return false;
  };

  // Load companies
  const loadCompanies = async () => {
    setLoadingCompanies(true);
    try {
      const response = await axios.get(`${API}/companies`);
      if (response.data.success) {
        setCompanies(response.data.companies || []);
        // Auto-select first company with certificate
        const withCert = response.data.companies.find(c => c.has_certificate);
        if (withCert && !selectedCompanyId) {
          setSelectedCompanyId(withCert.id);
        }
      }
    } catch (error) {
      console.error("Error loading companies:", error);
    } finally {
      setLoadingCompanies(false);
    }
  };

  // Add company
  const addCompany = async () => {
    if (!newCompanyName.trim() || !newCompanyCnpj.trim()) {
      toast.error("Preencha nome e CNPJ da empresa");
      return;
    }
    setAddingCompany(true);
    try {
      const response = await axios.post(`${API}/companies`, {
        name: newCompanyName,
        cnpj: newCompanyCnpj
      });
      if (response.data.success) {
        toast.success("Empresa cadastrada!");
        setCompanies(prev => [...prev, response.data.company]);
        setNewCompanyName("");
        setNewCompanyCnpj("");
        setShowAddCompany(false);
        setSelectedCompanyId(response.data.company.id);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao cadastrar empresa");
    } finally {
      setAddingCompany(false);
    }
  };

  // Delete company
  const deleteCompany = async (companyId) => {
    if (!window.confirm("Excluir empresa e seu certificado?")) return;
    try {
      await axios.delete(`${API}/companies/${companyId}`);
      setCompanies(prev => prev.filter(c => c.id !== companyId));
      if (selectedCompanyId === companyId) {
        setSelectedCompanyId("");
      }
      toast.success("Empresa excluída");
    } catch (error) {
      toast.error("Erro ao excluir empresa");
    }
  };

  // Upload certificate
  const uploadCertificate = async () => {
    if (!selectedCompanyId) {
      toast.error("Selecione uma empresa");
      return;
    }
    if (!certFile) {
      toast.error("Selecione um arquivo .pfx ou .p12");
      return;
    }
    if (!certPassword) {
      toast.error("Informe a senha do certificado");
      return;
    }

    setUploadingCert(true);
    try {
      const formData = new FormData();
      formData.append('file', certFile);
      formData.append('password', certPassword);

      const response = await axios.post(
        `${API}/companies/${selectedCompanyId}/certificate`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );

      if (response.data.success) {
        toast.success("Certificado enviado com sucesso!");
        setCertFile(null);
        setCertPassword("");
        // Refresh companies to update certificate status
        loadCompanies();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao enviar certificado");
    } finally {
      setUploadingCert(false);
    }
  };

  // Delete certificate
  const deleteCertificate = async (companyId) => {
    if (!window.confirm("Remover certificado desta empresa?")) return;
    try {
      await axios.delete(`${API}/companies/${companyId}/certificate`);
      loadCompanies();
      toast.success("Certificado removido");
    } catch (error) {
      toast.error("Erro ao remover certificado");
    }
  };

  // Handle certificate file selection
  const handleCertFileSelect = (e) => {
    const file = e.target.files[0];
    if (file && (file.name.endsWith('.pfx') || file.name.endsWith('.p12'))) {
      setCertFile(file);
    } else {
      toast.error("Selecione um arquivo .pfx ou .p12 válido");
    }
  };

  // Handle certificate drag and drop
  const handleCertDrop = (e) => {
    e.preventDefault();
    setIsDraggingCert(false);
    const file = e.dataTransfer.files[0];
    if (file && (file.name.endsWith('.pfx') || file.name.endsWith('.p12'))) {
      setCertFile(file);
    } else {
      toast.error("Selecione um arquivo .pfx ou .p12 válido");
    }
  };

  // Consultar NF-e por chave de acesso
  const consultarPorChave = async () => {
    if (!selectedCompanyId) {
      toast.error("Selecione uma empresa");
      return;
    }
    
    const company = companies.find(c => c.id === selectedCompanyId);
    if (!company?.has_certificate) {
      toast.error("Esta empresa não possui certificado cadastrado");
      return;
    }
    
    const chaveLimpa = chaveNfe.replace(/\s/g, "").replace(/-/g, "");
    if (chaveLimpa.length !== 44) {
      toast.error("A chave de acesso deve conter 44 dígitos");
      return;
    }

    setLoadingChave(true);
    setChaveError(null);
    setNfeByChave(null);
    
    try {
      const response = await axios.get(`${API}/nfe/sefaz/consultar-chave`, {
        params: { company_id: selectedCompanyId, chave: chaveLimpa }
      });
      
      if (response.data.success) {
        if (response.data.document) {
          setNfeByChave(response.data.document);
          toast.success("NF-e encontrada!");
        } else {
          setChaveError(response.data.message || "NF-e não encontrada");
          toast.info(response.data.message || "NF-e não encontrada");
        }
      } else {
        setChaveError(response.data.error || "Erro ao consultar");
        toast.error(response.data.error || "Erro ao consultar");
      }
    } catch (error) {
      const errMsg = error.response?.data?.error || "Erro ao conectar com SEFAZ";
      setChaveError(errMsg);
      toast.error(errMsg);
    } finally {
      setLoadingChave(false);
    }
  };

  // Load companies on mount
  useEffect(() => {
    loadCompanies();
  }, []);

  // Carregar NF-e da SEFAZ para o formulário de importação
  const carregarNfeSefaz = (nfe) => {
    // Convert SEFAZ format to local format
    const products = (nfe.products || []).map((p, idx) => {
      // Create a unique product key for tracking products without EAN
      // Format: NFE_NUMBER:PRODUCT_INDEX:PRODUCT_NAME_HASH
      const productKey = `${nfe.nfe_number}:${idx}:${(p.descricao || '').substring(0, 30).replace(/[^a-zA-Z0-9]/g, '')}`;
      
      return {
        ean: p.ean || "",
        original_ean: p.ean || "",  // Keep track of original EAN
        product_key: productKey,  // Unique identifier for products without EAN
        product_name: p.descricao || "",
        quantity: p.quantidade || 1,
        unit: p.unidade || "UN",
        unit_value: p.valor_unitario || 0,
        total_value: p.valor_total || 0,
        missing_ean: !p.ean || p.ean.length < 8,
        missing_ean_original: !p.ean || p.ean.length < 8,  // Track if originally missing EAN
        manually_imported: p.manually_imported || false,  // From NF-e record
        batches: [{ 
          batch_number: p.lote || p.batch || '0001', 
          quantity: p.quantidade || 1, 
          unit: p.unidade || 'UN', 
          expiry_date: p.validade || p.expiry_date || '' 
        }]
      };
    });
    
    setNfeData({
      nfe_number: nfe.nfe_number,
      access_key: nfe.chave,
      supplier_name: nfe.emitente_nome,
      supplier_cnpj: nfe.emitente_cnpj,
      issue_date: nfe.data_emissao,
      total_value: nfe.valor_total,
      products_count: products.length,
      source: 'sefaz'
    });
    setNfeProducts(products);
    
    // Select products with valid EAN
    const validIndices = products
      .map((p, i) => ({ index: i, hasEan: p.ean && p.ean.length >= 8 }))
      .filter(item => item.hasEan)
      .map(item => item.index);
    setSelectedNfeProducts(validIndices);
    
    // Check imported EANs
    const eans = products.map(p => p.ean).filter(e => e && e.length >= 8);
    const originalEans = products.map(p => p.original_ean).filter(e => e && e.length >= 8);
    const productKeys = products.map(p => p.product_key).filter(Boolean);
    if (eans.length > 0 || originalEans.length > 0 || productKeys.length > 0) {
      checkImportedEans(eans, nfe.nfe_number, originalEans, productKeys);
    }
    
    setNfeSourceTab("arquivo"); // Switch to file view to show products
    toast.success(`NF-e ${nfe.nfe_number} carregada para importação`);
  };

  const toggleNfeProductSelection = (index) => {
    setSelectedNfeProducts(prev => 
      prev.includes(index) 
        ? prev.filter(i => i !== index)
        : [...prev, index]
    );
  };

  const selectAllNfeProducts = () => {
    // Only select products with valid EAN
    const validIndices = nfeProducts
      .map((p, i) => ({ index: i, hasEan: p.ean && p.ean.length >= 8 }))
      .filter(item => item.hasEan)
      .map(item => item.index);
    
    if (selectedNfeProducts.length === validIndices.length) {
      setSelectedNfeProducts([]);
    } else {
      setSelectedNfeProducts(validIndices);
    }
  };

  const updateNfeProductConversion = (index, field, value) => {
    setNfeProducts(prev => prev.map((p, i) => {
      if (i === index) {
        const updated = { ...p, [field]: value };
        // Recalculate converted cost if conversion is enabled and we have all data
        const isConversionActive = updated.conversion_enabled || updated.needs_conversion;
        if (isConversionActive && updated.origin_qty && updated.dest_qty) {
          const originQty = parseFloat(updated.origin_qty) || 1;
          const destQty = parseFloat(updated.dest_qty) || 1;
          updated.converted_unit_cost = (updated.unit_cost * originQty) / destQty;
        }
        return updated;
      }
      return p;
    }));
  };

  const updateNfeProductEan = (index, newEan) => {
    setNfeProducts(prev => prev.map((p, i) => {
      if (i === index) {
        // Save original EAN if not already saved (for DUN->EAN conversion tracking)
        const originalEan = p.original_ean || p.ean;
        return { 
          ...p, 
          ean: newEan, 
          original_ean: originalEan,
          missing_ean: !newEan || newEan.length < 8 
        };
      }
      return p;
    }));
  };

  // Update batch info (lot number, expiry date) for NF-e product - LEGACY (single batch)
  const updateNfeProductBatch = (index, field, value) => {
    setNfeProducts(prev => prev.map((p, i) => {
      if (i === index) {
        return { ...p, [field]: value };
      }
      return p;
    }));
  };

  // Generate next batch number (0001, 0002, etc.)
  const generateBatchNumber = (existingBatches) => {
    const nextNum = (existingBatches?.length || 0) + 1;
    return String(nextNum).padStart(4, '0');
  };

  // Add a new batch to a product
  const addNfeProductBatch = (productIndex) => {
    setNfeProducts(prev => prev.map((p, i) => {
      if (i === productIndex) {
        const currentBatches = p.batches || [{ batch_number: '0001', quantity: p.quantity, unit: p.unit || 'UN', expiry_date: '' }];
        const nextBatchNumber = generateBatchNumber(currentBatches);
        return { 
          ...p, 
          batches: [...currentBatches, { batch_number: nextBatchNumber, quantity: 0, unit: p.unit || 'UN', expiry_date: '' }]
        };
      }
      return p;
    }));
    
    // Also update nfeSearchResults if exists
    setNfeSearchResults(prev => prev.map((p, i) => {
      if (i === productIndex && p.original_product) {
        const currentBatches = p.original_product.batches || [{ batch_number: '0001', quantity: p.original_product.quantity, unit: p.original_product.unit || 'UN', expiry_date: '' }];
        const nextBatchNumber = generateBatchNumber(currentBatches);
        const updatedBatches = [...currentBatches, { batch_number: nextBatchNumber, quantity: 0, unit: p.original_product.unit || 'UN', expiry_date: '' }];
        return { 
          ...p, 
          batches: updatedBatches,
          original_product: { ...p.original_product, batches: updatedBatches }
        };
      }
      return p;
    }));
  };

  // Update a specific batch item
  const updateNfeProductBatchItem = (productIndex, batchIndex, field, value) => {
    // Update nfeProducts
    setNfeProducts(prev => prev.map((p, i) => {
      if (i === productIndex) {
        const currentBatches = p.batches || [{ batch_number: '', quantity: p.quantity, expiry_date: '' }];
        const updatedBatches = currentBatches.map((b, bi) => {
          if (bi === batchIndex) {
            return { ...b, [field]: value };
          }
          return b;
        });
        return { ...p, batches: updatedBatches };
      }
      return p;
    }));
    
    // Also update nfeSearchResults if exists (for when user edits after searching)
    setNfeSearchResults(prev => prev.map((p, i) => {
      if (i === productIndex && p.original_product) {
        const currentBatches = p.original_product.batches || [{ batch_number: '', quantity: p.original_product.quantity, expiry_date: '' }];
        const updatedBatches = currentBatches.map((b, bi) => {
          if (bi === batchIndex) {
            return { ...b, [field]: value };
          }
          return b;
        });
        return { 
          ...p, 
          batches: updatedBatches,
          original_product: { ...p.original_product, batches: updatedBatches }
        };
      }
      return p;
    }));
  };

  // Remove a batch from a product
  const removeNfeProductBatch = (productIndex, batchIndex) => {
    setNfeProducts(prev => prev.map((p, i) => {
      if (i === productIndex) {
        const currentBatches = p.batches || [];
        if (currentBatches.length <= 1) return p;
        return { 
          ...p, 
          batches: currentBatches.filter((_, bi) => bi !== batchIndex)
        };
      }
      return p;
    }));
    
    // Also update nfeSearchResults if exists
    setNfeSearchResults(prev => prev.map((p, i) => {
      if (i === productIndex && p.original_product) {
        const currentBatches = p.original_product.batches || [];
        if (currentBatches.length <= 1) return p;
        const updatedBatches = currentBatches.filter((_, bi) => bi !== batchIndex);
        return { 
          ...p, 
          batches: updatedBatches,
          original_product: { ...p.original_product, batches: updatedBatches }
        };
      }
      return p;
    }));
  };

  // Search products in Bluesoft (doesn't save to stock yet)
  const searchNfeProducts = async () => {
    if (selectedNfeProducts.length === 0) {
      toast.error("Selecione pelo menos um produto");
      return;
    }

    // Check if any selected product is missing EAN
    const missingEan = selectedNfeProducts.some(index => {
      const p = nfeProducts[index];
      return !p.ean || p.ean.length < 8;
    });
    if (missingEan) {
      toast.error("Preencha o EAN de todos os produtos selecionados");
      return;
    }

    setLoading(true);
    const selectedProducts = selectedNfeProducts.map(index => nfeProducts[index]);
    const searchResults = [];

    for (let i = 0; i < selectedProducts.length; i++) {
      const product = selectedProducts[i];
      setProcessingProgress(((i + 1) / selectedProducts.length) * 100);
      
      try {
        const response = await axios.post(`${API}/search/single`, { 
          ean: product.ean,
          expected_name: product.product_name,
          preferred_key_id: selectedBluesoftKey
        });
        
        // Extract only product data (exclude quota)
        const { quota, ...productData } = response.data;
        if (quota) setBluesoftQuota(quota);
        
        // Calculate final unit cost (with conversion if needed)
        const finalCost = product.needs_conversion && product.converted_unit_cost
          ? product.converted_unit_cost
          : product.unit_cost;

        // Store result with all data needed for confirmation
        searchResults.push({
          ...productData,
          nfe_unit_cost: finalCost,
          nfe_original_unit: product.unit,
          nfe_quantity: product.quantity,
          batches: product.batches,
          from_nfe: true,
          confirmed: false, // Not confirmed yet
          original_product: product // Keep original NF-e product data
        });
      } catch (error) {
        console.error(`Error searching EAN ${product.ean}:`, error);
        searchResults.push({
          ean: product.ean,
          status: "not_found",
          product_name: product.product_name,
          match_analysis: error.response?.data?.detail || "Erro na busca",
          nfe_unit_cost: product.unit_cost,
          from_nfe: true,
          confirmed: false,
          original_product: product
        });
      }
    }

    // Store search results for individual confirmation (don't add to main results yet)
    setNfeSearchResults(searchResults);
    setLoading(false);
    setProcessingProgress(0);
    loadBluesoftQuota();
    const found = searchResults.filter(r => r.status === "found").length;
    toast.success(`${found} de ${searchResults.length} produtos encontrados. Confirme cada produto para salvar no estoque.`);
  };

  // Confirm and save a single product to stock
  const confirmNfeProduct = async (index) => {
    const product = nfeSearchResults[index];
    if (!product || product.confirmed) return;
    
    setSavingProduct(product.ean);
    
    try {
      const originalProduct = product.original_product;
      
      // Create or update product in stock system
      await axios.post(`${API}/stock/products`, {
        ean: product.ean,
        name: product.product_name || originalProduct.product_name,
        unit: originalProduct.unit === "UN" ? "UN" : (originalProduct.dest_unit || "UN"),
        cost_price: product.nfe_unit_cost,
        image_url: product.image_url || null,
        image_base64: product.image_base64 || null
      });

      // Create batches (lots) with quantity and expiry date
      const batchesToCreate = originalProduct.batches && originalProduct.batches.length > 0 
        ? originalProduct.batches 
        : [{ batch_number: '', quantity: originalProduct.quantity, unit: originalProduct.unit, expiry_date: '' }];
      
      for (const batch of batchesToCreate) {
        if (batch.quantity > 0) {
          await axios.post(`${API}/stock/batches`, {
            ean: product.ean,
            batch_number: batch.batch_number || `NF-${nfeData?.nfe_number || 'AUTO'}-${Date.now()}`,
            quantity: batch.quantity,
            unit: batch.unit || originalProduct.unit || "UN",
            expiry_date: batch.expiry_date || new Date(Date.now() + 365*24*60*60*1000).toISOString().split('T')[0],
            store_id: nfeImportStoreId,
            store_name: STORE_LOCATIONS.find(s => s.id === nfeImportStoreId)?.name,
            nfe_id: nfeData?.id,
            nfe_number: nfeData?.nfe_number,
            nfe_original_ean: originalProduct.original_ean || originalProduct.ean,  // Track original EAN from NF-e
            nfe_product_key: originalProduct.product_key,  // Track products without EAN
            stock_type: nfeImportStockType
          });
        }
      }

      // Mark as confirmed and add to main results
      setNfeSearchResults(prev => prev.map((p, i) => 
        i === index ? { ...p, confirmed: true } : p
      ));
      setResults(prev => [...prev, { ...product, confirmed: true }]);
      
      toast.success(`${product.product_name} salvo no estoque!`);
    } catch (error) {
      console.error("Error saving product:", error);
      toast.error(`Erro ao salvar ${product.product_name}`);
    } finally {
      setSavingProduct(null);
    }
  };

  // Skip a product (don't save to stock)
  const skipNfeProduct = (index) => {
    setNfeSearchResults(prev => prev.filter((_, i) => i !== index));
    toast.info("Produto ignorado");
  };

  // Start editing a product name in NF-e search results
  const startEditingNfeProductName = (index) => {
    setEditingNfeProductIndex(index);
    setEditedNfeProductName(nfeSearchResults[index]?.product_name || "");
  };

  // Save edited product name in NF-e search results
  const saveEditedNfeProductName = (index) => {
    if (editedNfeProductName.trim()) {
      setNfeSearchResults(prev => prev.map((p, i) => {
        if (i !== index) return p;
        return { 
          ...p, 
          product_name: editedNfeProductName.trim(),
          original_product_name: p.original_product_name || p.product_name
        };
      }));
      toast.success("Nome do produto atualizado");
    }
    setEditingNfeProductIndex(null);
    setEditedNfeProductName("");
  };

  // Cancel editing product name
  const cancelEditingNfeProductName = () => {
    setEditingNfeProductIndex(null);
    setEditedNfeProductName("");
  };

  // Save selected NF-e products directly to stock (without Bluesoft search)
  const saveNfeProductsToStock = async () => {
    if (selectedNfeProducts.length === 0) {
      toast.error("Selecione pelo menos um produto");
      return;
    }

    // Check if any selected product is missing EAN
    const missingEan = selectedNfeProducts.some(index => {
      const p = nfeProducts[index];
      return !p.ean || p.ean.length < 8;
    });
    if (missingEan) {
      toast.error("Preencha o EAN de todos os produtos selecionados");
      return;
    }

    // Check if store is selected
    if (!nfeImportStoreId) {
      toast.error("Selecione a loja de destino");
      return;
    }

    setSavingToStock(true);
    const selectedProducts = selectedNfeProducts.map(index => ({ ...nfeProducts[index], originalIndex: index }));
    let savedCount = 0;
    let errorCount = 0;

    for (const product of selectedProducts) {
      try {
        // Create or update product in stock system
        await axios.post(`${API}/stock/products`, {
          ean: product.ean,
          name: product.product_name,
          unit: product.unit || "UN",
          cost_price: product.unit_cost || 0
        });

        // Create batches (lots) with quantity and expiry date
        const batchesToCreate = product.batches && product.batches.length > 0 
          ? product.batches 
          : [{ batch_number: '', quantity: product.quantity, unit: product.unit, expiry_date: '' }];
        
        for (const batch of batchesToCreate) {
          if (batch.quantity > 0) {
            await axios.post(`${API}/stock/batches`, {
              ean: product.ean,
              batch_number: batch.batch_number || `NF-${nfeData?.nfe_number || 'AUTO'}-${Date.now()}`,
              quantity: batch.quantity,
              unit: batch.unit || product.unit || "UN",
              expiry_date: batch.expiry_date || new Date(Date.now() + 365*24*60*60*1000).toISOString().split('T')[0],
              store_id: nfeImportStoreId,
              store_name: STORE_LOCATIONS.find(s => s.id === nfeImportStoreId)?.name,
              nfe_id: nfeData?.id,
              nfe_number: nfeData?.nfe_number,
              nfe_original_ean: product.original_ean || product.ean,  // Track original EAN from NF-e
              nfe_product_key: product.product_key,  // Track products without EAN
              stock_type: nfeImportStockType
            });
          }
        }
        
        // Mark the product as imported in the NF-e record
        if (nfeData?.id) {
          try {
            await axios.post(`${API}/nfe/${nfeData.id}/mark-product-imported`, null, {
              params: { ean: product.ean, product_key: product.product_key }
            });
          } catch (markError) {
            console.error("Error marking product as imported:", markError);
          }
        }
        
        savedCount++;
      } catch (error) {
        console.error(`Error saving product ${product.ean}:`, error);
        errorCount++;
      }
    }

    setSavingToStock(false);
    
    if (savedCount > 0) {
      toast.success(`${savedCount} produto(s) carregado(s) no estoque!`);
      // Clear selection after saving
      setSelectedNfeProducts([]);
      // Refresh stock data
      loadStockProducts();
      loadLowStockProducts();
      // Refresh NF-e history to update import status
      loadNfeHistory();
    }
    if (errorCount > 0) {
      toast.error(`${errorCount} produto(s) com erro ao salvar`);
    }
  };

  const loadNfeHistory = async (filters = {}) => {
    setLoadingNfeHistory(true);
    try {
      // Get the store_id from selected store location (nfeImportStoreId)
      const selectedStoreLocation = STORE_LOCATIONS.find(s => s.id === nfeImportStoreId);
      
      const params = new URLSearchParams();
      params.append('limit', '500'); // Increased limit to show all NF-es
      
      // Filter by store_id
      if (nfeImportStoreId) {
        params.append('store_id', nfeImportStoreId);
      }
      
      // Filter by recipient CNPJ based on the selected store for import
      if (selectedStoreLocation?.cnpj) {
        params.append('recipient_cnpj', selectedStoreLocation.cnpj);
      }
      
      // Apply search filter
      if (filters.search || nfeHistorySearch) {
        params.append('search', filters.search || nfeHistorySearch);
      }
      
      // Apply date filters
      if (filters.dateFrom || nfeHistoryDateFrom) {
        params.append('date_from', filters.dateFrom || nfeHistoryDateFrom);
      }
      if (filters.dateTo || nfeHistoryDateTo) {
        params.append('date_to', filters.dateTo || nfeHistoryDateTo);
      }
      
      const queryString = params.toString();
      const url = `${API}/nfe/history${queryString ? '?' + queryString : ''}`;
      
      const response = await axios.get(url);
      setNfeHistory(response.data.nfes || []);
    } catch (error) {
      toast.error("Erro ao carregar histórico de NF-e");
    } finally {
      setLoadingNfeHistory(false);
    }
  };

  const syncNfesFromPg = async () => {
    setSyncingNfes(true);
    try {
      // Sync last 30 days of NF-es
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      const dateFrom = thirtyDaysAgo.toISOString().split('T')[0];
      
      const response = await axios.get(`${API}/nfe/sync-from-pg`, {
        params: { 
          store_id: nfeImportStoreId,
          date_from: dateFrom, 
          limit: 100 
        }
      });
      
      if (response.data.success) {
        if (response.data.synced > 0) {
          toast.success(`${response.data.synced} NF-e(s) sincronizada(s) do sistema!`);
          loadNfeHistory();
        } else if (response.data.skipped > 0) {
          toast.info(`Todas as ${response.data.skipped} NF-es já estavam sincronizadas`);
        } else {
          toast.info("Nenhuma NF-e nova encontrada no período");
        }
      }
    } catch (error) {
      toast.error("Erro ao sincronizar NF-es: " + (error.response?.data?.detail || error.message));
    } finally {
      setSyncingNfes(false);
    }
  };

  const loadNfeFromHistory = async (nfeId) => {
    try {
      const response = await axios.get(`${API}/nfe/${nfeId}`);
      setNfeData(response.data);
      const products = response.data.products.map(p => ({
        ...p,
        selected: true,
        origin_qty: p.origin_qty || 1,
        dest_qty: p.dest_qty || 1,
        original_ean: p.ean || "",  // Keep track of original EAN
        missing_ean: !p.ean || p.ean.length < 8,
        missing_ean_original: !p.ean || p.ean.length < 8,  // Track if originally missing EAN
        // Initialize batches with default batch number 0001
        batches: p.batches || [{ 
          batch_number: '0001', 
          quantity: p.quantity || 1, 
          unit: p.unit || 'UN', 
          expiry_date: '' 
        }]
      }));
      setNfeProducts(products);
      // Select only products with valid EAN by index (excluding manually imported ones which are already done)
      const validIndices = products
        .map((p, i) => ({ index: i, hasEan: p.ean && p.ean.length >= 8, manuallyImported: p.manually_imported }))
        .filter(item => item.hasEan && !item.manuallyImported)
        .map(item => item.index);
      setSelectedNfeProducts(validIndices);
      
      // Check which products are already imported
      const eans = products.map(p => p.ean).filter(e => e && e.length >= 8);
      const originalEans = products.map(p => p.original_ean).filter(e => e && e.length >= 8);
      const productKeys = products.map(p => p.product_key).filter(Boolean);
      if (eans.length > 0 || originalEans.length > 0 || productKeys.length > 0) {
        checkImportedEans(eans, response.data.nfe_number, originalEans, productKeys);
      }
      
      setShowNfeHistory(false);
      toast.success("NF-e carregada do histórico");
    } catch (error) {
      toast.error("Erro ao carregar NF-e");
    }
  };

  const deleteNfe = async (nfeId, deleteStock = false) => {
    try {
      const response = await axios.delete(`${API}/nfe/${nfeId}?delete_stock=${deleteStock}`);
      setNfeHistory(prev => prev.filter(n => n.id !== nfeId));
      if (deleteStock && response.data.deleted_batches > 0) {
        toast.success(`NF-e excluída com ${response.data.deleted_batches} lotes de estoque`);
      } else {
        toast.success("NF-e excluída");
      }
    } catch (error) {
      toast.error("Erro ao excluir NF-e");
    }
  };

  const resetNfeImport = () => {
    setNfeFiles([]);
    setNfeData(null);
    setNfeProducts([]);
    setSelectedNfeProducts([]);
    setShowDeliveryForm(false);
    setDeliveryInfo({
      vehicle_plate: "",
      driver_name: "",
      arrival_time: "",
      release_time: "",
      vehicle_photo: null,
      products_photo: null,
      notes: ""
    });
    if (nfeInputRef.current) {
      nfeInputRef.current.value = '';
    }
  };

  // ==================== NF-e DELIVERY INFO FUNCTIONS ====================
  
  // Handle photo capture/upload
  const handlePhotoCapture = async (type, file) => {
    if (!file) return;
    
    // Convert to base64
    const reader = new FileReader();
    reader.onload = (e) => {
      const base64 = e.target.result;
      setDeliveryInfo(prev => ({
        ...prev,
        [type]: base64
      }));
    };
    reader.readAsDataURL(file);
  };

  // Save delivery info to backend
  const saveDeliveryInfo = async () => {
    if (!nfeData?.id) {
      toast.error("NF-e não encontrada");
      return;
    }

    setSavingDeliveryInfo(true);
    try {
      const response = await axios.put(`${API}/nfe/${nfeData.id}/delivery-info`, deliveryInfo);
      if (response.data.success) {
        toast.success("Informações de recebimento salvas!");
        setShowDeliveryForm(false);
        // Update nfeData with delivery info
        setNfeData(prev => ({
          ...prev,
          delivery_info: response.data.delivery_info
        }));
      }
    } catch (error) {
      console.error("Error saving delivery info:", error);
      toast.error("Erro ao salvar informações de recebimento");
    } finally {
      setSavingDeliveryInfo(false);
    }
  };

  // Load existing delivery info when nfeData changes
  const loadDeliveryInfo = async (nfeId) => {
    if (!nfeId) return;
    try {
      const response = await axios.get(`${API}/nfe/${nfeId}/delivery-info`);
      if (response.data.success && response.data.delivery_info) {
        setDeliveryInfo({
          vehicle_plate: response.data.delivery_info.vehicle_plate || "",
          driver_name: response.data.delivery_info.driver_name || "",
          arrival_time: response.data.delivery_info.arrival_time || "",
          release_time: response.data.delivery_info.release_time || "",
          vehicle_photo: response.data.delivery_info.vehicle_photo || null,
          products_photo: response.data.delivery_info.products_photo || null,
          notes: response.data.delivery_info.notes || ""
        });
      }
    } catch (error) {
      console.error("Error loading delivery info:", error);
    }
  };

  // ==================== SALES FUNCTIONS ====================
  
  // Preview sales import
  // Direct file import - simpler and more reliable
  const importSalesFile = async (file) => {
    if (!file) return;
    
    // Confirm store selection
    const storeName = stores.find(s => s.id === importStoreId)?.name || importStoreId;
    if (!window.confirm(`Confirma a importação para a loja "${storeName}"?`)) {
      if (salesFileRef.current) salesFileRef.current.value = '';
      return;
    }
    
    setUploadingSales(true);
    setImportingSales(true);
    const formData = new FormData();
    formData.append("file", file);
    formData.append("store_id", importStoreId);
    
    try {
      const response = await axios.post(`${API}/sales/import-file`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      
      if (response.data.success) {
        const msg = response.data.imported_count > 0 
          ? `✅ ${response.data.imported_count} registros importados para ${response.data.store_name}!${response.data.duplicate_count > 0 ? ` (${response.data.duplicate_count} duplicados ignorados)` : ''}`
          : `ℹ️ Nenhum registro novo. ${response.data.duplicate_count} duplicados encontrados.`;
        toast.success(msg);
        
        // Refresh all data
        await loadSalesDashboard();
        await loadDailyComparison();
        await loadSalesImportHistory();
        await loadChartData();
        await loadTicketAnalysis();
      } else {
        toast.error(response.data.message || "Erro ao importar");
      }
    } catch (error) {
      console.error("Import error:", error);
      toast.error(error.response?.data?.detail || "Erro ao importar vendas");
    } finally {
      setUploadingSales(false);
      setImportingSales(false);
      if (salesFileRef.current) salesFileRef.current.value = '';
    }
  };

  // Preview sales import (legacy - still available)
  const previewSalesImport = async (file) => {
    if (!file) return;
    
    setUploadingSales(true);
    const formData = new FormData();
    formData.append("file", file);
    
    try {
      const response = await axios.post(`${API}/sales/preview-import`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      setSalesPreview(response.data);
      toast.success(`Arquivo analisado: ${response.data.new_count} novos, ${response.data.duplicate_count} duplicados`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao analisar arquivo");
    } finally {
      setUploadingSales(false);
    }
  };

  // Import sales from preview (legacy)
  const importSales = async () => {
    if (!salesPreview || !salesPreview.new_records.length) {
      toast.error("Nenhum registro novo para importar");
      return;
    }
    
    setImportingSales(true);
    try {
      const response = await axios.post(`${API}/sales/import`, {
        records: salesPreview.new_records,
        filename: salesPreview.filename
      });
      toast.success(`${response.data.imported_count} registros importados com sucesso!`);
      setSalesPreview(null);
      if (salesFileRef.current) salesFileRef.current.value = '';
      await loadSalesDashboard();
      await loadDailyComparison();
      await loadSalesImportHistory();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao importar vendas");
    } finally {
      setImportingSales(false);
    }
  };

  // Load stores with PostgreSQL database configured
  const loadPgStores = async () => {
    try {
      const response = await axios.get(`${API}/sales/pg-stores`);
      setPgStores(response.data.stores || []);
    } catch (error) {
      console.error("Error loading PG stores:", error);
    }
  };

  // Import sales from PostgreSQL database
  const importSalesFromDatabase = async () => {
    // Check if store has PostgreSQL config
    const pgStore = pgStores.find(s => s.id === importStoreId);
    if (!pgStore) {
      toast.error(`Loja "${importStoreId}" não tem acesso ao banco de dados configurado`);
      return;
    }

    const storeName = stores.find(s => s.id === importStoreId)?.name || importStoreId;
    if (!window.confirm(`Confirma a importação dos últimos 3 dias do banco de dados para "${storeName}"?`)) {
      return;
    }

    setImportingFromDb(true);
    try {
      const response = await axios.post(`${API}/sales/import-from-database?store_id=${importStoreId}&days=3`);
      
      if (response.data.success) {
        const msg = response.data.imported > 0 
          ? `✅ ${response.data.imported} registros importados do banco de dados!${response.data.duplicates > 0 ? ` (${response.data.duplicates} duplicados ignorados)` : ''}`
          : `ℹ️ Nenhum registro novo. ${response.data.duplicates} duplicados encontrados.`;
        toast.success(msg);
        
        // Refresh all data
        await loadSalesDashboard();
        await loadDailyComparison();
        await loadSalesImportHistory();
        await loadChartData();
        await loadTicketAnalysis();
      } else {
        toast.error(response.data.message || "Erro ao importar");
      }
    } catch (error) {
      console.error("Import from DB error:", error);
      toast.error(error.response?.data?.detail || "Erro ao importar do banco de dados");
    } finally {
      setImportingFromDb(false);
    }
  };

  // Sync all stores from PostgreSQL - called automatically on dashboard load and refresh
  const syncAllStores = async (showToast = true) => {
    setSyncingAllStores(true);
    try {
      const response = await axios.post(`${API}/sales/sync-all-stores?days=1`);
      
      if (response.data.success) {
        setLastSyncTime(new Date().toISOString());
        
        if (response.data.total_imported > 0) {
          if (showToast) {
            toast.success(`🔄 ${response.data.total_imported} novos registros sincronizados!`);
          }
          // Refresh dashboard data
          await loadSalesDashboard();
          await loadDailyComparison();
          await loadChartData();
          await loadTicketAnalysis();
        } else if (showToast) {
          toast.info("✅ Dados já estão atualizados");
        }
      }
    } catch (error) {
      console.error("Sync all stores error:", error);
      if (showToast) {
        toast.error("Erro ao sincronizar vendas");
      }
    } finally {
      setSyncingAllStores(false);
    }
  };

  // Load sales dashboard
  const loadSalesDashboard = async (storeIds = selectedStoreIds) => {
    setLoadingDashboard(true);
    try {
      const params = new URLSearchParams();
      if (salesDateFrom) params.append("date_from", salesDateFrom);
      if (salesDateTo) params.append("date_to", salesDateTo);
      params.append("group_by", salesGroupBy);
      // Pass store filter - single or multiple
      if (storeIds.length === 1) {
        params.append("store_id", storeIds[0]);
      } else if (storeIds.length > 1) {
        params.append("store_ids", storeIds.join(","));
      }
      
      const response = await fetchDashboardWithFallback(params);
      setSalesDashboard(response.data);
      
      // Also load top markup products
      loadTopMarkupProducts(storeIds);
    } catch (error) {
      toast.error("Erro ao carregar dashboard");
    } finally {
      setLoadingDashboard(false);
    }
  };
  
  // Load top markup products
  const loadTopMarkupProducts = async (storeIds = selectedStoreIds, sortAsc = markupSortAsc, limit = markupProductsLimit) => {
    setLoadingTopMarkupProducts(true);
    try {
      const params = new URLSearchParams({ limit: limit });
      if (salesDateFrom) params.append("date_from", salesDateFrom);
      if (salesDateTo) params.append("date_to", salesDateTo);
      if (storeIds.length === 1) {
        params.append("store_id", storeIds[0]);
      } else if (storeIds.length > 1) {
        params.append("store_ids", storeIds.join(","));
      }
      if (sortAsc) params.append("sort_asc", "true");
      
      const response = await axios.get(`${API}/sales/v4/top-markup-products?${params.toString()}`);
      setTopMarkupProducts(response.data.products || []);
    } catch (error) {
      console.error("Error loading top markup products:", error);
      setTopMarkupProducts([]);
    } finally {
      setLoadingTopMarkupProducts(false);
    }
  };

  // Load revenue projection for current month
  const loadRevenueProjection = async (storeIds = selectedStoreIds) => {
    setLoadingRevenueProjection(true);
    try {
      const params = new URLSearchParams();
      if (storeIds.length === 1) {
        params.append("store_id", storeIds[0]);
      } else if (storeIds.length > 1) {
        params.append("store_ids", storeIds.join(","));
      }
      const response = await axios.get(`${API}/sales/v4/revenue-projection?${params.toString()}`);
      setRevenueProjection(response.data);
    } catch (error) {
      console.error("Error loading revenue projection:", error);
      setRevenueProjection(null);
    } finally {
      setLoadingRevenueProjection(false);
    }
  };

  // Load sales import history
  const loadSalesImportHistory = async () => {
    try {
      const response = await axios.get(`${API}/sales/imports`);
      setSalesImportHistory(response.data.imports);
      if (response.data.stores) {
        setStores(response.data.stores);
      }
    } catch (error) {
      console.error("Error loading sales history:", error);
    }
  };

  // Load chart data (last 30 days)
  const loadChartData = async (storeIds = selectedStoreIds) => {
    setLoadingChart(true);
    try {
      const params = new URLSearchParams({ days: 30 });
      if (storeIds.length === 1) {
        params.append("store_id", storeIds[0]);
      } else if (storeIds.length > 1) {
        params.append("store_ids", storeIds.join(","));
      }
      const response = await axios.get(`${API}/sales/v4/chart-data?${params.toString()}`);
      setChartData(response.data.data || []);
    } catch (error) {
      console.error("Error loading chart data:", error);
    } finally {
      setLoadingChart(false);
    }
  };
  
  // Load store comparison data (for multi-store view)
  const loadStoreComparisonData = async () => {
    if (selectedStoreIds.length === 1) {
      setStoreComparisonData([]);
      return;
    }
    
    setLoadingStoreComparison(true);
    try {
      // Load data for each store
      const storesToLoad = selectedStoreIds.length > 0 ? selectedStoreIds : stores.map(s => s.id);
      const chartDataByStore = {};
      
      for (const storeId of storesToLoad) {
        const params = new URLSearchParams({ days: 30, store_id: storeId });
        const response = await axios.get(`${API}/sales/v4/chart-data?${params.toString()}`);
        chartDataByStore[storeId] = response.data.data || [];
      }
      
      // Merge data by date
      const allDates = new Set();
      Object.values(chartDataByStore).forEach(data => {
        data.forEach(d => allDates.add(d.data_full || d.data));
      });
      
      const mergedData = Array.from(allDates).sort().map(date => {
        const row = { data: date.length > 5 ? date.slice(5) : date, data_full: date };
        storesToLoad.forEach(storeId => {
          const storeData = chartDataByStore[storeId]?.find(d => (d.data_full || d.data) === date);
          // Use store ID as key to ensure uniqueness
          row[`${storeId}_liquido`] = storeData?.liquido || 0;
        });
        return row;
      });
      
      setStoreComparisonData(mergedData);
    } catch (error) {
      console.error("Error loading store comparison:", error);
    } finally {
      setLoadingStoreComparison(false);
    }
  };

  // Load markup metrics
  const loadMarkupMetrics = async (storeIds = selectedStoreIds) => {
    setLoadingMarkup(true);
    try {
      const params = new URLSearchParams();
      if (storeIds.length === 1) {
        params.append("store_id", storeIds[0]);
      } else if (storeIds.length > 1) {
        params.append("store_ids", storeIds.join(","));
      }
      if (salesDateFrom) params.append("date_from", salesDateFrom);
      if (salesDateTo) params.append("date_to", salesDateTo);
      const response = await axios.get(`${API}/sales/v4/markup-metrics?${params.toString()}`);
      setMarkupMetrics(response.data);
    } catch (error) {
      console.error("Error loading markup metrics:", error);
      setMarkupMetrics(null);
    } finally {
      setLoadingMarkup(false);
    }
  };

  // Get ticket médio for a specific product
  const loadProductTicket = async (produto) => {
    setSelectedProduct(produto);
    setLoadingProductTicket(true);
    setLoadingSelectedProductMarkup(true);
    setLoadingProductStock(true);
    setLoadingBasketAnalysis(true);
    setLoadingProductDailySales(true);
    setSelectedProductMarkup(null);
    setSelectedProductStock(null);
    setBasketAnalysis(null);
    setProductDailySales(null);
    setProductDailyPeriod("30"); // Reset to default 30 days
    try {
      const params = new URLSearchParams();
      if (selectedStoreIds.length === 1) params.append("store_id", selectedStoreIds[0]);
      else if (selectedStoreIds.length > 1) params.append("store_ids", selectedStoreIds.join(","));
      
      const response = await axios.get(`${API}/sales/product-ticket/${encodeURIComponent(produto)}?${params.toString()}`);
      setProductTicket(response.data);
      
      // Load daily sales for this product
      try {
        const dailyParams = new URLSearchParams();
        if (selectedStoreIds.length === 1) dailyParams.append("store_id", selectedStoreIds[0]);
        else if (selectedStoreIds.length > 1) dailyParams.append("store_ids", selectedStoreIds.join(","));
        dailyParams.append("days", "30");
        const dailyResponse = await axios.get(`${API}/sales/product-daily/${encodeURIComponent(produto)}?${dailyParams.toString()}`);
        setProductDailySales(dailyResponse.data);
      } catch (dailyError) {
        console.error("Error loading product daily sales:", dailyError);
        setProductDailySales(null);
      }
      
      // Also load markup data, stock data, and basket analysis for this product by EAN (if available)
      if (response.data?.ean) {
        const ean = response.data.ean;
        
        // Load markup
        try {
          const markupResponse = await axios.get(`${API}/sales/product-markup/${ean}?${params.toString()}`);
          setSelectedProductMarkup(markupResponse.data);
        } catch (markupError) {
          console.error("Error loading product markup:", markupError);
          setSelectedProductMarkup(null);
        }
        
        // Load stock info
        try {
          const stockParams = new URLSearchParams();
          if (selectedStoreIds.length === 1) stockParams.append("store_id", selectedStoreIds[0]);
          const stockResponse = await axios.get(`${API}/stock/products/${ean}/sales-analysis?${stockParams.toString()}`);
          setSelectedProductStock(stockResponse.data);
        } catch (stockError) {
          console.error("Error loading product stock:", stockError);
          setSelectedProductStock(null);
        }
        
        // Load basket analysis (products frequently bought together)
        try {
          const basketParams = new URLSearchParams();
          if (selectedStoreIds.length === 1) basketParams.append("store_id", selectedStoreIds[0]);
          basketParams.append("limit", "5");
          const basketResponse = await axios.get(`${API}/sales/basket-analysis/${ean}?${basketParams.toString()}`);
          setBasketAnalysis(basketResponse.data);
        } catch (basketError) {
          console.error("Error loading basket analysis:", basketError);
          setBasketAnalysis(null);
        }
      }
    } catch (error) {
      console.error("Error loading product ticket:", error);
      setProductTicket(null);
      setSelectedProductMarkup(null);
      setSelectedProductStock(null);
      setBasketAnalysis(null);
      setProductDailySales(null);
    } finally {
      setLoadingProductTicket(false);
      setLoadingSelectedProductMarkup(false);
      setLoadingProductStock(false);
      setLoadingBasketAnalysis(false);
      setLoadingProductDailySales(false);
    }
  };

  // Reload product daily sales when period changes
  const loadProductDailySalesForPeriod = async (days, startDate = null, endDate = null) => {
    if (!selectedProduct) return;
    setLoadingProductDailySales(true);
    try {
      const params = new URLSearchParams();
      if (selectedStoreIds.length === 1) params.append("store_id", selectedStoreIds[0]);
      else if (selectedStoreIds.length > 1) params.append("store_ids", selectedStoreIds.join(","));
      
      if (startDate && endDate) {
        params.append("start_date", startDate);
        params.append("end_date", endDate);
      } else {
        params.append("days", days);
      }
      
      const response = await axios.get(`${API}/sales/product-daily/${encodeURIComponent(selectedProduct)}?${params.toString()}`);
      setProductDailySales(response.data);
    } catch (error) {
      console.error("Error loading product daily sales:", error);
      setProductDailySales(null);
    } finally {
      setLoadingProductDailySales(false);
    }
  };

  // Send chat message to AI
  const sendSalesChatMessage = async () => {
    if (!salesChatInput.trim()) return;
    
    const question = salesChatInput.trim();
    setSalesChatMessages(prev => [...prev, { role: "user", content: question }]);
    setSalesChatInput("");
    setSalesChatLoading(true);
    
    try {
      const response = await axios.post(`${API}/sales/chat`, {
        question: question,
        date_from: salesDateFrom || null,
        date_to: salesDateTo || null,
        store_id: selectedStoreIds.length === 1 ? selectedStoreIds[0] : null
      });
      
      if (response.data.success) {
        setSalesChatMessages(prev => [...prev, { 
          role: "assistant", 
          content: response.data.answer,
          context: response.data.context_summary
        }]);
      } else {
        toast.error(response.data.error || "Erro ao processar pergunta");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao enviar pergunta");
    } finally {
      setSalesChatLoading(false);
    }
  };

  // Load daily comparison (today vs yesterday vs same day last week vs 7-day avg)
  const loadDailyComparison = async (storeIds = selectedStoreIds, selectedDate = dailyComparisonDate) => {
    setLoadingComparison(true);
    try {
      const params = new URLSearchParams();
      if (storeIds.length === 1) {
        params.append("store_id", storeIds[0]);
      } else if (storeIds.length > 1) {
        params.append("store_ids", storeIds.join(","));
      }
      if (selectedDate) {
        const dateStr = selectedDate instanceof Date 
          ? selectedDate.toLocaleDateString('pt-BR')
          : selectedDate;
        params.append("date", dateStr);
      }
      
      // Use v4 (PostgreSQL Direct)
      let response;
      try {
        response = await axios.get(`${API}/sales/v4/daily-comparison?${params.toString()}`);
        console.log('[DailyComparison] Using v4 API (PostgreSQL Direct)');
      } catch (e) {
        console.error('[DailyComparison] V4 failed:', e.message);
        throw e;
      }
      
      setDailyComparison(response.data);
    } catch (error) {
      console.error("Error loading daily comparison:", error);
    } finally {
      setLoadingComparison(false);
    }
  };

  // Load ticket analysis (above/below average, ranges)
  const loadTicketAnalysis = async (storeIds = selectedStoreIds) => {
    setLoadingTicketAnalysis(true);
    try {
      const params = new URLSearchParams();
      if (storeIds.length === 1) {
        params.append("store_id", storeIds[0]);
      } else if (storeIds.length > 1) {
        params.append("store_ids", storeIds.join(","));
      }
      const response = await axios.get(`${API}/sales/v4/ticket-analysis?${params.toString()}`);
      setTicketAnalysis(response.data);
    } catch (error) {
      console.error("Error loading ticket analysis:", error);
    } finally {
      setLoadingTicketAnalysis(false);
    }
  };

  // Load top products for a specific ticket range
  const loadTicketRangeProducts = async (range) => {
    setSelectedTicketRange(range);
    setLoadingTicketRangeProducts(true);
    try {
      const params = new URLSearchParams();
      params.append("min_value", range.min);
      if (range.max !== null) params.append("max_value", range.max);
      if (selectedStoreIds.length === 1) params.append("store_id", selectedStoreIds[0]);
      params.append("limit", "10");
      
      const response = await axios.get(`${API}/sales/v4/ticket-range-products?${params.toString()}`);
      setTicketRangeProducts(response.data);
    } catch (error) {
      console.error("Error loading ticket range products:", error);
      setTicketRangeProducts(null);
    } finally {
      setLoadingTicketRangeProducts(false);
    }
  };

  // Load payment methods breakdown
  const loadPaymentMethods = async (storeIds = selectedStoreIds, dateFrom = null, dateTo = null) => {
    setLoadingPaymentMethods(true);
    try {
      const storeId = storeIds.length === 1 ? storeIds[0] : "vencidinho_cangaiba";
      const params = { store_id: storeId };
      // Use date range if provided, otherwise use salesDateFrom/salesDateTo
      // Match the behavior of loadSalesDashboard - no fallback to 30 days
      if (dateFrom && dateTo) {
        params.date_from = dateFrom;
        params.date_to = dateTo;
      } else if (salesDateFrom) {
        params.date_from = salesDateFrom;
      }
      if (!dateFrom && !dateTo && salesDateTo) {
        params.date_to = salesDateTo;
      }
      // If no dates provided, backend will use all available data (same as dashboard)
      const response = await axios.get(`${API}/sales/v4/by-payment-method`, { params });
      if (response.data.success) {
        setPaymentMethods(response.data.payment_methods || []);
      }
    } catch (error) {
      console.error("Error loading payment methods:", error);
      setPaymentMethods([]);
    } finally {
      setLoadingPaymentMethods(false);
    }
  };

  // ============== STOCK MANAGEMENT FUNCTIONS ==============
  
  // Load stock products
  const loadStockProducts = async () => {
    setLoadingStock(true);
    try {
      const params = new URLSearchParams();
      if (stockSearch) params.append("search", stockSearch);
      if (showLowStockOnly) params.append("low_stock_only", "true");
      if (stockStoreId) params.append("store_id", stockStoreId);
      
      const response = await axios.get(`${API}/stock/products?${params.toString()}`);
      setStockProducts(response.data.products || []);
    } catch (error) {
      console.error("Error loading stock:", error);
      toast.error("Erro ao carregar estoque");
    } finally {
      setLoadingStock(false);
    }
  };

  // Load low stock products for dashboard alert
  const loadLowStockProducts = async (storeIds = selectedStoreIds, signal = null) => {
    try {
      const params = new URLSearchParams();
      // Use selectedStoreIds for dashboard consistency
      if (storeIds && storeIds.length === 1) {
        params.append("store_id", storeIds[0]);
      }
      const config = signal ? { signal } : {};
      const response = await axios.get(`${API}/stock/low-stock?${params.toString()}`, config);
      setLowStockProducts(response.data.products || []);
    } catch (error) {
      if (!axios.isCancel(error)) {
        console.error("Error loading low stock:", error);
      }
    }
  };

  // Load expiring products for dashboard alert
  const loadExpiringProducts = async (storeIds = selectedStoreIds, signal = null) => {
    try {
      const params = new URLSearchParams();
      // Use selectedStoreIds for dashboard consistency
      // When array has exactly 1 store, filter by it
      if (storeIds && storeIds.length === 1) {
        params.append("store_id", storeIds[0]);
      }
      params.append("days", "7");
      params.append("exclude_expired", "true"); // Don't show already expired products in dashboard
      params.append("exclude_zero_stock", "true"); // Don't show products with zero stock
      // Remove min_count to only get products that actually expire within the days limit
      const config = signal ? { signal } : {};
      const response = await axios.get(`${API}/stock/expiring?${params.toString()}`, config);
      // Filter to only show products that expire within 7 days
      const products = (response.data.products || []).filter(p => p.days_until_expiry <= 7);
      setExpiringProducts(products);
    } catch (error) {
      if (!axios.isCancel(error)) {
        console.error("Error loading expiring products:", error);
      }
    }
  };

  // Load slow selling products for dashboard alert
  const loadSlowSellingProducts = async (storeIds = selectedStoreIds, signal = null) => {
    setLoadingSlowSelling(true);
    try {
      const params = new URLSearchParams();
      // Use selectedStoreIds for dashboard consistency
      if (storeIds.length === 1) {
        params.append("store_id", storeIds[0]);
      }
      params.append("limit", "10");
      const config = signal ? { signal } : {};
      const response = await axios.get(`${API}/stock/slow-selling-products?${params.toString()}`, config);
      setSlowSellingProducts(response.data.products || []);
      if (response.data.settings) {
        setSalesTargetSettings(response.data.settings);
      }
    } catch (error) {
      if (!axios.isCancel(error)) {
        console.error("Error loading slow selling products:", error);
      }
    } finally {
      setLoadingSlowSelling(false);
    }
  };

  // Update sales target settings
  const updateSalesTargetSettings = async (newSettings) => {
    try {
      await axios.post(`${API}/stock/sales-target-settings`, newSettings);
      toast.success("Configurações de meta atualizadas");
      setSalesTargetSettings(newSettings);
      setShowSettingsModal(false);
      loadSlowSellingProducts(selectedStoreIds);
    } catch (error) {
      toast.error("Erro ao atualizar configurações");
    }
  };

  // Load stores from API
  const loadStores = async () => {
    try {
      const response = await axios.get(`${API}/stores`);
      console.log('[Stores] Loaded:', response.data);
      setStores(response.data.stores || []);
      // Set default store from API if not already set
      if (!selectedStoreId && response.data.stores?.length > 0) {
        const defaultStore = response.data.stores.find(s => s.is_default);
        if (defaultStore) {
          setSelectedStoreId(defaultStore.cnpj || "");
          setStockStoreId(defaultStore.cnpj || "");
          setImportStoreId(defaultStore.cnpj || "");
        }
      }
    } catch (error) {
      console.error("[Stores] Error loading stores:", error);
    }
  };

  // Load product detail with batches and movements
  const loadStockProductDetail = async (ean) => {
    setSelectedStockProduct(ean);
    setLoadingStockDetail(true);
    try {
      const params = new URLSearchParams();
      if (stockStoreId) params.append("store_id", stockStoreId);
      const response = await axios.get(`${API}/stock/products/${ean}?${params.toString()}`);
      setStockProductDetail(response.data);
    } catch (error) {
      console.error("Error loading product detail:", error);
      toast.error("Erro ao carregar detalhes do produto");
    } finally {
      setLoadingStockDetail(false);
    }
  };

  // Update product min stock
  const updateMinStock = async (ean, minStock) => {
    try {
      await axios.put(`${API}/stock/products/${ean}`, { min_stock: parseInt(minStock) });
      toast.success("Estoque mínimo atualizado");
      setEditingMinStock(null);
      loadStockProducts();
      if (selectedStockProduct === ean) {
        loadStockProductDetail(ean);
      }
    } catch (error) {
      toast.error("Erro ao atualizar estoque mínimo");
    }
  };

  // Update product image
  const updateProductImage = async (ean, imageBase64) => {
    try {
      await axios.put(`${API}/stock/products/${ean}`, { image_base64: imageBase64 });
      toast.success("Foto atualizada!");
      setEditingProductImage(null);
      loadStockProducts();
      loadLowStockProducts();
      if (selectedStockProduct === ean) {
        loadStockProductDetail(ean);
      }
    } catch (error) {
      toast.error("Erro ao atualizar foto");
    }
  };

  // Handle paste image for product
  const handlePasteProductImage = async (e, ean) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    
    for (const item of items) {
      if (item.type.startsWith('image/')) {
        const blob = item.getAsFile();
        const reader = new FileReader();
        reader.onload = () => {
          updateProductImage(ean, reader.result);
        };
        reader.readAsDataURL(blob);
        break;
      }
    }
  };

  // Process sales for stock deduction
  const processSalesForStock = async () => {
    try {
      const response = await axios.post(`${API}/stock/process-sales`);
      if (response.data.success) {
        toast.success(response.data.message);
        loadStockProducts();
        loadLowStockProducts();
        loadExpiringProducts();
      }
    } catch (error) {
      toast.error("Erro ao processar vendas");
    }
  };

  // Load stock on section change
  useEffect(() => {
    if (activeSection === "estoque") {
      // Debounce search to avoid API calls on every keystroke
      const timeoutId = setTimeout(() => {
        loadStockProducts();
      }, 300); // Wait 300ms after user stops typing
      
      return () => clearTimeout(timeoutId);
    }
  }, [activeSection, stockSearch, showLowStockOnly, stockStoreId]);

  // Load low stock and expiring products for dashboard
  useEffect(() => {
    loadStores();
    loadLowStockProducts(selectedStoreIds);
    loadExpiringProducts(selectedStoreIds);
    loadSlowSellingProducts(selectedStoreIds);
  }, []);

  // Reload all dashboard data (uses a new AbortController internally)
  const reloadDashboardData = (storeIds = selectedStoreIds) => {
    // Increment refresh trigger to force useEffect to re-run
    setRefreshTrigger(prev => prev + 1);
  };

  // Load initial data on mount (non-store-dependent)
  useEffect(() => {
    loadSalesImportHistory();
    loadPgStores(); // Load stores with PostgreSQL database configured
    // Auto-sync sales data on dashboard load (silent mode)
    syncAllStores(false);
  }, []);

  // Reload dashboard when store or filters change
  useEffect(() => {
    if (salesDateFrom || salesDateTo || salesGroupBy) {
      loadSalesDashboard();
    }
  }, [salesDateFrom, salesDateTo, salesGroupBy]);

  // Track the latest request ID for store selection changes to prevent race conditions
  const storeSelectionRequestRef = useRef(0);
  
  // Reload dashboard when store selection changes
  useEffect(() => {
    // Only load data if at least one store is selected
    if (stores.length > 0 && selectedStoreIds.length > 0) {
      const storeIds = selectedStoreIds;
      const currentRequestId = ++storeSelectionRequestRef.current;
      
      // Create AbortController for this batch of requests
      const abortController = new AbortController();
      const signal = abortController.signal;
      
      // Helper to check if this request is still valid
      const isStaleRequest = () => currentRequestId !== storeSelectionRequestRef.current;
      
      // Load Sales Dashboard with abort signal
      const loadDashboardWithSignal = async () => {
        setLoadingDashboard(true);
        try {
          const params = new URLSearchParams();
          if (salesDateFrom) params.append("date_from", salesDateFrom);
          if (salesDateTo) params.append("date_to", salesDateTo);
          params.append("group_by", salesGroupBy);
          if (storeIds.length === 1) {
            params.append("store_id", storeIds[0]);
          } else if (storeIds.length > 1) {
            params.append("store_ids", storeIds.join(","));
          }
          
          const response = await fetchDashboardWithFallback(params, signal);
          if (!isStaleRequest()) {
            setSalesDashboard(response.data);
            loadTopMarkupProducts(storeIds);
            
            // Use by_payment from V4 response if available (more accurate than legacy endpoint)
            if (response.data.by_payment && response.data.by_payment.length > 0) {
              const totalGeral = response.data.by_payment.reduce((sum, pm) => sum + (pm.valor || 0), 0);
              const transformedPayments = response.data.by_payment.map(pm => {
                const forma = pm.forma_pagamento || 'Outros';
                const formaLower = forma.toLowerCase();
                let categoria = 'other';
                let icon = '💰';
                
                if (formaLower.includes('cred') || formaLower.includes('crédito')) {
                  categoria = 'credit_card';
                  icon = '💳';
                } else if (formaLower.includes('deb') || formaLower.includes('débito')) {
                  categoria = 'debit_card';
                  icon = '💳';
                } else if (formaLower.includes('dinh') || formaLower.includes('espécie')) {
                  categoria = 'cash';
                  icon = '💵';
                } else if (formaLower.includes('pix')) {
                  categoria = 'pix';
                  icon = '📱';
                } else if (formaLower.includes('voucher') || formaLower.includes('vale') || formaLower.includes('ticket') || formaLower.includes('vr') || formaLower.includes('va')) {
                  categoria = 'voucher';
                  icon = '🎫';
                }
                
                return {
                  forma_pagamento: forma,
                  categoria,
                  icon,
                  num_vendas: pm.quantidade || 0,
                  total_valor: pm.valor || 0,
                  ticket_medio: pm.quantidade > 0 ? pm.valor / pm.quantidade : 0,
                  percentual: totalGeral > 0 ? ((pm.valor / totalGeral) * 100).toFixed(1) : 0
                };
              });
              setPaymentMethods(transformedPayments);
              setLoadingPaymentMethods(false);
            }
          }
        } catch (error) {
          if (!axios.isCancel(error) && !isStaleRequest()) {
            console.error("Error loading dashboard:", error);
          }
        } finally {
          if (!isStaleRequest()) {
            setLoadingDashboard(false);
          }
        }
      };
      
      // Load Daily Comparison with abort signal - using v3 fallback
      const loadDailyComparisonWithSignal = async () => {
        setLoadingComparison(true);
        console.log('[DailyComparison] Starting load for stores:', storeIds);
        try {
          const params = new URLSearchParams();
          if (storeIds.length === 1) {
            params.append("store_id", storeIds[0]);
          } else if (storeIds.length > 1) {
            params.append("store_ids", storeIds.join(","));
          }
          if (dailyComparisonDate) {
            const dateStr = dailyComparisonDate instanceof Date 
              ? dailyComparisonDate.toLocaleDateString('pt-BR')
              : dailyComparisonDate;
            params.append("date", dateStr);
          }
          
          // Use v4 (PostgreSQL Direct)
          let response;
          try {
            console.log('[DailyComparison] Calling V4 API...');
            response = await axios.get(`${API}/sales/v4/daily-comparison?${params.toString()}`, { signal });
            console.log('[DailyComparison] V4 Response:', response.data);
          } catch (e) {
            if (axios.isCancel(e)) throw e;
            console.error('[DailyComparison] V4 failed:', e.message);
            throw e;
          }
          
          if (!isStaleRequest()) {
            console.log('[DailyComparison] Setting data, has_data:', response.data?.has_data);
            setDailyComparison(response.data);
          }
        } catch (error) {
          if (!axios.isCancel(error) && !isStaleRequest()) {
            console.error("[DailyComparison] Error loading daily comparison:", error);
          }
        } finally {
          if (!isStaleRequest()) {
            setLoadingComparison(false);
          }
        }
      };
      
      // Load Chart Data with abort signal
      const loadChartDataWithSignal = async () => {
        setLoadingChart(true);
        try {
          const params = new URLSearchParams({ days: 30 });
          if (storeIds.length === 1) {
            params.append("store_id", storeIds[0]);
          } else if (storeIds.length > 1) {
            params.append("store_ids", storeIds.join(","));
          }
          
          const response = await axios.get(`${API}/sales/v4/chart-data?${params.toString()}`, { signal });
          if (!isStaleRequest()) {
            setChartData(response.data.data);
          }
        } catch (error) {
          if (!axios.isCancel(error) && !isStaleRequest()) {
            console.error("Error loading chart data:", error);
          }
        } finally {
          if (!isStaleRequest()) {
            setLoadingChart(false);
          }
        }
      };
      
      // Load Ticket Analysis with abort signal
      const loadTicketAnalysisWithSignal = async () => {
        setLoadingTicketAnalysis(true);
        try {
          const params = new URLSearchParams();
          if (storeIds.length === 1) {
            params.append("store_id", storeIds[0]);
          } else if (storeIds.length > 1) {
            params.append("store_ids", storeIds.join(","));
          }
          
          const response = await axios.get(`${API}/sales/v4/ticket-analysis?${params.toString()}`, { signal });
          if (!isStaleRequest()) {
            setTicketAnalysis(response.data);
          }
        } catch (error) {
          if (!axios.isCancel(error) && !isStaleRequest()) {
            console.error("Error loading ticket analysis:", error);
          }
        } finally {
          if (!isStaleRequest()) {
            setLoadingTicketAnalysis(false);
          }
        }
      };
      
      // Load Markup Metrics with abort signal
      const loadMarkupMetricsWithSignal = async () => {
        setLoadingMarkup(true);
        try {
          const params = new URLSearchParams();
          if (storeIds.length === 1) {
            params.append("store_id", storeIds[0]);
          } else if (storeIds.length > 1) {
            params.append("store_ids", storeIds.join(","));
          }
          
          const response = await axios.get(`${API}/sales/v4/markup-metrics?${params.toString()}`, { signal });
          if (!isStaleRequest()) {
            setMarkupMetrics(response.data);
          }
        } catch (error) {
          if (!axios.isCancel(error) && !isStaleRequest()) {
            console.error("Error loading markup metrics:", error);
          }
        } finally {
          if (!isStaleRequest()) {
            setLoadingMarkup(false);
          }
        }
      };
      
      // Load Payment Methods with abort signal
      const loadPaymentMethodsWithSignal = async () => {
        setLoadingPaymentMethods(true);
        try {
          const storeId = storeIds.length === 1 ? storeIds[0] : "vencidinho_cangaiba";
          const params = { store_id: storeId };
          if (salesDateFrom) params.date_from = salesDateFrom;
          if (salesDateTo) params.date_to = salesDateTo;
          
          const response = await axios.get(`${API}/sales/v4/by-payment-method`, { params, signal });
          if (!isStaleRequest() && response.data.success) {
            setPaymentMethods(response.data.payment_methods || []);
          }
        } catch (error) {
          if (!axios.isCancel(error) && !isStaleRequest()) {
            console.error("Error loading payment methods:", error);
            setPaymentMethods([]);
          }
        } finally {
          if (!isStaleRequest()) {
            setLoadingPaymentMethods(false);
          }
        }
      };
      
      // Load Revenue Projection with abort signal
      const loadRevenueProjectionWithSignal = async () => {
        setLoadingRevenueProjection(true);
        try {
          const params = new URLSearchParams();
          if (storeIds.length === 1) {
            params.append("store_id", storeIds[0]);
          } else if (storeIds.length > 1) {
            params.append("store_ids", storeIds.join(","));
          }
          
          const response = await axios.get(`${API}/sales/v4/revenue-projection?${params.toString()}`, { signal });
          if (!isStaleRequest()) {
            setRevenueProjection(response.data);
          }
        } catch (error) {
          if (!axios.isCancel(error) && !isStaleRequest()) {
            console.error("Error loading revenue projection:", error);
            setRevenueProjection(null);
          }
        } finally {
          if (!isStaleRequest()) {
            setLoadingRevenueProjection(false);
          }
        }
      };
      
      // Execute all data loading functions
      loadDashboardWithSignal();
      loadDailyComparisonWithSignal();
      loadChartDataWithSignal();
      loadTicketAnalysisWithSignal();
      loadMarkupMetricsWithSignal();
      loadPaymentMethodsWithSignal();
      loadRevenueProjectionWithSignal();
      loadSlowSellingProducts(storeIds, signal);
      loadExpiringProducts(storeIds, signal);
      loadLowStockProducts(storeIds, signal);
      
      if (storeIds.length !== 1) {
        loadStoreComparisonData();
      }
      
      // Cleanup: abort pending requests when dependencies change
      return () => {
        abortController.abort();
      };
    }
  }, [selectedStoreIds, stores, refreshTrigger]);

  // Delete import
  const deleteImport = async (importId) => {
    if (!window.confirm("Tem certeza que deseja excluir esta importação? Todos os registros serão removidos.")) {
      return;
    }
    try {
      const response = await axios.delete(`${API}/sales/imports/${importId}`);
      if (response.data.success) {
        toast.success(response.data.message);
        loadSalesImportHistory();
        reloadDashboardData(selectedStoreIds);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao excluir importação");
    }
  };

  // Load Bluesoft quota
  const loadBluesoftQuota = async () => {
    try {
      const response = await axios.get(`${API}/bluesoft-quota`);
      setBluesoftQuota(response.data);
    } catch (error) {
      console.error("Error loading quota:", error);
    }
  };

  // Load quota on mount
  useEffect(() => {
    loadBluesoftQuota();
  }, []);

  // Reload NF-e history when store selection changes and modal is open
  useEffect(() => {
    if (showNfeHistory) {
      loadNfeHistory();
    }
  }, [nfeImportStoreId, showNfeHistory]);

  // Handle scanner input - detects fast typing (scanner) vs manual typing
  const handleEanInput = useCallback((e) => {
    const value = e.target.value.replace(/\D/g, ''); // Only digits
    const now = Date.now();
    const timeDiff = now - lastKeyTime.current;
    
    // Scanner typically inputs chars very fast (< 50ms between chars)
    // Manual typing is slower (> 100ms between chars)
    const isLikelyScanner = timeDiff < 50 && value.length > 3;
    
    if (isLikelyScanner) {
      setScannerMode(true);
    } else if (timeDiff > 300) {
      setScannerMode(false);
    }
    
    lastKeyTime.current = now;
    setSingleEan(value);
    
    // Auto-search ONLY when scanner completes 13 digits (EAN-13)
    // For manual typing, user must press Enter
    if (isLikelyScanner && value.length === 13) {
      // Small delay to ensure state is updated
      setTimeout(() => {
        searchSingleEanDirect(value, true);
      }, 100);
    }
  }, []);

  // Handle Enter key - search manually
  const handleEanKeyDown = (e) => {
    if (e.key === "Enter" && singleEan.trim().length >= 8) {
      e.preventDefault();
      searchSingleEan();
    }
  };

  // Single EAN search (direct call with EAN parameter for scanner)
  const searchSingleEanDirect = async (ean, fromScanner = false) => {
    if (!ean || ean.length < 8) {
      toast.error("EAN deve ter pelo menos 8 dígitos");
      return;
    }

    setLoading(true);
    
    // Clear field immediately if from scanner
    if (fromScanner) {
      setSingleEan("");
    }
    
    try {
      const response = await axios.post(`${API}/search/single`, { 
        ean: ean,
        preferred_key_id: selectedBluesoftKey 
      });
      const { quota, ...resultData } = response.data;
      
      // Try to capture image as base64 if not already present
      if (resultData.image_url && !resultData.image_base64 && resultData.status === 'found') {
        const imageBase64 = await imageUrlToBase64(resultData.image_url);
        if (imageBase64) {
          resultData.image_base64 = imageBase64;
        }
      }
      
      // Add to results (prepend new result in scanner mode)
      if (fromScanner) {
        setResults(prev => [resultData, ...prev]);
      } else {
        setResults([resultData]);
      }
      
      if (quota) setBluesoftQuota(quota);
      
      const scanIcon = fromScanner ? "📷 " : "";
      if (resultData.from_cache) {
        toast.success(`${scanIcon}Busca concluída! (cache)`);
      } else {
        toast.success(`${scanIcon}Busca concluída!`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro na busca");
    } finally {
      setLoading(false);
      setScannerMode(false);
    }
  };

  // Single EAN search (uses state)
  const searchSingleEan = async () => {
    const ean = singleEan.trim();
    if (!ean) {
      toast.error("Digite um EAN válido");
      return;
    }
    await searchSingleEanDirect(ean, scannerMode);
  };

  // Search for product image by name (when not found in Bluesoft)
  const searchImageByName = async (ean, suggestedName) => {
    // Ask user for the product name
    const productName = window.prompt(
      "Digite o nome do produto para buscar a imagem na internet:",
      suggestedName || ""
    );
    
    if (!productName || productName.trim() === "") {
      return; // User cancelled
    }
    
    setSearchingImage(ean);
    toast.info("Buscando imagem na internet... aguarde");
    
    try {
      const response = await axios.post(`${API}/search/image-by-name`, {
        product_name: productName.trim(),
        ean: ean
      });
      
      if (response.data.success) {
        // Update the result with the new image and product name
        const imageData = response.data.image_base64 || response.data.image_url;
        setResults(prev => prev.map(r => 
          r.ean === ean 
            ? { 
                ...r, 
                image_base64: response.data.image_base64 || null,
                image_url: response.data.image_url || null,
                product_name: productName.trim(), 
                status: 'found' 
              }
            : r
        ));
        toast.success("Imagem encontrada na internet!");
      } else {
        toast.error(response.data.error || "Não foi possível encontrar a imagem");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao buscar imagem");
    } finally {
      setSearchingImage(null);
    }
  };

  // Handle file upload
  const handleFileUpload = async (file) => {
    if (!file) return;

    const formData = new FormData();
    formData.append("file", file);

    setLoading(true);
    try {
      const response = await axios.post(`${API}/upload/excel`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setUploadedItems(response.data.items);
      toast.success(`${response.data.count} itens carregados do Excel`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao processar arquivo");
    } finally {
      setLoading(false);
    }
  };

  // Process bulk search
  const processBulkSearch = async () => {
    if (uploadedItems.length === 0) {
      toast.error("Carregue um arquivo Excel primeiro");
      return;
    }

    setLoading(true);
    setProcessingProgress(0);

    try {
      const response = await axios.post(`${API}/search/bulk`, { items: uploadedItems });
      
      // Try to capture images as base64 for products that don't have it
      const resultsWithImages = await Promise.all(
        response.data.results.map(async (result) => {
          if (result.image_url && !result.image_base64 && result.status === 'found') {
            const imageBase64 = await imageUrlToBase64(result.image_url);
            if (imageBase64) {
              return { ...result, image_base64: imageBase64 };
            }
          }
          return result;
        })
      );
      
      setResults(resultsWithImages);
      setProcessingProgress(100);
      loadBluesoftQuota(); // Refresh quota after bulk search
      const fromCache = response.data.results.filter(r => r.from_cache).length;
      toast.success(`Busca concluída: ${response.data.found} encontrados, ${response.data.not_found} não encontrados${fromCache > 0 ? ` (${fromCache} do cache)` : ''}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro na busca em lote");
    } finally {
      setLoading(false);
    }
  };

  // Export to Excel
  const exportToExcel = () => {
    if (results.length === 0) {
      toast.error("Nenhum resultado para exportar");
      return;
    }

    const exportData = results.map((r) => ({
      EAN: r.ean,
      "Nome do Produto": r.product_name || "",
      Marca: r.brand || "",
      "Nome Esperado": r.expected_name || "",
      "Score de Match": r.match_score || "",
      Análise: r.match_analysis || "",
      "URL da Imagem": r.image_url || "",
      Status: r.status === "found" ? "Encontrado" : "Não encontrado",
    }));

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Resultados");
    const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    const data = new Blob([excelBuffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
    saveAs(data, `resultados_ean_${new Date().toISOString().split("T")[0]}.xlsx`);
    toast.success("Arquivo exportado com sucesso!");
  };

  // Copy image to clipboard
  const copyImageToClipboard = async (imageUrl) => {
    try {
      const response = await fetch(imageUrl);
      const blob = await response.blob();
      await navigator.clipboard.write([
        new ClipboardItem({ [blob.type]: blob })
      ]);
      toast.success("Imagem copiada! Cole com Ctrl+V no campo de imagem");
    } catch (error) {
      toast.info("Clique com botão direito na imagem e selecione 'Copiar imagem'");
    }
  };

  // Open image viewer
  const openImageViewer = (imageUrl, product) => {
    setViewerImage(imageUrl);
    setViewerProduct(product);
    setZoomLevel(1);
    setViewerOpen(true);
  };

  // Drag and drop handlers
  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file && (file.name.endsWith(".xlsx") || file.name.endsWith(".xls"))) {
      handleFileUpload(file);
    } else {
      toast.error("Arquivo deve ser .xlsx ou .xls");
    }
  }, []);

  const getStatusBadge = (status, score) => {
    if (status === "found") {
      if (score >= 90) return <Badge data-testid="badge-match-high" className="bg-emerald-500 hover:bg-emerald-600">Match Alto</Badge>;
      if (score >= 70) return <Badge data-testid="badge-match-medium" className="bg-amber-500 hover:bg-amber-600">Match Médio</Badge>;
      if (score >= 50) return <Badge data-testid="badge-match-low" className="bg-orange-500 hover:bg-orange-600">Match Baixo</Badge>;
      return <Badge data-testid="badge-found" className="bg-[#4A90E2] hover:bg-[#3A80D2]">Encontrado</Badge>;
    }
    return <Badge data-testid="badge-not-found" variant="destructive">Não encontrado</Badge>;
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="app-container">
      <Toaster position="top-right" richColors />
      
      {/* Expiring Products Modal */}
      <ExpiringProductsModal
        open={showExpiringModal}
        onOpenChange={setShowExpiringModal}
        expiringProducts={expiringProducts}
      />
      
      {/* Image Viewer Modal */}
      <ImageViewerModal
        open={viewerOpen}
        onOpenChange={setViewerOpen}
        image={viewerImage}
        product={viewerProduct}
        zoomLevel={zoomLevel}
        onZoomChange={setZoomLevel}
        onCopyImage={copyImageToClipboard}
      />

      {/* Art Generation Modal */}
      {/* Removed during white-labeling cleanup */}

      {/* Price Search Modal */}
      <Dialog open={showPriceModal} onOpenChange={setShowPriceModal}>
        <DialogContent className={`max-h-[90vh] overflow-y-auto ${priceResults ? 'max-w-6xl' : 'max-w-2xl'}`} data-testid="price-search-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-xl font-semibold">
              <DollarSign className="w-5 h-5 text-emerald-600" />
              Pesquisa de Preços
            </DialogTitle>
            <DialogDescription>
              {selectedProductForPrice?.product_name || "Produto"}
              <span className="ml-2 text-slate-400">EAN: {selectedProductForPrice?.ean}</span>
            </DialogDescription>
          </DialogHeader>
          
          <div className={`mt-4 ${priceResults ? 'flex gap-6' : ''}`}>
          {/* Left side - Form */}
          <div className={`space-y-4 ${priceResults ? 'w-[360px] flex-shrink-0' : ''}`}>
            {/* Store Location Selection */}
            <div className="space-y-2">
              <Label className="text-slate-700 font-medium text-sm flex items-center gap-2">
                <MapPin className="w-4 h-4 text-rose-500" />
                Praça / Loja
              </Label>
              <div className="grid gap-2">
                {STORE_LOCATIONS.map((store) => (
                  <div
                    key={store.id}
                    onClick={() => setSelectedStoreLocation(store.id)}
                    className={`p-2.5 rounded-lg border-2 cursor-pointer transition-all ${
                      selectedStoreLocation === store.id 
                        ? 'border-rose-500 bg-rose-50' 
                        : 'border-slate-200 hover:border-rose-300'
                    }`}
                  >
                    <p className={`text-sm font-medium ${selectedStoreLocation === store.id ? 'text-rose-700' : 'text-slate-700'}`}>
                      {store.name}
                    </p>
                    <p className="text-xs text-slate-500 truncate">{store.address}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Market Types Selection */}
            <div className="space-y-2">
              <Label className="text-slate-700 font-medium text-sm">Tipos de Mercado</Label>
              <div className={`grid gap-1.5 ${priceResults ? 'grid-cols-2' : 'grid-cols-2 sm:grid-cols-3'}`}>
                {MARKET_TYPES.map((market) => {
                  const Icon = market.icon;
                  const isSelected = priceMarketTypes.includes(market.id);
                  return (
                    <div
                      key={market.id}
                      onClick={() => toggleMarketType(market.id)}
                      className={`flex items-center gap-2 p-2 rounded-lg border-2 cursor-pointer transition-all ${
                        isSelected 
                          ? 'border-emerald-500 bg-emerald-50 text-emerald-700' 
                          : 'border-slate-200 hover:border-slate-300 text-slate-600'
                      }`}
                    >
                      <div className={`w-6 h-6 rounded-md flex items-center justify-center ${isSelected ? 'bg-emerald-500 text-white' : 'bg-slate-100'}`}>
                        <Icon className="w-3.5 h-3.5" />
                      </div>
                      <span className="text-xs font-medium">{market.label}</span>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Cost and Markup */}
            <div className="grid grid-cols-3 gap-4">
              {/* Product Cost */}
              <div className="space-y-2">
                <Label className="text-slate-700 font-medium">
                  Custo (R$) *
                  {selectedProductForPrice?.nfe_unit_cost && (
                    <span className="ml-1 text-xs text-emerald-600 font-normal">(NF-e)</span>
                  )}
                </Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">R$</span>
                  <Input 
                    type="number" 
                    step="0.01"
                    value={productCost} 
                    onChange={(e) => handleCostChange(e.target.value)}
                    placeholder="0.00"
                    className="pl-10 border-slate-200"
                  />
                </div>
              </div>

              {/* Markup Percentage */}
              <div className="space-y-2">
                <Label className={`font-medium flex items-center gap-1 ${priceInputMode === "markup" ? "text-emerald-700" : "text-slate-500"}`}>
                  Markup (%)
                  {priceInputMode === "markup" && <span className="text-[10px] bg-emerald-100 text-emerald-700 px-1 rounded">editando</span>}
                </Label>
                <div className="relative">
                  <Input 
                    type="number" 
                    value={markupPercentage} 
                    onChange={(e) => handleMarkupChange(e.target.value)}
                    placeholder="Ex: 30"
                    className={`pr-10 ${priceInputMode === "markup" ? "border-emerald-300 ring-1 ring-emerald-200" : "border-slate-200"}`}
                  />
                  <Percent className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                </div>
              </div>

              {/* Selling Price - NEW */}
              <div className="space-y-2">
                <Label className={`font-medium flex items-center gap-1 ${priceInputMode === "price" ? "text-blue-700" : "text-slate-500"}`}>
                  Preço Venda
                  {priceInputMode === "price" && <span className="text-[10px] bg-blue-100 text-blue-700 px-1 rounded">editando</span>}
                </Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">R$</span>
                  <Input 
                    type="number" 
                    step="0.01"
                    value={sellingPrice} 
                    onChange={(e) => handleSellingPriceChange(e.target.value)}
                    placeholder="0.00"
                    className={`pl-10 ${priceInputMode === "price" ? "border-blue-300 ring-1 ring-blue-200" : "border-slate-200"}`}
                  />
                </div>
              </div>
            </div>

            {/* Preview calculation */}
            {productCost && parseFloat(productCost) > 0 && sellingPrice && parseFloat(sellingPrice) > 0 && (
              <div className="p-3 bg-gradient-to-r from-slate-50 to-emerald-50 rounded-lg border border-slate-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-slate-500 mb-1">Resumo da Precificação:</p>
                    <p className="text-lg font-bold text-slate-800">
                      R$ {parseFloat(productCost).toFixed(2)} → R$ {parseFloat(sellingPrice).toFixed(2)}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-slate-500 mb-1">Margem:</p>
                    <p className={`text-lg font-bold ${parseFloat(markupPercentage) >= 30 ? 'text-emerald-600' : parseFloat(markupPercentage) >= 15 ? 'text-amber-600' : 'text-red-600'}`}>
                      +{markupPercentage || 0}%
                    </p>
                  </div>
                </div>
                <p className="text-xs text-slate-400 mt-1">
                  Lucro bruto: R$ {(parseFloat(sellingPrice) - parseFloat(productCost)).toFixed(2)} por unidade
                </p>
              </div>
            )}

            {/* Purchase Simulation Section - Auto-loaded from last NF-e */}
            <div className="p-4 bg-gradient-to-r from-indigo-50 to-purple-50 rounded-lg border border-indigo-200">
              <h4 className="text-sm font-semibold text-indigo-800 mb-3 flex items-center gap-2">
                <Calculator className="w-4 h-4" />
                Simulação de Compra (Última NF-e)
              </h4>
              
              {loadingPurchaseSimulation ? (
                <div className="flex items-center justify-center py-6">
                  <Loader2 className="w-5 h-5 animate-spin text-indigo-600" />
                  <span className="ml-2 text-sm text-slate-600">Carregando dados da NF-e...</span>
                </div>
              ) : purchaseSimulation ? (
                <div className="space-y-3">
                  {/* NF-e Info Header */}
                  <div className="flex items-center justify-between p-2 bg-white rounded-lg border border-indigo-100">
                    <div className="flex items-center gap-2">
                      <FileText className="w-4 h-4 text-indigo-600" />
                      <span className="text-sm font-medium text-slate-700">NF-e: {purchaseSimulation.nfe_number}</span>
                    </div>
                    <span className="text-xs text-slate-500">
                      {purchaseSimulation.nfe_date && new Date(purchaseSimulation.nfe_date).toLocaleDateString('pt-BR')}
                    </span>
                  </div>
                  
                  {/* Summary Stats */}
                  <div className="grid grid-cols-4 gap-2">
                    <div className="p-2 bg-white rounded-lg border border-indigo-100 text-center">
                      <p className="text-[10px] text-slate-500">Entrada</p>
                      <p className="text-sm font-bold text-indigo-600">{purchaseSimulation.total_qty_entered}</p>
                    </div>
                    <div className="p-2 bg-white rounded-lg border border-indigo-100 text-center">
                      <p className="text-[10px] text-slate-500">Vendido</p>
                      <p className="text-sm font-bold text-emerald-600">{purchaseSimulation.total_sold}</p>
                    </div>
                    <div className="p-2 bg-white rounded-lg border border-indigo-100 text-center">
                      <p className="text-[10px] text-slate-500">Restante</p>
                      <p className="text-sm font-bold text-amber-600">{purchaseSimulation.total_remaining}</p>
                    </div>
                    <div className="p-2 bg-white rounded-lg border border-indigo-100 text-center">
                      <p className="text-[10px] text-slate-500">Média/dia*</p>
                      <p className="text-sm font-bold text-blue-600">{purchaseSimulation.daily_avg_sales_30d}/dia</p>
                    </div>
                  </div>
                  
                  {/* Sales explanation */}
                  <div className="text-[10px] text-slate-400 px-1">
                    * Média calculada em {purchaseSimulation.days_with_sales_30d || 0} dias com vendas nos últimos 30 dias
                    {purchaseSimulation.total_sales_30d > 0 && (
                      <span> ({purchaseSimulation.total_sales_30d} un vendidas)</span>
                    )}
                  </div>
                  
                  {/* Batches List */}
                  <div className="space-y-2">
                    <p className="text-xs text-slate-600 font-medium">Lotes desta NF-e:</p>
                    {purchaseSimulation.batches.map((batch, idx) => {
                      const statusColors = {
                        expired: 'bg-red-50 border-red-300',
                        critical: 'bg-amber-50 border-amber-300',
                        warning: 'bg-yellow-50 border-yellow-300',
                        ok: 'bg-emerald-50 border-emerald-300'
                      };
                      const statusIcons = {
                        expired: '🔴',
                        critical: '🟠',
                        warning: '🟡',
                        ok: '🟢'
                      };
                      
                      return (
                        <div key={idx} className={`p-3 rounded-lg border ${statusColors[batch.status]}`}>
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <span>{statusIcons[batch.status]}</span>
                              <span className="text-sm font-medium text-slate-700">Lote: {batch.batch_number}</span>
                            </div>
                            <span className="text-xs text-slate-500">
                              Validade: {batch.expiry_date && new Date(batch.expiry_date).toLocaleDateString('pt-BR')}
                            </span>
                          </div>
                          
                          <div className="grid grid-cols-2 gap-2 mb-2">
                            <div className="text-xs">
                              <span className="text-slate-500">Restante: </span>
                              <span className="font-bold text-slate-700">{batch.remaining_qty}/{batch.initial_qty} un</span>
                            </div>
                            <div className="text-xs text-right">
                              <span className="text-slate-500">Dias p/ vencer: </span>
                              <span className={`font-bold ${batch.days_until_expiry <= 0 ? 'text-red-600' : batch.days_until_expiry <= 7 ? 'text-amber-600' : 'text-emerald-600'}`}>
                                {batch.days_until_expiry !== null ? (batch.days_until_expiry <= 0 ? 'Vencido' : `${batch.days_until_expiry} dias`) : 'N/A'}
                              </span>
                            </div>
                          </div>
                          
                          {batch.required_daily_sales !== null && batch.days_until_expiry > 0 && (
                            <div className="pt-2 border-t border-slate-200">
                              <div className="flex items-center justify-between">
                                <span className="text-xs text-slate-600">Meta p/ zerar antes de vencer:</span>
                                <div className="text-right">
                                  <span className={`text-sm font-bold ${batch.is_achievable === false ? 'text-red-600' : batch.is_achievable ? 'text-emerald-600' : 'text-slate-700'}`}>
                                    {batch.required_daily_sales} un/dia
                                  </span>
                                  <span className="text-xs text-slate-500 ml-1">
                                    ({batch.required_weekly_sales} un/sem)
                                  </span>
                                </div>
                              </div>
                              {batch.is_achievable === false && purchaseSimulation.daily_avg_sales_30d > 0 && (
                                <p className="text-xs text-red-600 mt-1 flex items-center gap-1">
                                  <AlertTriangle className="w-3 h-3" />
                                  Média atual ({purchaseSimulation.daily_avg_sales_30d}/dia) abaixo da meta!
                                </p>
                              )}
                              {batch.is_achievable === true && (
                                <p className="text-xs text-emerald-600 mt-1 flex items-center gap-1">
                                  <Check className="w-3 h-3" />
                                  Meta alcançável com base no histórico
                                </p>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ) : (
                <div className="p-4 bg-white/50 rounded-lg text-center">
                  <p className="text-sm text-slate-500">Nenhuma NF-e encontrada para este produto</p>
                  <p className="text-xs text-slate-400 mt-1">Importe uma NF-e para ver a simulação de compra</p>
                </div>
              )}
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3">
              <Button 
                onClick={searchPrices} 
                disabled={searchingPrices || priceMarketTypes.length === 0 || !productCost || parseFloat(productCost) <= 0}
                className="flex-1 h-12 bg-emerald-600 hover:bg-emerald-700 text-white font-medium"
              >
                {searchingPrices ? (
                  <><Loader2 className="w-5 h-5 mr-2 animate-spin" />Pesquisando...</>
                ) : (
                  <><Search className="w-5 h-5 mr-2" />Pesquisar Preços</>
                )}
              </Button>
            </div>

          </div>
            
            {/* Right side - Results */}
            {priceResults && (
              <div className="flex-1 space-y-4 border-l border-slate-200 pl-6">
                {/* Summary Cards - 2x2 grid */}
                <div className="grid grid-cols-2 gap-3">
                  {/* Cost */}
                  <div className="p-3 rounded-xl bg-slate-100 border border-slate-200 text-center">
                    <p className="text-xs text-slate-500 mb-1">Seu Custo</p>
                    <p className="text-xl font-bold text-slate-700">R$ {priceResults.product_cost?.toFixed(2)}</p>
                  </div>
                  {/* Selling Price */}
                  <div className="p-3 rounded-xl bg-[#4A90E2]/10 border border-[#4A90E2]/30 text-center">
                    <p className="text-xs text-[#4A90E2] mb-1">Seu Preço (+{priceResults.markup_percentage}%)</p>
                    <p className="text-xl font-bold text-[#4A90E2]">R$ {priceResults.selling_price?.toFixed(2)}</p>
                  </div>
                  {/* Average Price */}
                  <div className="p-3 rounded-xl bg-slate-50 border border-slate-200 text-center">
                    <p className="text-xs text-slate-500 mb-1">Média Concorrência</p>
                    <p className="text-xl font-bold text-slate-900">R$ {priceResults.average_price?.toFixed(2)}</p>
                  </div>
                  {/* Discount */}
                  <div className={`p-3 rounded-xl border text-center ${
                    priceResults.discount_vs_average > 0 
                      ? 'bg-emerald-50 border-emerald-200' 
                      : 'bg-amber-50 border-amber-200'
                  }`}>
                    <p className="text-xs mb-1" style={{color: priceResults.discount_vs_average > 0 ? '#059669' : '#d97706'}}>
                      {priceResults.discount_vs_average > 0 ? 'Mais Barato' : 'Mais Caro'}
                    </p>
                    <p className="text-xl font-bold flex items-center justify-center gap-1" style={{color: priceResults.discount_vs_average > 0 ? '#059669' : '#d97706'}}>
                      {priceResults.discount_vs_average > 0 && <TrendingDown className="w-4 h-4" />}
                      {Math.abs(priceResults.discount_vs_average).toFixed(1)}%
                    </p>
                  </div>
                </div>

                {/* Price Cards - 3 columns */}
                <div className="space-y-2">
                  <Label className="text-slate-700 font-medium">Preços Encontrados ({priceResults.prices_found?.length || 0})</Label>
                  <div className="grid grid-cols-3 gap-2 max-h-[400px] overflow-y-auto pr-1">
                    {priceResults.prices_found?.map((price, index) => {
                      const Icon = getMarketIcon(price.market_type);
                      const marketLabel = MARKET_TYPES.find(m => m.id === price.market_type)?.label || price.market_type;
                      return (
                        <div key={index} className="p-2.5 rounded-lg border border-slate-200 bg-white hover:shadow-md transition-shadow">
                          <div className="flex items-center gap-2 mb-1">
                            <div className="w-7 h-7 rounded-md bg-slate-100 flex items-center justify-center flex-shrink-0">
                              <Icon className="w-4 h-4 text-slate-600" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-slate-900 text-sm truncate">{price.market_name}</p>
                              <p className="text-xs text-slate-500">{marketLabel}</p>
                            </div>
                          </div>
                          <div className="flex items-center justify-between mt-2 pt-2 border-t border-slate-100">
                            <p className="text-lg font-bold text-emerald-600">R$ {price.price?.toFixed(2)}</p>
                            {priceResults.average_price > 0 && (
                              <p className={`text-xs ${price.price < priceResults.average_price ? 'text-emerald-500' : 'text-amber-500'}`}>
                                {price.price < priceResults.average_price ? '▼' : '▲'} 
                                {Math.abs(((price.price - priceResults.average_price) / priceResults.average_price) * 100).toFixed(0)}%
                              </p>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
                
                {/* Similar Products Section */}
                {priceResults.similar_products && priceResults.similar_products.length > 0 && (
                  <div className="space-y-2 mt-4 pt-4 border-t border-slate-200">
                    <Label className="text-slate-700 font-medium flex items-center gap-2">
                      <Package className="w-4 h-4 text-purple-600" />
                      Produtos Similares de Outras Marcas ({priceResults.similar_products.length})
                    </Label>
                    <p className="text-xs text-slate-500 mb-2">
                      Produtos com mesmo peso/quantidade de marcas concorrentes
                    </p>
                    <div className="grid grid-cols-2 gap-3">
                      {priceResults.similar_products.map((product, index) => {
                        const priceDiff = priceResults.selling_price > 0 && product.avg_price > 0
                          ? ((priceResults.selling_price - product.avg_price) / product.avg_price) * 100
                          : 0;
                        const isOurPriceLower = priceDiff < 0;
                        
                        return (
                          <div 
                            key={index} 
                            className="p-3 rounded-xl border-2 border-purple-200 bg-gradient-to-br from-purple-50 to-indigo-50 hover:shadow-md transition-shadow"
                          >
                            <div className="flex items-start gap-2 mb-2">
                              <div className="w-8 h-8 rounded-lg bg-purple-100 flex items-center justify-center flex-shrink-0">
                                <span className="text-purple-700 font-bold text-sm">{index + 1}</span>
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="font-semibold text-slate-800 text-sm leading-tight">
                                  {product.brand}
                                </p>
                                <p className="text-xs text-slate-600 truncate" title={product.product_name}>
                                  {product.product_name}
                                </p>
                              </div>
                            </div>
                            
                            <div className="flex items-center justify-between bg-white rounded-lg p-2">
                              <div>
                                <p className="text-[10px] text-slate-400 uppercase">Peso/Qtde</p>
                                <p className="text-xs font-medium text-slate-700">{product.weight_qty}</p>
                              </div>
                              <div className="text-right">
                                <p className="text-[10px] text-slate-400 uppercase">Preço Médio</p>
                                <p className="text-lg font-bold text-purple-700">
                                  R$ {product.avg_price?.toFixed(2)}
                                </p>
                              </div>
                            </div>
                            
                            {priceResults.selling_price > 0 && product.avg_price > 0 && (
                              <div className={`mt-2 p-2 rounded-lg text-center ${
                                isOurPriceLower 
                                  ? 'bg-emerald-100 text-emerald-700' 
                                  : 'bg-amber-100 text-amber-700'
                              }`}>
                                <p className="text-xs font-medium">
                                  {isOurPriceLower ? (
                                    <>Seu preço é <span className="font-bold">{Math.abs(priceDiff).toFixed(1)}% menor</span></>
                                  ) : (
                                    <>Seu preço é <span className="font-bold">{Math.abs(priceDiff).toFixed(1)}% maior</span></>
                                  )}
                                </p>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* NF-e History Modal */}
      <NfeHistoryModal
        open={showNfeHistory}
        onOpenChange={setShowNfeHistory}
        search={nfeHistorySearch}
        setSearch={setNfeHistorySearch}
        dateFrom={nfeHistoryDateFrom}
        setDateFrom={setNfeHistoryDateFrom}
        dateTo={nfeHistoryDateTo}
        setDateTo={setNfeHistoryDateTo}
        nfeHistory={nfeHistory}
        loading={loadingNfeHistory}
        syncingNfes={syncingNfes}
        onSearch={loadNfeHistory}
        onSyncFromPg={syncNfesFromPg}
        onLoadNfeFromHistory={loadNfeFromHistory}
        onDeleteNfe={deleteNfe}
      />

      {/* Layout with Sidebar */}
      <div className="flex min-h-screen">
        {/* Mobile Header */}
        <div className="fixed top-0 left-0 right-0 h-14 bg-slate-900 text-white flex items-center justify-between px-4 z-50 md:hidden">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-[#4A90E2] flex items-center justify-center">
              <Barcode className="w-5 h-5 text-white" />
            </div>
            <span className="font-semibold text-sm">EAN Extractor</span>
          </div>
          <button 
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="p-2 rounded-lg hover:bg-slate-700"
          >
            <Menu className="w-6 h-6" />
          </button>
        </div>

        {/* Mobile Menu Overlay */}
        {mobileMenuOpen && (
          <div 
            className="fixed inset-0 bg-black/50 z-40 md:hidden"
            onClick={() => setMobileMenuOpen(false)}
          />
        )}

        {/* Sidebar - Hidden on mobile, visible on md+ */}
        <aside className={`
          ${sidebarCollapsed ? 'w-16' : 'w-64'} 
          bg-slate-900 text-white flex flex-col transition-all duration-300 fixed h-full z-50
          ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'} md:translate-x-0
        `}>
          {/* Logo */}
          <div className="p-4 border-b border-slate-700 flex items-center justify-between">
            <div className={`flex items-center gap-3 ${sidebarCollapsed ? 'justify-center w-full' : ''}`}>
              <div className="w-10 h-10 rounded-xl bg-[#4A90E2] flex items-center justify-center flex-shrink-0">
                <Package className="w-6 h-6 text-white" />
              </div>
              {!sidebarCollapsed && (
                <div>
                  <h1 className="text-lg font-semibold tracking-tight">Grupo Best By</h1>
                  <p className="text-xs text-slate-400">Sistema de Gestão</p>
                </div>
              )}
            </div>
            <button 
              onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
              className={`p-1.5 rounded-lg hover:bg-slate-700 transition-colors ${sidebarCollapsed ? 'absolute -right-3 top-6 bg-slate-800 border border-slate-600' : ''}`}
            >
              {sidebarCollapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-3 space-y-1">
            {/* Dashboards - First menu item */}
            <button
              onClick={() => { setActiveSection("dashboards"); setMobileMenuOpen(false); }}
              className={`w-full flex items-center gap-3 px-3 py-3 rounded-xl transition-all ${
                activeSection === "dashboards" 
                  ? 'bg-emerald-600 text-white shadow-lg shadow-emerald-600/30' 
                  : 'text-slate-300 hover:bg-slate-800 hover:text-white'
              } ${sidebarCollapsed ? 'justify-center' : ''}`}
              title="Dashboards"
            >
              <BarChart3 className="w-5 h-5 flex-shrink-0" />
              {!sidebarCollapsed && <span className="font-medium">Dashboards</span>}
            </button>

            <button
              onClick={() => { setActiveSection("precificacao"); setMobileMenuOpen(false); }}
              className={`w-full flex items-center gap-3 px-3 py-3 rounded-xl transition-all ${
                activeSection === "precificacao" 
                  ? 'bg-[#4A90E2] text-white shadow-lg shadow-[#4A90E2]/30' 
                  : 'text-slate-300 hover:bg-slate-800 hover:text-white'
              } ${sidebarCollapsed ? 'justify-center' : ''}`}
              title="Entrada de Mercadoria"
            >
              <DollarSign className="w-5 h-5 flex-shrink-0" />
              {!sidebarCollapsed && <span className="font-medium">Entrada de Mercadoria</span>}
            </button>

            <button
              onClick={() => { setActiveSection("estoque"); setMobileMenuOpen(false); }}
              className={`w-full flex items-center gap-3 px-3 py-3 rounded-xl transition-all ${
                activeSection === "estoque" 
                  ? 'bg-amber-600 text-white shadow-lg shadow-amber-600/30' 
                  : 'text-slate-300 hover:bg-slate-800 hover:text-white'
              } ${sidebarCollapsed ? 'justify-center' : ''}`}
              title="Estoque Virtual"
            >
              <Package className="w-5 h-5 flex-shrink-0" />
              {!sidebarCollapsed && (
                <span className="font-medium flex items-center gap-2">
                  Estoque
                  {lowStockProducts.length > 0 && (
                    <span className="px-1.5 py-0.5 text-xs bg-red-500 rounded-full">{lowStockProducts.length}</span>
                  )}
                </span>
              )}
            </button>

            {/* P&L Gerencial */}
            <button 
              onClick={() => { setActiveSection("pnl"); setMobileMenuOpen(false); }}
              className={`w-full flex items-center gap-3 px-3 py-3 rounded-xl transition-all ${
                activeSection === "pnl" 
                  ? 'bg-amber-600 text-white shadow-lg shadow-amber-600/30' 
                  : 'text-slate-300 hover:bg-slate-800 hover:text-white'
              } ${sidebarCollapsed ? 'justify-center' : ''}`}
              title="P&L Gerencial"
              data-testid="pnl-menu-btn"
            >
              <FileSpreadsheet className="w-5 h-5 flex-shrink-0" />
              {!sidebarCollapsed && (
                <span className="font-medium">P&L Gerencial</span>
              )}
            </button>

            {/* Compras */}
            <button 
              onClick={() => { setActiveSection("compras"); setMobileMenuOpen(false); }}
              className={`w-full flex items-center gap-3 px-3 py-3 rounded-xl transition-all ${
                activeSection === "compras" 
                  ? 'bg-teal-600 text-white shadow-lg shadow-teal-600/30' 
                  : 'text-slate-300 hover:bg-slate-800 hover:text-white'
              } ${sidebarCollapsed ? 'justify-center' : ''}`}
              title="Compras"
              data-testid="compras-menu-btn"
            >
              <ShoppingCart className="w-5 h-5 flex-shrink-0" />
              {!sidebarCollapsed && (
                <span className="font-medium">Compras</span>
              )}
            </button>

            {/* Cobranças PIX */}
            <button 
              onClick={() => { setActiveSection("cobrancas"); setMobileMenuOpen(false); }}
              className={`w-full flex items-center gap-3 px-3 py-3 rounded-xl transition-all ${
                activeSection === "cobrancas" 
                  ? 'bg-blue-600 text-white shadow-lg shadow-blue-600/30' 
                  : 'text-slate-300 hover:bg-slate-800 hover:text-white'
              } ${sidebarCollapsed ? 'justify-center' : ''}`}
              title="Cobranças PIX"
              data-testid="cobrancas-menu-btn"
            >
              <QrCode className="w-5 h-5 flex-shrink-0" />
              {!sidebarCollapsed && (
                <span className="font-medium">Cobranças PIX</span>
              )}
            </button>

            {/* Controle de Caixa */}
            <button 
              onClick={() => { setActiveSection("caixa"); setMobileMenuOpen(false); }}
              className={`w-full flex items-center gap-3 px-3 py-3 rounded-xl transition-all ${
                activeSection === "caixa" 
                  ? 'bg-emerald-600 text-white shadow-lg shadow-emerald-600/30' 
                  : 'text-slate-300 hover:bg-slate-800 hover:text-white'
              } ${sidebarCollapsed ? 'justify-center' : ''}`}
              title="Controle de Caixa"
              data-testid="caixa-menu-btn"
            >
              <DollarSign className="w-5 h-5 flex-shrink-0" />
              {!sidebarCollapsed && (
                <span className="font-medium">Controle de Caixa</span>
              )}
            </button>
          </nav>

          {/* Bluesoft Quota Footer */}
          {!sidebarCollapsed && (
            <div className="p-3 border-t border-slate-700">
              <div className="p-3 bg-slate-800 rounded-xl">
                <p className="text-xs text-slate-400 mb-2">API Bluesoft</p>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-medium">{bluesoftQuota.total_remaining} consultas</span>
                  <span className="text-xs text-slate-400">de {bluesoftQuota.total_limit}</span>
                </div>
                <div className="w-full h-2 bg-slate-700 rounded-full overflow-hidden">
                  <div 
                    className={`h-full rounded-full transition-all ${
                      bluesoftQuota.total_remaining <= 15 ? 'bg-red-500' : 
                      bluesoftQuota.total_remaining <= 30 ? 'bg-amber-500' : 'bg-emerald-500'
                    }`}
                    style={{ width: `${((bluesoftQuota.total_limit - bluesoftQuota.total_remaining) / bluesoftQuota.total_limit) * 100}%` }}
                  />
                </div>
              </div>

              {/* Powered by CrowdSA */}
              <a
                href="https://www.crowdsa.com.br"
                target="_blank"
                rel="noopener noreferrer"
                className="mt-3 flex flex-col items-center hover:opacity-80 transition-opacity"
                data-testid="crowdsa-sidebar-badge"
              >
                <div className="flex items-center gap-2">
                  <span className="text-[10px] text-slate-400">Powered by</span>
                  <img src="/crowdsa-logo.png" alt="CrowdSA" className="h-4" />
                </div>
                <span className="text-[9px] text-slate-500 mt-1">© 2026 CrowdSA. All rights reserved.</span>
              </a>
            </div>
          )}
        </aside>

        {/* Main Content Area */}
        <div className={`flex-1 transition-all duration-300 pt-14 md:pt-0 ${sidebarCollapsed ? 'md:ml-16' : 'md:ml-64'}`}>
          {/* Top Bar */}
          <header className="bg-white/80 backdrop-blur-md border-b border-slate-200/50 sticky top-14 md:top-0 z-30 px-4 md:px-6 py-3 md:py-4">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg md:text-xl font-semibold text-slate-900 tracking-tight">
                  {activeSection === "precificacao" && "Entrada de Mercadoria"}
                  {activeSection === "dashboards" && "Dashboards de Vendas"}
                  {activeSection === "estoque" && "Estoque Virtual"}
                  {activeSection === "pnl" && "P&L Gerencial"}
                  {activeSection === "compras" && "Previsões de Compra"}
                  {activeSection === "cobrancas" && "Cobranças PIX"}
                  {activeSection === "caixa" && "Controle de Caixa"}
                </h2>
                <p className="text-sm text-slate-500">
                  {activeSection === "precificacao" && "Consulta EAN e importação de NF-e"}
                  {activeSection === "dashboards" && "Análise de vendas e métricas"}
                  {activeSection === "estoque" && "Controle de estoque e lotes com validade"}
                  {activeSection === "pnl" && "Demonstrativo de Resultado do Exercício"}
                  {activeSection === "compras" && "Gerencie fornecedores e pedidos de compra"}
                  {activeSection === "cobrancas" && "Gere e gerencie cobranças PIX via Banco do Brasil"}
                  {activeSection === "caixa" && "Saldos em cofre, caixinha e registradoras por loja"}
                </p>
              </div>
              
              <div className="flex items-center gap-3">
                {/* Global Store Selector - Only show for sections that use it */}
                {(activeSection === "compras") && (
                  <div className="flex items-center gap-2 bg-slate-100 rounded-lg px-3 py-1.5">
                    <Store className="w-4 h-4 text-slate-500" />
                    <select
                      value={selectedStoreId}
                      onChange={(e) => setSelectedStoreId(e.target.value)}
                      className="bg-transparent border-none text-sm font-medium text-slate-700 focus:outline-none cursor-pointer"
                    >
                      {stores.map(store => (
                        <option key={store.id} value={store.id}>{store.name}</option>
                      ))}
                    </select>
                  </div>
                )}
                {activeSection === "precificacao" && results.length > 0 && (
                  <Button onClick={exportToExcel} className="bg-[#4A90E2] hover:bg-[#3A80D2] text-white">
                    <FileSpreadsheet className="w-4 h-4 mr-2" />Exportar Excel
                  </Button>
                )}
                {activeSection === "dashboards" && (
                  <div className="flex items-center gap-3">
                    {/* Multi-Store Checkbox Selector */}
                    <div className="flex items-center gap-3 bg-white rounded-lg border border-slate-200 px-3 py-2">
                      <span className="text-xs text-slate-500 font-medium">Lojas:</span>
                      
                      {/* Select All Checkbox */}
                      <label className="flex items-center gap-1.5 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={isAllStoresSelected}
                          onChange={(e) => e.target.checked ? selectAllStores() : clearStoreSelection()}
                          className="w-4 h-4 rounded border-slate-300 text-[#4A90E2] focus:ring-[#4A90E2]"
                        />
                        <span className="text-xs text-slate-600 font-medium">Todas</span>
                      </label>
                      
                      <div className="w-px h-4 bg-slate-200" />
                      
                      {/* Individual Store Checkboxes */}
                      {stores.map(store => (
                        <label key={store.id} className="flex items-center gap-1.5 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={selectedStoreIds.includes(store.id)}
                            onChange={() => toggleStoreSelection(store.id)}
                            className="w-4 h-4 rounded border-slate-300 text-[#4A90E2] focus:ring-[#4A90E2]"
                          />
                          <span className="text-xs text-slate-600 whitespace-nowrap">{store.name}</span>
                        </label>
                      ))}
                    </div>
                    
                    {selectedStoreIds.length > 0 && (
                      <Button onClick={() => reloadDashboardData(selectedStoreIds)} variant="outline" className="border-slate-200">
                        <RefreshCw className="w-4 h-4 mr-2" />Atualizar
                      </Button>
                    )}
                  </div>
                )}
              </div>
            </div>
          </header>

          {/* Main Content */}
          <main className="p-3 md:p-6">
            {/* ==================== PRECIFICAÇÃO SECTION ==================== */}
            {activeSection === "precificacao" && (
              <>
                {/* Store Selector for Entrada de Mercadoria */}
                <Card className="mb-4 shadow-sm border-slate-200">
                  <CardContent className="py-3 px-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Store className="w-5 h-5 text-[#4A90E2]" />
                        <span className="text-sm font-medium text-slate-700">Loja para importação:</span>
                      </div>
                      <select
                        value={nfeImportStoreId}
                        onChange={(e) => setNfeImportStoreId(e.target.value)}
                        className="bg-white border border-slate-200 rounded-lg px-3 py-2 text-sm font-medium text-slate-700 focus:outline-none focus:ring-2 focus:ring-[#4A90E2]/20 focus:border-[#4A90E2] cursor-pointer min-w-[200px]"
                      >
                        {STORE_LOCATIONS.map(store => (
                          <option key={store.id} value={store.id}>{store.name}</option>
                        ))}
                      </select>
                    </div>
                  </CardContent>
                </Card>

                {/* Search Command Center */}
                <Card className="mb-4 md:mb-8 shadow-[0_2px_8px_rgba(0,0,0,0.04)] border-slate-200" data-testid="search-command-center">
                  <CardHeader className="pb-2 md:pb-4 px-3 md:px-6">
                    <CardTitle className="text-lg md:text-2xl font-semibold text-slate-900 tracking-tight font-[Manrope]">Importação de NF-e</CardTitle>
                    <CardDescription className="text-xs md:text-base text-slate-500">Importe notas fiscais via arquivo XML ou consulta SEFAZ</CardDescription>
                  </CardHeader>
                  <CardContent className="px-3 md:px-6">
                <div className="space-y-4">
                  {/* Header with History Button */}
                  <div className="flex items-center justify-end">
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => { setShowNfeHistory(true); loadNfeHistory(); }}
                      className="border-slate-200"
                    >
                      <History className="w-4 h-4 mr-2" />Histórico NF-e
                    </Button>
                  </div>

                  {/* Source Tabs: Arquivo vs SEFAZ */}
                  <div className="mb-4">
                    <div className="flex gap-2 border-b border-slate-200 pb-2">
                      <button
                        onClick={() => setNfeSourceTab("arquivo")}
                        className={`px-3 md:px-4 py-2 text-xs md:text-sm font-medium rounded-t-lg transition-colors ${
                          nfeSourceTab === "arquivo"
                            ? 'bg-white border border-b-white border-slate-200 text-[#4A90E2] -mb-[2px]'
                            : 'text-slate-500 hover:text-slate-700'
                        }`}
                      >
                        <Upload className="w-4 h-4 md:mr-2 inline" />
                        <span className="hidden md:inline">Arquivo XML</span>
                        <span className="md:hidden">XML</span>
                      </button>
                      <button
                        onClick={() => setNfeSourceTab("sefaz")}
                        className={`px-3 md:px-4 py-2 text-xs md:text-sm font-medium rounded-t-lg transition-colors ${
                          nfeSourceTab === "sefaz"
                            ? 'bg-white border border-b-white border-slate-200 text-[#4A90E2] -mb-[2px]'
                            : 'text-slate-500 hover:text-slate-700'
                        }`}
                      >
                        <Key className="w-4 h-4 md:mr-2 inline" />
                        <span className="hidden md:inline">Consultar SEFAZ - Chave de Acesso</span>
                        <span className="md:hidden">SEFAZ</span>
                      </button>
                    </div>
                  </div>

                  {/* SEFAZ Tab Content */}
                  {nfeSourceTab === "sefaz" && (
                    <div className="space-y-4">
                      {/* Company Selector */}
                      <div className="p-3 md:p-4 bg-slate-50 rounded-xl border border-slate-200">
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-3">
                          <div className="flex items-center gap-2">
                            <Building className="w-5 h-5 text-[#4A90E2]" />
                            <span className="font-medium text-slate-700 text-sm md:text-base">Selecione a Empresa</span>
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setShowAddCompany(!showAddCompany)}
                            className="border-slate-200 text-xs md:text-sm"
                          >
                            <Plus className="w-4 h-4 mr-1" />
                            Nova Empresa
                          </Button>
                        </div>

                        {/* Add Company Form */}
                        {showAddCompany && (
                          <div className="mb-4 p-3 bg-white rounded-lg border border-slate-200">
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
                              <div>
                                <Label className="text-xs text-slate-500">Nome da Empresa</Label>
                                <Input
                                  value={newCompanyName}
                                  onChange={(e) => setNewCompanyName(e.target.value)}
                                  placeholder="Ex: Minha Empresa Ltda"
                                  className="mt-1 text-sm"
                                />
                              </div>
                              <div>
                                <Label className="text-xs text-slate-500">CNPJ</Label>
                                <Input
                                  value={newCompanyCnpj}
                                  onChange={(e) => setNewCompanyCnpj(e.target.value)}
                                  placeholder="00.000.000/0000-00"
                                  className="mt-1 text-sm"
                                />
                              </div>
                            </div>
                            <div className="flex gap-2 justify-end">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setShowAddCompany(false)}
                              >
                                Cancelar
                              </Button>
                              <Button
                                size="sm"
                                onClick={addCompany}
                                disabled={addingCompany}
                                className="bg-[#4A90E2] hover:bg-[#3A80D2] text-white"
                              >
                                {addingCompany ? <Loader2 className="w-4 h-4 animate-spin" /> : "Cadastrar"}
                              </Button>
                            </div>
                          </div>
                        )}

                        {/* Company List */}
                        {loadingCompanies ? (
                          <div className="flex items-center justify-center py-4">
                            <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
                          </div>
                        ) : companies.length === 0 ? (
                          <div className="text-center py-4 text-slate-500 text-sm">
                            Nenhuma empresa cadastrada. Clique em "Nova Empresa" para adicionar.
                          </div>
                        ) : (
                          <div className="space-y-2">
                            {companies.map(company => (
                              <div
                                key={company.id}
                                onClick={() => setSelectedCompanyId(company.id)}
                                className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${
                                  selectedCompanyId === company.id
                                    ? 'border-[#4A90E2] bg-blue-50'
                                    : 'border-transparent bg-white hover:border-slate-300'
                                }`}
                              >
                                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                                  <div className="flex items-center gap-2 sm:gap-3 min-w-0">
                                    <div className={`w-3 h-3 rounded-full flex-shrink-0 ${company.has_certificate ? 'bg-emerald-500' : 'bg-amber-400'}`} />
                                    <div className="min-w-0">
                                      <p className="font-medium text-slate-800 text-sm md:text-base truncate">{company.name}</p>
                                      <p className="text-xs text-slate-500 truncate">CNPJ: {company.cnpj}</p>
                                    </div>
                                  </div>
                                  <div className="flex items-center gap-2 justify-end">
                                    {company.has_certificate ? (
                                      <Badge className="bg-emerald-100 text-emerald-700 text-[10px] whitespace-nowrap">
                                        <Check className="w-2.5 h-2.5 mr-0.5" />OK
                                      </Badge>
                                    ) : (
                                      <Badge className="bg-amber-100 text-amber-700 text-[10px] whitespace-nowrap">
                                        <AlertTriangle className="w-2.5 h-2.5 mr-0.5" />Sem Cert.
                                      </Badge>
                                    )}
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={(e) => { e.stopPropagation(); deleteCompany(company.id); }}
                                      className="h-7 w-7 p-0 text-slate-400 hover:text-red-500"
                                    >
                                      <Trash2 className="w-3.5 h-3.5" />
                                    </Button>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>

                      {/* Certificate Upload - Show only if company selected and has no certificate */}
                      {selectedCompanyId && !companies.find(c => c.id === selectedCompanyId)?.has_certificate && (
                        <div className="p-3 md:p-4 bg-amber-50 rounded-xl border border-amber-200">
                          <div className="flex items-center gap-2 mb-2">
                            <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0" />
                            <span className="font-medium text-amber-800 text-sm md:text-base">Certificado Digital</span>
                          </div>
                          <p className="text-xs md:text-sm text-amber-700 mb-3">
                            Envie o certificado (.pfx ou .p12) para consultar na SEFAZ.
                          </p>

                          {/* Drag & Drop Area */}
                          <div
                            onClick={() => certInputRef.current?.click()}
                            onDragOver={(e) => { e.preventDefault(); setIsDraggingCert(true); }}
                            onDragLeave={() => setIsDraggingCert(false)}
                            onDrop={handleCertDrop}
                            className={`border-2 border-dashed rounded-xl p-4 md:p-6 text-center cursor-pointer transition-colors ${
                              isDraggingCert
                                ? 'border-[#4A90E2] bg-blue-50'
                                : certFile
                                  ? 'border-emerald-400 bg-emerald-50'
                                  : 'border-amber-300 hover:border-[#4A90E2] hover:bg-white'
                            }`}
                          >
                            <input
                              ref={certInputRef}
                              type="file"
                              accept=".pfx,.p12"
                              onChange={handleCertFileSelect}
                              className="hidden"
                            />
                            {certFile ? (
                              <div className="flex items-center justify-center gap-2 text-emerald-700">
                                <CheckCircle className="w-5 h-5 flex-shrink-0" />
                                <span className="font-medium text-sm truncate max-w-[180px]">{certFile.name}</span>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={(e) => { e.stopPropagation(); setCertFile(null); }}
                                  className="h-6 w-6 p-0 text-slate-400 hover:text-red-500 flex-shrink-0"
                                >
                                  <X className="w-4 h-4" />
                                </Button>
                              </div>
                            ) : (
                              <>
                                <Upload className="w-6 h-6 md:w-8 md:h-8 mx-auto mb-2 text-amber-400" />
                                <p className="text-xs md:text-sm text-amber-700">Toque para selecionar</p>
                                <p className="text-xs text-amber-500 mt-1">.pfx ou .p12</p>
                              </>
                            )}
                          </div>

                          {/* Password Field */}
                          <div className="mt-3">
                            <Label className="text-xs md:text-sm text-amber-800">Senha do Certificado</Label>
                            <Input
                              type="password"
                              value={certPassword}
                              onChange={(e) => setCertPassword(e.target.value)}
                              placeholder="Digite a senha do certificado"
                              className="mt-1 bg-white"
                            />
                          </div>

                          {/* Upload Button */}
                          <Button
                            onClick={uploadCertificate}
                            disabled={!certFile || !certPassword || uploadingCert}
                            className="w-full mt-4 bg-amber-600 hover:bg-amber-700 text-white"
                          >
                            {uploadingCert ? (
                              <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Enviando...</>
                            ) : (
                              <><Upload className="w-4 h-4 mr-2" />Enviar Certificado</>
                            )}
                          </Button>
                        </div>
                      )}

                      {/* SEFAZ Query Section - Show only if company has certificate */}
                      {selectedCompanyId && companies.find(c => c.id === selectedCompanyId)?.has_certificate && (
                        <div className="space-y-3 md:space-y-4">
                          {/* Header */}
                          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                            <div>
                              <h4 className="font-medium text-slate-800 text-sm md:text-base">Consulta por Chave</h4>
                              <p className="text-xs md:text-sm text-slate-500">Digite os 44 dígitos da NF-e</p>
                            </div>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => deleteCertificate(selectedCompanyId)}
                              className="border-red-200 text-red-600 hover:bg-red-50 text-xs self-end sm:self-auto"
                            >
                              <Trash2 className="w-3.5 h-3.5 mr-1" />Remover Cert.
                            </Button>
                          </div>

                          {/* Chave input */}
                          <div className="p-3 md:p-4 bg-slate-50 rounded-xl">
                            <Label className="text-xs md:text-sm text-slate-700 font-medium">Chave de Acesso</Label>
                            <div className="flex flex-col sm:flex-row gap-2 mt-2">
                              <Input
                                value={chaveNfe}
                                onChange={(e) => setChaveNfe(e.target.value.replace(/[^\d]/g, ""))}
                                placeholder="44 dígitos"
                                className="font-mono text-sm flex-1"
                                maxLength={44}
                              />
                              <Button
                                onClick={consultarPorChave}
                                disabled={loadingChave || chaveNfe.length !== 44}
                                className="bg-[#4A90E2] hover:bg-[#3A80D2] text-white w-full sm:w-auto"
                              >
                                {loadingChave ? (
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                  <><Search className="w-4 h-4 mr-1" />Buscar</>
                                )}
                              </Button>
                            </div>
                            <p className="text-xs text-slate-400 mt-2">
                              {chaveNfe.length}/44 dígitos
                              {chaveNfe.length === 44 && <span className="text-emerald-500 ml-2">✓ Válido</span>}
                            </p>
                          </div>

                          {chaveError && (
                            <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-red-700 text-xs md:text-sm">
                              <AlertCircle className="w-4 h-4 inline mr-2" />
                              {chaveError}
                            </div>
                          )}

                          {loadingChave && (
                            <div className="flex items-center justify-center py-6">
                              <Loader2 className="w-5 h-5 animate-spin text-[#4A90E2]" />
                              <span className="ml-2 text-sm text-slate-500">Consultando...</span>
                            </div>
                          )}

                          {nfeByChave && (
                            <div className={`p-3 md:p-4 rounded-xl border ${
                              nfeByChave.already_imported
                                ? 'bg-emerald-50 border-emerald-200'
                                : 'bg-white border-slate-200'
                            }`}>
                              <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
                                <div className="min-w-0">
                                  <div className="flex items-center gap-2 mb-2 flex-wrap">
                                    <p className="font-semibold text-base md:text-lg text-slate-900">NF-e #{nfeByChave.nfe_number}</p>
                                    {nfeByChave.already_imported ? (
                                      <Badge className="bg-emerald-100 text-emerald-700 text-[10px]">
                                        <Check className="w-3 h-3 mr-1" />Importada
                                      </Badge>
                                    ) : (
                                      <Badge className="bg-amber-100 text-amber-700 text-[10px]">Pendente</Badge>
                                    )}
                                  </div>
                                  <p className="text-slate-700 font-medium text-sm truncate">{nfeByChave.emitente_nome}</p>
                                  <div className="grid grid-cols-2 gap-x-4 gap-y-1 mt-2 text-xs md:text-sm text-slate-500">
                                    <span className="truncate">CNPJ: {nfeByChave.emitente_cnpj}</span>
                                    <span>Data: {nfeByChave.data_emissao}</span>
                                    <span>Série: {nfeByChave.serie}</span>
                                    <span>Tipo: {nfeByChave.tipo_nf}</span>
                                  </div>
                                  {nfeByChave.products_count > 0 && (
                                    <p className="text-xs text-slate-500 mt-2">
                                      {nfeByChave.products_count} produto(s)
                                    </p>
                                  )}
                                </div>
                                <div className="flex flex-row md:flex-col items-center md:items-end justify-between md:justify-start gap-2">
                                  <p className="text-xl md:text-2xl font-bold text-[#4A90E2]">
                                    R$ {nfeByChave.valor_total?.toFixed(2)}
                                  </p>
                                  {!nfeByChave.already_imported && (
                                    <Button
                                      onClick={() => carregarNfeSefaz(nfeByChave)}
                                      className="bg-[#4A90E2] hover:bg-[#3A80D2] text-white text-xs md:text-sm"
                                      size="sm"
                                    >
                                      <Download className="w-4 h-4 mr-1" />Importar
                                    </Button>
                                  )}
                                </div>
                              </div>
                            </div>
                          )}

                          {!loadingChave && !nfeByChave && !chaveError && (
                            <div className="text-center py-6 md:py-8 bg-slate-50 rounded-xl">
                              <Key className="w-8 h-8 md:w-10 md:h-10 mx-auto mb-2 text-slate-300" />
                              <p className="text-sm text-slate-500">Digite a chave para buscar</p>
                              <p className="text-xs text-slate-400 mt-1">Localizada no DANFE</p>
                            </div>
                          )}
                        </div>
                      )}

                      {/* No Company Selected Message */}
                      {!selectedCompanyId && companies.length > 0 && (
                        <div className="text-center py-6 text-slate-500">
                          <Building className="w-8 h-8 mx-auto mb-2 text-slate-300" />
                          <p className="text-sm">Selecione uma empresa</p>
                        </div>
                      )}
                    </div>
                  )}

                  {/* File Upload Tab Content */}
                  {nfeSourceTab === "arquivo" && !nfeData && (
                    <div 
                      onClick={() => nfeInputRef.current?.click()}
                      className="border-2 border-dashed border-slate-300 rounded-xl p-8 text-center cursor-pointer hover:border-[#4A90E2]/50 hover:bg-slate-50 transition-colors"
                    >
                      <input 
                        ref={nfeInputRef}
                        type="file" 
                        accept=".xml"
                        multiple
                        onChange={handleNfeFileSelect} 
                        className="hidden" 
                      />
                      {nfeFiles.length > 0 ? (
                        <div className="flex flex-col items-center gap-3">
                          <div className="flex items-center gap-2">
                            <FileText className="w-10 h-10 text-[#4A90E2]" />
                            {nfeFiles.length > 1 && (
                              <span className="bg-[#4A90E2] text-white text-sm font-bold px-2 py-1 rounded-full">
                                {nfeFiles.length}
                              </span>
                            )}
                          </div>
                          <div className="w-full max-w-md">
                            {nfeFiles.length === 1 ? (
                              <p className="font-medium text-slate-700">{nfeFiles[0].name}</p>
                            ) : (
                              <div className="space-y-1">
                                <p className="font-medium text-slate-700">{nfeFiles.length} arquivos selecionados</p>
                                <div className="max-h-32 overflow-y-auto text-left bg-slate-50 rounded-lg p-2">
                                  {nfeFiles.map((file, i) => (
                                    <p key={i} className="text-xs text-slate-600 truncate">
                                      {i + 1}. {file.name}
                                    </p>
                                  ))}
                                </div>
                              </div>
                            )}
                            <p className="text-sm text-slate-500 mt-2">Clique para adicionar mais ou no botão abaixo para importar</p>
                          </div>
                          <Button 
                            onClick={(e) => { e.stopPropagation(); uploadNfe(); }}
                            disabled={uploadingNfe}
                            className="mt-2 bg-[#4A90E2] hover:bg-[#3A80D2] text-white"
                          >
                            {uploadingNfe ? (
                              <>
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                {nfeUploadProgress.total > 1 
                                  ? `Importando ${nfeUploadProgress.current}/${nfeUploadProgress.total}...`
                                  : "Importando..."
                                }
                              </>
                            ) : (
                              <>
                                <Upload className="w-4 h-4 mr-2" />
                                {nfeFiles.length === 1 ? "Importar NF-e" : `Importar ${nfeFiles.length} NF-es`}
                              </>
                            )}
                          </Button>
                        </div>
                      ) : (
                        <div className="flex flex-col items-center gap-2">
                          <Upload className="w-12 h-12 text-slate-400" />
                          <p className="text-lg font-medium text-slate-700">Arraste seus arquivos XML aqui</p>
                          <p className="text-sm text-slate-500">ou clique para selecionar (.xml)</p>
                          <p className="text-xs text-slate-400">Você pode selecionar múltiplos arquivos</p>
                        </div>
                      )}
                    </div>
                  )}

                  {/* NF-e Data Display */}
                  {nfeData && (
                    <div className="space-y-4">
                      {/* NF-e Info */}
                      <div className="p-4 bg-[#4A90E2]/5 rounded-xl border border-[#4A90E2]/20">
                        <div className="flex items-start justify-between">
                          <div className="space-y-1">
                            <p className="font-semibold text-slate-900">NF-e #{nfeData.nfe_number}</p>
                            <p className="text-sm text-slate-600">{nfeData.supplier_name}</p>
                            <p className="text-xs text-slate-500">CNPJ: {nfeData.supplier_cnpj} | Data: {nfeData.issue_date}</p>
                          </div>
                          <div className="text-right">
                            <p className="text-sm text-slate-500">Valor Total</p>
                            <p className="text-xl font-bold text-[#4A90E2]">R$ {nfeData.total_value?.toFixed(2)}</p>
                          </div>
                        </div>
                        <div className="flex items-center justify-between mt-3">
                          <Button 
                            variant="outline" 
                            size="sm" 
                            onClick={resetNfeImport}
                            className="border-slate-300"
                          >
                            <RefreshCw className="w-4 h-4 mr-2" />Importar outra NF-e
                          </Button>
                          <Button 
                            variant="outline" 
                            size="sm" 
                            onClick={() => {
                              setShowDeliveryForm(!showDeliveryForm);
                              if (!showDeliveryForm && nfeData?.id) {
                                loadDeliveryInfo(nfeData.id);
                              }
                            }}
                            className={showDeliveryForm ? "bg-emerald-50 border-emerald-300 text-emerald-700" : "border-slate-300"}
                          >
                            <Truck className="w-4 h-4 mr-2" />
                            {showDeliveryForm ? "Fechar Recebimento" : "Info Recebimento"}
                          </Button>
                        </div>
                      </div>

                      {/* Delivery/Receiving Information Form */}
                      {showDeliveryForm && (
                        <div className="p-4 bg-emerald-50 rounded-xl border border-emerald-200 space-y-4">
                          <div className="flex items-center gap-2 mb-2">
                            <Truck className="w-5 h-5 text-emerald-600" />
                            <span className="font-medium text-emerald-800">Informações de Recebimento</span>
                          </div>
                          
                          {/* Vehicle Plate and Driver */}
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <Label className="text-xs text-emerald-700 font-medium">Placa do Veículo</Label>
                              <Input
                                value={deliveryInfo.vehicle_plate}
                                onChange={(e) => setDeliveryInfo(prev => ({ ...prev, vehicle_plate: e.target.value.toUpperCase() }))}
                                placeholder="ABC-1234"
                                className="mt-1 bg-white border-emerald-200"
                                maxLength={8}
                              />
                            </div>
                            <div>
                              <Label className="text-xs text-emerald-700 font-medium">Nome do Entregador</Label>
                              <Input
                                value={deliveryInfo.driver_name}
                                onChange={(e) => setDeliveryInfo(prev => ({ ...prev, driver_name: e.target.value }))}
                                placeholder="Nome do motorista"
                                className="mt-1 bg-white border-emerald-200"
                              />
                            </div>
                          </div>
                          
                          {/* Arrival and Release Times */}
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <Label className="text-xs text-emerald-700 font-medium flex items-center gap-1">
                                <Clock className="w-3 h-3" /> Horário de Chegada
                              </Label>
                              <Input
                                type="datetime-local"
                                value={deliveryInfo.arrival_time}
                                onChange={(e) => setDeliveryInfo(prev => ({ ...prev, arrival_time: e.target.value }))}
                                className="mt-1 bg-white border-emerald-200"
                              />
                            </div>
                            <div>
                              <Label className="text-xs text-emerald-700 font-medium flex items-center gap-1">
                                <Clock className="w-3 h-3" /> Horário de Liberação
                              </Label>
                              <Input
                                type="datetime-local"
                                value={deliveryInfo.release_time}
                                onChange={(e) => setDeliveryInfo(prev => ({ ...prev, release_time: e.target.value }))}
                                className="mt-1 bg-white border-emerald-200"
                              />
                            </div>
                          </div>
                          
                          {/* Photos */}
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {/* Vehicle Photo */}
                            <div>
                              <Label className="text-xs text-emerald-700 font-medium flex items-center gap-1">
                                <Camera className="w-3 h-3" /> Foto do Veículo
                              </Label>
                              <div className="mt-1 space-y-2">
                                <input
                                  type="file"
                                  accept="image/*"
                                  capture="environment"
                                  ref={vehiclePhotoRef}
                                  onChange={(e) => handlePhotoCapture('vehicle_photo', e.target.files[0])}
                                  className="hidden"
                                />
                                <div className="flex gap-2">
                                  <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    onClick={() => vehiclePhotoRef.current?.click()}
                                    className="flex-1 border-emerald-300 text-emerald-700 hover:bg-emerald-100"
                                  >
                                    <Camera className="w-4 h-4 mr-1" />
                                    {deliveryInfo.vehicle_photo ? "Trocar" : "Tirar/Anexar"}
                                  </Button>
                                  {deliveryInfo.vehicle_photo && (
                                    <Button
                                      type="button"
                                      variant="outline"
                                      size="sm"
                                      onClick={() => setDeliveryInfo(prev => ({ ...prev, vehicle_photo: null }))}
                                      className="border-red-300 text-red-600 hover:bg-red-50"
                                    >
                                      <X className="w-4 h-4" />
                                    </Button>
                                  )}
                                </div>
                                {deliveryInfo.vehicle_photo && (
                                  <img 
                                    src={deliveryInfo.vehicle_photo} 
                                    alt="Veículo" 
                                    className="w-full h-32 object-cover rounded-lg border border-emerald-200"
                                  />
                                )}
                              </div>
                            </div>
                            
                            {/* Products Photo */}
                            <div>
                              <Label className="text-xs text-emerald-700 font-medium flex items-center gap-1">
                                <Camera className="w-3 h-3" /> Foto dos Produtos
                              </Label>
                              <div className="mt-1 space-y-2">
                                <input
                                  type="file"
                                  accept="image/*"
                                  capture="environment"
                                  ref={productsPhotoRef}
                                  onChange={(e) => handlePhotoCapture('products_photo', e.target.files[0])}
                                  className="hidden"
                                />
                                <div className="flex gap-2">
                                  <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    onClick={() => productsPhotoRef.current?.click()}
                                    className="flex-1 border-emerald-300 text-emerald-700 hover:bg-emerald-100"
                                  >
                                    <Camera className="w-4 h-4 mr-1" />
                                    {deliveryInfo.products_photo ? "Trocar" : "Tirar/Anexar"}
                                  </Button>
                                  {deliveryInfo.products_photo && (
                                    <Button
                                      type="button"
                                      variant="outline"
                                      size="sm"
                                      onClick={() => setDeliveryInfo(prev => ({ ...prev, products_photo: null }))}
                                      className="border-red-300 text-red-600 hover:bg-red-50"
                                    >
                                      <X className="w-4 h-4" />
                                    </Button>
                                  )}
                                </div>
                                {deliveryInfo.products_photo && (
                                  <img 
                                    src={deliveryInfo.products_photo} 
                                    alt="Produtos" 
                                    className="w-full h-32 object-cover rounded-lg border border-emerald-200"
                                  />
                                )}
                              </div>
                            </div>
                          </div>
                          
                          {/* Notes */}
                          <div>
                            <Label className="text-xs text-emerald-700 font-medium">Observações</Label>
                            <Textarea
                              value={deliveryInfo.notes}
                              onChange={(e) => setDeliveryInfo(prev => ({ ...prev, notes: e.target.value }))}
                              placeholder="Observações sobre o recebimento..."
                              className="mt-1 bg-white border-emerald-200 h-20"
                            />
                          </div>
                          
                          {/* Save Button */}
                          <Button
                            onClick={saveDeliveryInfo}
                            disabled={savingDeliveryInfo}
                            className="w-full bg-emerald-600 hover:bg-emerald-700 text-white"
                          >
                            {savingDeliveryInfo ? (
                              <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Salvando...</>
                            ) : (
                              <><Check className="w-4 h-4 mr-2" />Salvar Informações de Recebimento</>
                            )}
                          </Button>
                        </div>
                      )}

                      {/* Store Selection for Stock */}
                      <div className="p-4 bg-amber-50 rounded-xl border border-amber-200">
                        <div className="flex items-center gap-2 mb-2">
                          <Store className="w-4 h-4 text-amber-600" />
                          <span className="text-sm font-medium text-amber-700">Loja de Destino do Estoque</span>
                        </div>
                        <select
                          value={nfeImportStoreId}
                          onChange={(e) => setNfeImportStoreId(e.target.value)}
                          className="w-full h-10 px-3 border border-amber-300 rounded-md text-sm bg-white"
                        >
                          {STORE_LOCATIONS.map(store => (
                            <option key={store.id} value={store.id}>{store.name}</option>
                          ))}
                        </select>
                        <p className="text-xs text-amber-600 mt-1">Os produtos serão cadastrados no estoque desta loja</p>
                      </div>

                      {/* Stock Type Selection */}
                      <div className="p-4 bg-blue-50 rounded-xl border border-blue-200">
                        <div className="flex items-center gap-2 mb-2">
                          <Package className="w-4 h-4 text-blue-600" />
                          <span className="text-sm font-medium text-blue-700">Tipo de Estoque</span>
                        </div>
                        <div className="flex gap-3">
                          <label className={`flex-1 flex items-center gap-2 p-3 rounded-lg border-2 cursor-pointer transition-all ${
                            nfeImportStockType === "regular" 
                              ? 'border-blue-500 bg-blue-100' 
                              : 'border-slate-200 bg-white hover:border-slate-300'
                          }`}>
                            <input
                              type="radio"
                              name="stockType"
                              value="regular"
                              checked={nfeImportStockType === "regular"}
                              onChange={(e) => setNfeImportStockType(e.target.value)}
                              className="hidden"
                            />
                            <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                              nfeImportStockType === "regular" ? 'border-blue-500' : 'border-slate-300'
                            }`}>
                              {nfeImportStockType === "regular" && <div className="w-2 h-2 rounded-full bg-blue-500" />}
                            </div>
                            <div>
                              <p className="text-sm font-medium text-slate-700">Regular</p>
                              <p className="text-xs text-slate-500">Estoque normal</p>
                            </div>
                          </label>
                          <label className={`flex-1 flex items-center gap-2 p-3 rounded-lg border-2 cursor-pointer transition-all ${
                            nfeImportStockType === "fifo" 
                              ? 'border-emerald-500 bg-emerald-100' 
                              : 'border-slate-200 bg-white hover:border-slate-300'
                          }`}>
                            <input
                              type="radio"
                              name="stockType"
                              value="fifo"
                              checked={nfeImportStockType === "fifo"}
                              onChange={(e) => setNfeImportStockType(e.target.value)}
                              className="hidden"
                            />
                            <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                              nfeImportStockType === "fifo" ? 'border-emerald-500' : 'border-slate-300'
                            }`}>
                              {nfeImportStockType === "fifo" && <div className="w-2 h-2 rounded-full bg-emerald-500" />}
                            </div>
                            <div>
                              <p className="text-sm font-medium text-slate-700">FIFO</p>
                              <p className="text-xs text-slate-500">First In, First Out</p>
                            </div>
                          </label>
                        </div>
                        <p className="text-xs text-blue-600 mt-2">
                          {nfeImportStockType === "fifo" 
                            ? "Produtos mais antigos serão vendidos primeiro" 
                            : "Estoque sem controle de ordem de saída"}
                        </p>
                      </div>

                      {/* Products Selection */}
                      <div className="space-y-3">
                        {/* Tabs for filtering products */}
                        <div className="flex items-center justify-between">
                          <div className="flex gap-2">
                            <button
                              onClick={() => setNfeImportTab("all")}
                              className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                                nfeImportTab === "all" 
                                  ? 'bg-slate-800 text-white' 
                                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                              }`}
                            >
                              Todos ({nfeProducts.length})
                            </button>
                            <button
                              onClick={() => setNfeImportTab("pending")}
                              className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                                nfeImportTab === "pending" 
                                  ? 'bg-amber-600 text-white' 
                                  : 'bg-amber-100 text-amber-700 hover:bg-amber-200'
                              }`}
                            >
                              Pendentes ({nfeProducts.filter(p => p.ean && !isProductImported(p)).length})
                            </button>
                            <button
                              onClick={() => setNfeImportTab("imported")}
                              className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                                nfeImportTab === "imported" 
                                  ? 'bg-emerald-600 text-white' 
                                  : 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200'
                              }`}
                            >
                              Já Importados ({nfeProducts.filter(p => p.ean && isProductImported(p)).length})
                            </button>
                          </div>
                          <Button variant="ghost" size="sm" onClick={selectAllNfeProducts}>
                            {selectedNfeProducts.length === nfeProducts.length ? "Desmarcar todos" : "Selecionar todos"}
                          </Button>
                        </div>

                        <div className="max-h-[400px] overflow-y-auto space-y-2">
                          {nfeProducts
                            .map((product, index) => ({ product, index }))
                            .filter(({ product }) => {
                              if (nfeImportTab === "pending") return !isProductImported(product);
                              if (nfeImportTab === "imported") return isProductImported(product);
                              return true; // "all"
                            })
                            .map(({ product, index }) => (
                            <div 
                              key={`product-${index}`}
                              className={`p-3 rounded-xl border-2 transition-colors ${
                                isProductImported(product)
                                  ? 'border-emerald-300 bg-emerald-50'
                                  : selectedNfeProducts.includes(index)
                                    ? 'border-[#4A90E2] bg-[#4A90E2]/5'
                                    : product.missing_ean
                                      ? 'border-amber-300 bg-amber-50'
                                      : 'border-slate-200 bg-white'
                              }`}
                            >
                              <div className="flex items-start gap-3">
                                {/* Checkbox */}
                                <div 
                                  onClick={() => !product.missing_ean && toggleNfeProductSelection(index)}
                                  className={`w-5 h-5 rounded border-2 flex items-center justify-center mt-1 ${
                                    product.missing_ean 
                                      ? 'border-slate-200 bg-slate-100 cursor-not-allowed'
                                      : selectedNfeProducts.includes(index)
                                        ? 'bg-[#4A90E2] border-[#4A90E2] cursor-pointer'
                                        : 'border-slate-300 cursor-pointer'
                                  }`}
                                  title={product.missing_ean ? "Preencha o EAN primeiro" : ""}
                                >
                                  {selectedNfeProducts.includes(index) && !product.missing_ean && (
                                    <Check className="w-3 h-3 text-white" />
                                  )}
                                </div>

                                {/* Product Info */}
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2">
                                    <p className="font-medium text-slate-900 text-sm truncate">{product.product_name}</p>
                                    {isProductImported(product) && (
                                      <Badge className="bg-emerald-100 text-emerald-700 text-[10px] px-1.5 py-0">
                                        <Check className="w-2.5 h-2.5 mr-0.5" />Já importado
                                      </Badge>
                                    )}
                                  </div>
                                  
                                  {/* EAN Input - Always present to prevent focus loss */}
                                  {product.missing_ean_original ? (
                                    <div className="mt-2 space-y-2">
                                      <div className="p-2 bg-amber-100 rounded-lg border border-amber-300">
                                        <div className="flex items-center gap-2 mb-1">
                                          <AlertCircle className="w-4 h-4 text-amber-600" />
                                          <span className="text-xs font-medium text-amber-700">Produto sem GTIN - Insira o EAN:</span>
                                        </div>
                                        <Input 
                                          type="text"
                                          value={product.ean || ""}
                                          onChange={(e) => updateNfeProductEan(index, e.target.value)}
                                          placeholder="Digite o código EAN (13 dígitos)"
                                          className="h-8 text-sm bg-white"
                                          maxLength={14}
                                        />
                                        {product.ean && product.ean.length >= 8 && (
                                          <p className="text-xs text-emerald-600 mt-1 flex items-center gap-1">
                                            <Check className="w-3 h-3" /> EAN válido - Agora você pode selecionar este produto
                                          </p>
                                        )}
                                      </div>
                                      
                                      {/* Show batch fields when EAN is valid */}
                                      {product.ean && product.ean.length >= 8 && (
                                        <>
                                          <div className="flex items-center gap-3 text-xs text-slate-500 px-1">
                                            <span>Qtd: {product.quantity} {product.unit}</span>
                                            <span className="text-emerald-600 font-medium">R$ {product.unit_cost?.toFixed(2)}/{product.unit}</span>
                                          </div>
                                          
                                          {/* Batch (Lote) Fields for Stock - Multiple Batches */}
                                          <div className="p-2 bg-amber-50 rounded-lg border border-amber-200">
                                            <div className="flex items-center justify-between mb-2">
                                              <div className="flex items-center gap-2">
                                                <Package className="w-4 h-4 text-amber-600" />
                                                <span className="text-xs font-medium text-amber-700">Lotes para Estoque</span>
                                              </div>
                                              <Button
                                                type="button"
                                                variant="ghost"
                                                size="sm"
                                                onClick={() => addNfeProductBatch(index)}
                                                className="h-6 px-2 text-xs text-amber-700 hover:bg-amber-100"
                                              >
                                                <Plus className="w-3 h-3 mr-1" />Adicionar Lote
                                              </Button>
                                            </div>
                                            
                                            {/* List of batches */}
                                            <div className="space-y-2">
                                              {(product.batches && product.batches.length > 0 ? product.batches : [{ batch_number: '0001', quantity: product.quantity, unit: product.unit || 'UN', expiry_date: '' }]).map((batch, batchIndex) => (
                                                <div key={`${index}-missing-${batchIndex}`} className="grid grid-cols-12 gap-2 items-end bg-white p-2 rounded border border-amber-100">
                                                  <div className="col-span-3">
                                                    <label className="text-xs text-slate-500">Nº do Lote</label>
                                                    <input 
                                                      type="text"
                                                      value={batch.batch_number || String(batchIndex + 1).padStart(4, '0')}
                                                      onChange={(e) => updateNfeProductBatchItem(index, batchIndex, 'batch_number', e.target.value)}
                                                      placeholder="0001"
                                                      className="h-7 w-full text-sm border border-slate-200 rounded-md px-2 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                    />
                                                  </div>
                                                  <div className="col-span-2">
                                                    <label className="text-xs text-slate-500">Quantidade</label>
                                                    <input 
                                                      type="number"
                                                      value={batch.quantity || ""}
                                                      onChange={(e) => updateNfeProductBatchItem(index, batchIndex, 'quantity', parseFloat(e.target.value) || 0)}
                                                      placeholder="Qtd"
                                                      className="h-7 w-full text-sm border border-slate-200 rounded-md px-2 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                    />
                                                  </div>
                                                  <div className="col-span-2">
                                                    <label className="text-xs text-slate-500">Unidade</label>
                                                    <select 
                                                      value={batch.unit || product.unit || "UN"}
                                                      onChange={(e) => updateNfeProductBatchItem(index, batchIndex, 'unit', e.target.value)}
                                                      className="h-7 w-full text-sm border border-slate-200 rounded-md px-2 bg-white"
                                                    >
                                                      {UNIT_OPTIONS.map(u => (
                                                        <option key={u.id} value={u.id}>{u.id}</option>
                                                      ))}
                                                    </select>
                                                  </div>
                                                  <div className="col-span-3">
                                                    <label className="text-xs text-slate-500">Validade</label>
                                                    <input 
                                                      type="date"
                                                      value={batch.expiry_date || ""}
                                                      onChange={(e) => updateNfeProductBatchItem(index, batchIndex, 'expiry_date', e.target.value)}
                                                      className="h-7 w-full text-sm border border-slate-200 rounded-md px-2 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                    />
                                                  </div>
                                                  <div className="col-span-2 flex gap-1">
                                                    <Button
                                                      type="button"
                                                      variant="outline"
                                                      size="sm"
                                                      onClick={(e) => {
                                                        e.stopPropagation();
                                                        const qty = batch.quantity || product.quantity;
                                                        const unit = batch.unit || product.unit || 'UN';
                                                        const expiryDate = batch.expiry_date 
                                                          ? new Date(batch.expiry_date + 'T12:00:00').toLocaleDateString('pt-BR')
                                                          : '';
                                                        const text = [
                                                          product.product_name,
                                                          product.ean,
                                                          `Qtde: ${qty} ${unit}`,
                                                          expiryDate ? `Validade: *${expiryDate}*` : ''
                                                        ].filter(Boolean).join('\n');
                                                        navigator.clipboard.writeText(text).then(() => {
                                                          toast.success('Dados copiados para WhatsApp!');
                                                        }).catch((err) => {
                                                          prompt('Copie o texto abaixo:', text);
                                                        });
                                                      }}
                                                      disabled={!batch.quantity || !batch.expiry_date}
                                                      className={`h-7 w-7 p-0 ${batch.quantity && batch.expiry_date ? 'text-green-600 border-green-300 hover:bg-green-50' : 'text-slate-300'}`}
                                                      title={batch.quantity && batch.expiry_date ? "Copiar para WhatsApp" : "Preencha quantidade e validade"}
                                                    >
                                                      <Copy className="w-3 h-3" />
                                                    </Button>
                                                    {(product.batches?.length > 1 || batchIndex > 0) && (
                                                      <Button
                                                        type="button"
                                                        variant="ghost"
                                                        size="sm"
                                                        onClick={() => removeNfeProductBatch(index, batchIndex)}
                                                        className="h-7 w-7 p-0 text-red-500 hover:bg-red-50"
                                                      >
                                                        <Trash2 className="w-3 h-3" />
                                                      </Button>
                                                    )}
                                                  </div>
                                                </div>
                                              ))}
                                            </div>
                                            
                                            {/* Total quantity check */}
                                            {product.batches && product.batches.length > 0 && (
                                              <div className="mt-2 text-xs">
                                                <span className="text-slate-500">Total dos lotes: </span>
                                                <span className={`font-medium ${
                                                  product.batches.reduce((sum, b) => sum + (b.quantity || 0), 0) === product.quantity 
                                                    ? 'text-emerald-600' 
                                                    : 'text-red-600'
                                                }`}>
                                                  {product.batches.reduce((sum, b) => sum + (b.quantity || 0), 0)} / {product.quantity} {product.unit}
                                                </span>
                                              </div>
                                            )}
                                          </div>
                                        </>
                                      )}
                                    </div>
                                  ) : (
                                    <div className="space-y-2 mt-1">
                                      <div className="flex items-center gap-3 text-xs text-slate-500">
                                        <div className="flex items-center gap-1">
                                          <span className="text-slate-400">EAN:</span>
                                          <Input 
                                            type="text"
                                            value={product.ean || ""}
                                            onChange={(e) => updateNfeProductEan(index, e.target.value)}
                                            className="h-6 w-32 md:w-40 text-xs font-mono px-2"
                                            maxLength={14}
                                            title="Clique para editar o EAN (ex: trocar DUN por EAN)"
                                          />
                                          {product.original_ean && product.ean !== product.original_ean && (
                                            <span className="text-amber-600 text-[10px]" title={`Original: ${product.original_ean}`}>
                                              (editado)
                                            </span>
                                          )}
                                        </div>
                                        <span>Qtd: {product.quantity} {product.unit}</span>
                                        <span className="text-emerald-600 font-medium">R$ {product.unit_cost?.toFixed(2)}/{product.unit}</span>
                                      </div>
                                      
                                      {/* Batch (Lote) Fields for Stock - Multiple Batches */}
                                      <div className="p-2 bg-amber-50 rounded-lg border border-amber-200">
                                        <div className="flex items-center justify-between mb-2">
                                          <div className="flex items-center gap-2">
                                            <Package className="w-4 h-4 text-amber-600" />
                                            <span className="text-xs font-medium text-amber-700">Lotes para Estoque</span>
                                          </div>
                                          <Button
                                            type="button"
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => addNfeProductBatch(index)}
                                            className="h-6 px-2 text-xs text-amber-700 hover:bg-amber-100"
                                          >
                                            <Plus className="w-3 h-3 mr-1" />Adicionar Lote
                                          </Button>
                                        </div>
                                        
                                        {/* List of batches - Direct inputs with immediate state updates */}
                                        <div className="space-y-2">
                                          {(product.batches && product.batches.length > 0 ? product.batches : [{ batch_number: '0001', quantity: product.quantity, unit: product.unit || 'UN', expiry_date: '' }]).map((batch, batchIndex) => (
                                            <div key={`${index}-${batchIndex}`} className="grid grid-cols-12 gap-2 items-end bg-white p-2 rounded border border-amber-100">
                                              <div className="col-span-3">
                                                <label className="text-xs text-slate-500">Nº do Lote</label>
                                                <input 
                                                  type="text"
                                                  value={batch.batch_number || String(batchIndex + 1).padStart(4, '0')}
                                                  onChange={(e) => updateNfeProductBatchItem(index, batchIndex, 'batch_number', e.target.value)}
                                                  placeholder="0001"
                                                  className="h-7 w-full text-sm border border-slate-200 rounded-md px-2 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                />
                                              </div>
                                              <div className="col-span-2">
                                                <label className="text-xs text-slate-500">Quantidade</label>
                                                <input 
                                                  type="number"
                                                  value={batch.quantity || ""}
                                                  onChange={(e) => updateNfeProductBatchItem(index, batchIndex, 'quantity', parseFloat(e.target.value) || 0)}
                                                  placeholder="Qtd"
                                                  className="h-7 w-full text-sm border border-slate-200 rounded-md px-2 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                />
                                              </div>
                                              <div className="col-span-2">
                                                <label className="text-xs text-slate-500">Unidade</label>
                                                <select 
                                                  value={batch.unit || product.unit || "UN"}
                                                  onChange={(e) => updateNfeProductBatchItem(index, batchIndex, 'unit', e.target.value)}
                                                  className="h-7 w-full text-sm border border-slate-200 rounded-md px-2 bg-white"
                                                >
                                                  {UNIT_OPTIONS.map(u => (
                                                    <option key={u.id} value={u.id}>{u.id}</option>
                                                  ))}
                                                </select>
                                              </div>
                                              <div className="col-span-3">
                                                <label className="text-xs text-slate-500">Validade</label>
                                                <input 
                                                  type="date"
                                                  value={batch.expiry_date || ""}
                                                  onChange={(e) => updateNfeProductBatchItem(index, batchIndex, 'expiry_date', e.target.value)}
                                                  className="h-7 w-full text-sm border border-slate-200 rounded-md px-2 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                />
                                              </div>
                                              <div className="col-span-2 flex gap-1">
                                                {/* Copy to WhatsApp Button */}
                                                <Button
                                                  type="button"
                                                  variant="outline"
                                                  size="sm"
                                                  onClick={(e) => {
                                                    e.stopPropagation();
                                                    const qty = batch.quantity || product.quantity;
                                                    const unit = batch.unit || product.unit || 'UN';
                                                    const expiryDate = batch.expiry_date 
                                                      ? new Date(batch.expiry_date + 'T12:00:00').toLocaleDateString('pt-BR')
                                                      : '';
                                                    
                                                    let conversionText = '';
                                                    if (product.origin_qty && product.dest_qty && product.origin_unit && product.dest_unit && product.origin_unit !== product.dest_unit) {
                                                      conversionText = `Conversão: ${product.origin_qty} ${product.origin_unit} para ${product.dest_qty} ${product.dest_unit}`;
                                                    }
                                                    
                                                    const text = [
                                                      product.product_name,
                                                      product.ean,
                                                      `Qtde: ${qty} ${unit}`,
                                                      conversionText,
                                                      expiryDate ? `Validade: *${expiryDate}*` : ''
                                                    ].filter(Boolean).join('\n');
                                                    
                                                    navigator.clipboard.writeText(text).then(() => {
                                                      toast.success('Dados copiados para WhatsApp!');
                                                    }).catch((err) => {
                                                      console.log('Clipboard error:', err);
                                                      prompt('Copie o texto abaixo:', text);
                                                    });
                                                  }}
                                                  disabled={!batch.quantity || !batch.expiry_date}
                                                  className={`h-7 w-7 p-0 ${batch.quantity && batch.expiry_date ? 'text-green-600 border-green-300 hover:bg-green-50' : 'text-slate-300'}`}
                                                  title={batch.quantity && batch.expiry_date ? "Copiar para WhatsApp" : "Preencha quantidade e validade"}
                                                >
                                                  <Copy className="w-3 h-3" />
                                                </Button>
                                                {(product.batches?.length > 1 || batchIndex > 0) && (
                                                  <Button
                                                    type="button"
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() => removeNfeProductBatch(index, batchIndex)}
                                                    className="h-7 w-7 p-0 text-red-500 hover:bg-red-50"
                                                  >
                                                    <Trash2 className="w-3 h-3" />
                                                  </Button>
                                                )}
                                              </div>
                                            </div>
                                          ))}
                                        </div>
                                        
                                        {/* Total quantity check */}
                                        {product.batches && product.batches.length > 0 && (
                                          <div className="mt-2 text-xs">
                                            <span className="text-slate-500">Total dos lotes: </span>
                                            <span className={`font-medium ${
                                              product.batches.reduce((sum, b) => sum + (b.quantity || 0), 0) === product.quantity 
                                                ? 'text-emerald-600' 
                                                : 'text-red-600'
                                            }`}>
                                              {product.batches.reduce((sum, b) => sum + (b.quantity || 0), 0)} / {product.quantity} {product.unit}
                                            </span>
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  )}
                                  
                                  {/* Conversion Fields - Always show with toggle */}
                                  {!product.missing_ean && (
                                    <div className="mt-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
                                      <div className="flex items-center justify-between mb-2">
                                        <div className="flex items-center gap-2">
                                          <RefreshCw className="w-4 h-4 text-blue-600" />
                                          <span className="text-xs font-medium text-blue-700">
                                            Conversão de Unidade {product.needs_conversion && `(${product.unit} → UN)`}
                                          </span>
                                        </div>
                                        <label className="flex items-center gap-2 cursor-pointer">
                                          <span className="text-xs text-slate-500">Habilitar</span>
                                          <input
                                            type="checkbox"
                                            checked={product.conversion_enabled || product.needs_conversion}
                                            onChange={(e) => updateNfeProductConversion(index, 'conversion_enabled', e.target.checked)}
                                            className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                                          />
                                        </label>
                                      </div>
                                      
                                      {(product.conversion_enabled || product.needs_conversion) && (
                                        <>
                                          <div className="grid grid-cols-5 gap-2 items-center">
                                            <div>
                                              <label className="text-xs text-slate-500">Qtd Origem</label>
                                              <Input 
                                                type="number"
                                                value={product.origin_qty || ""}
                                                onChange={(e) => updateNfeProductConversion(index, 'origin_qty', e.target.value)}
                                                className="h-8 text-sm"
                                                placeholder="1"
                                              />
                                            </div>
                                            <div>
                                              <label className="text-xs text-slate-500">Un. Origem</label>
                                              <select 
                                                value={product.origin_unit || product.unit}
                                                onChange={(e) => updateNfeProductConversion(index, 'origin_unit', e.target.value)}
                                                className="h-8 w-full text-sm border border-slate-200 rounded-md px-2"
                                              >
                                                {UNIT_OPTIONS.map(u => (
                                                  <option key={u.id} value={u.id}>{u.id}</option>
                                                ))}
                                              </select>
                                            </div>
                                            <div className="flex items-center justify-center pt-4">
                                              <ArrowRight className="w-4 h-4 text-slate-400" />
                                            </div>
                                            <div>
                                              <label className="text-xs text-slate-500">Qtd Destino</label>
                                              <Input 
                                                type="number"
                                                value={product.dest_qty || ""}
                                                onChange={(e) => updateNfeProductConversion(index, 'dest_qty', e.target.value)}
                                                className="h-8 text-sm"
                                                placeholder="12"
                                              />
                                            </div>
                                            <div>
                                              <label className="text-xs text-slate-500">Un. Destino</label>
                                              <select 
                                                value={product.dest_unit || "UN"}
                                                onChange={(e) => updateNfeProductConversion(index, 'dest_unit', e.target.value)}
                                                className="h-8 w-full text-sm border border-slate-200 rounded-md px-2"
                                              >
                                                {UNIT_OPTIONS.map(u => (
                                                  <option key={u.id} value={u.id}>{u.id}</option>
                                                ))}
                                              </select>
                                            </div>
                                          </div>
                                          {product.converted_unit_cost && (
                                            <p className="mt-2 text-xs text-emerald-700 font-medium">
                                              Custo convertido: R$ {product.converted_unit_cost.toFixed(2)}/UN
                                            </p>
                                          )}
                                        </>
                                      )}
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>

                        {/* Action Buttons */}
                        <div className="flex gap-3">
                          <Button 
                            onClick={searchNfeProducts}
                            disabled={loading || selectedNfeProducts.length === 0}
                            className="flex-1 h-12 bg-[#4A90E2] hover:bg-[#3A80D2] text-white font-medium"
                          >
                            {loading ? (
                              <><Loader2 className="w-5 h-5 mr-2 animate-spin" />Buscando...</>
                            ) : (
                              <><Search className="w-5 h-5 mr-2" />Buscar na Bluesoft</>
                            )}
                          </Button>
                        </div>
                        {loading && processingProgress > 0 && (
                          <Progress value={processingProgress} className="h-2" />
                        )}
                      </div>

                      {/* Products Pending Confirmation */}
                      {nfeSearchResults.length > 0 && (
                        <div className="mt-6 space-y-4">
                          <div className="flex items-center justify-between">
                            <h4 className="font-semibold text-slate-800 flex items-center gap-2">
                              <Package className="w-5 h-5 text-amber-600" />
                              Produtos Encontrados - Confirme para Salvar no Estoque
                            </h4>
                            <span className="text-sm text-slate-500">
                              {nfeSearchResults.filter(p => p.confirmed).length} de {nfeSearchResults.length} confirmados
                            </span>
                          </div>
                          
                          <div className="space-y-3">
                            {nfeSearchResults.map((product, index) => (
                              <div 
                                key={index}
                                className={`p-4 rounded-xl border ${
                                  product.confirmed 
                                    ? 'bg-emerald-50 border-emerald-200' 
                                    : product.status === 'not_found'
                                    ? 'bg-red-50 border-red-200'
                                    : 'bg-white border-slate-200'
                                }`}
                              >
                                <div className="flex items-start gap-4">
                                  {/* Product Image */}
                                  <div className="w-16 h-16 rounded-lg border border-slate-200 bg-white flex items-center justify-center overflow-hidden">
                                    {product.image_url || product.image_base64 ? (
                                      <img src={product.image_base64 || product.image_url} alt="" className="w-full h-full object-contain" />
                                    ) : (
                                      <Package className="w-8 h-8 text-slate-300" />
                                    )}
                                  </div>
                                  
                                  {/* Product Info */}
                                  <div className="flex-1">
                                    <div className="flex items-start justify-between">
                                      <div className="flex-1">
                                        {editingNfeProductIndex === index ? (
                                          /* Edit Mode */
                                          <div className="flex items-center gap-2 mb-1">
                                            <Input
                                              value={editedNfeProductName}
                                              onChange={(e) => setEditedNfeProductName(e.target.value)}
                                              className="h-8 flex-1"
                                              autoFocus
                                              onKeyDown={(e) => {
                                                if (e.key === 'Enter') saveEditedNfeProductName(index);
                                                if (e.key === 'Escape') cancelEditingNfeProductName();
                                              }}
                                            />
                                            <Button 
                                              variant="ghost" 
                                              size="sm" 
                                              className="h-8 w-8 p-0"
                                              onClick={() => saveEditedNfeProductName(index)}
                                            >
                                              <Check className="w-4 h-4 text-emerald-600" />
                                            </Button>
                                            <Button 
                                              variant="ghost" 
                                              size="sm" 
                                              className="h-8 w-8 p-0"
                                              onClick={cancelEditingNfeProductName}
                                            >
                                              <X className="w-4 h-4 text-red-500" />
                                            </Button>
                                          </div>
                                        ) : (
                                          /* Display Mode */
                                          <div className="flex items-center gap-2 mb-1">
                                            <h5 className="font-medium text-slate-800">{product.product_name}</h5>
                                            <Button 
                                              variant="ghost" 
                                              size="sm" 
                                              className="h-6 w-6 p-0 opacity-60 hover:opacity-100"
                                              onClick={() => startEditingNfeProductName(index)}
                                              title="Editar nome do produto"
                                            >
                                              <Edit className="w-3.5 h-3.5 text-slate-500" />
                                            </Button>
                                            {product.original_product_name && product.original_product_name !== product.product_name && (
                                              <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded-full" title={`Nome original: ${product.original_product_name}`}>
                                                Editado
                                              </span>
                                            )}
                                          </div>
                                        )}
                                        <p className="text-sm text-slate-500">EAN: {product.ean}</p>
                                        {product.original_product && (
                                          <div className="mt-1 text-xs text-slate-500">
                                            <span>Qtd: {product.original_product.quantity} {product.original_product.unit}</span>
                                            <span className="mx-2">•</span>
                                            <span>Custo: R$ {product.nfe_unit_cost?.toFixed(2)}</span>
                                            {product.original_product.batches?.length > 0 && (
                                              <>
                                                <span className="mx-2">•</span>
                                                <span>{product.original_product.batches.length} lote(s)</span>
                                              </>
                                            )}
                                          </div>
                                        )}
                                      </div>
                                      
                                      {/* Status/Actions */}
                                      <div className="flex items-center gap-2">
                                        {product.confirmed ? (
                                          <Badge className="bg-emerald-100 text-emerald-700">
                                            <Check className="w-3 h-3 mr-1" />Salvo
                                          </Badge>
                                        ) : (
                                          <>
                                            {product.status === 'not_found' && (
                                              <Badge variant="destructive" className="mr-2">Não encontrado</Badge>
                                            )}
                                            <Button
                                              size="sm"
                                              variant="outline"
                                              onClick={() => skipNfeProduct(index)}
                                              className="border-slate-300"
                                            >
                                              Pular
                                            </Button>
                                            <Button
                                              size="sm"
                                              onClick={() => confirmNfeProduct(index)}
                                              disabled={savingProduct === product.ean}
                                              className="bg-emerald-600 hover:bg-emerald-700 text-white"
                                            >
                                              {savingProduct === product.ean ? (
                                                <Loader2 className="w-4 h-4 animate-spin" />
                                              ) : (
                                                <>
                                                  <Package className="w-4 h-4 mr-1" />Carregar
                                                </>
                                              )}
                                            </Button>
                                          </>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                          
                          {/* Clear results button */}
                          {nfeSearchResults.every(p => p.confirmed || p.status === 'not_found') && (
                            <Button 
                              variant="outline" 
                              onClick={() => setNfeSearchResults([])}
                              className="w-full border-slate-300"
                            >
                              Limpar Resultados
                            </Button>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
          </CardContent>
        </Card>
              </>
            )}

            {/* ==================== DASHBOARDS SECTION ==================== */}
            {activeSection === "dashboards" && (
              <div className="space-y-6">
                {/* Dashboard Authentication */}
                {!dashboardAuthenticated ? (
                  <div className="min-h-[60vh] flex items-center justify-center">
                    <Card className="w-full max-w-md shadow-lg">
                      <CardHeader className="text-center">
                        <div className="mx-auto w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mb-4">
                          <Lock className="w-8 h-8 text-emerald-600" />
                        </div>
                        <CardTitle className="text-2xl">Dashboards de Vendas</CardTitle>
                        <p className="text-slate-500 mt-2">Digite a senha para acessar</p>
                      </CardHeader>
                      <CardContent>
                        <form onSubmit={(e) => {
                          e.preventDefault();
                          if (dashboardPassword === PROTECTED_PASSWORD) {
                            setDashboardAuthenticated(true);
                            setDashboardAuthError("");
                            loadSalesDashboard();
                          } else {
                            setDashboardAuthError("Senha incorreta");
                          }
                        }} className="space-y-4">
                          <div>
                            <Label htmlFor="dashboard-password">Senha</Label>
                            <Input
                              id="dashboard-password"
                              type="password"
                              value={dashboardPassword}
                              onChange={(e) => setDashboardPassword(e.target.value)}
                              placeholder="Digite a senha"
                              className={dashboardAuthError ? "border-red-500" : ""}
                            />
                            {dashboardAuthError && (
                              <p className="text-red-500 text-sm mt-1">{dashboardAuthError}</p>
                            )}
                          </div>
                          <Button 
                            type="submit" 
                            className="w-full bg-emerald-600 hover:bg-emerald-700"
                          >
                            <Lock className="w-4 h-4 mr-2" />
                            Acessar
                          </Button>
                        </form>
                      </CardContent>
                    </Card>
                  </div>
                ) : (
                  <>
                {/* No Store Selected Message */}
                {selectedStoreIds.length === 0 && (
                  <Card className="shadow-sm border-slate-200 bg-gradient-to-r from-slate-50 to-blue-50">
                    <CardContent className="py-12">
                      <div className="text-center">
                        <Store className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                        <h3 className="text-xl font-semibold text-slate-700 mb-2">Selecione uma ou mais lojas</h3>
                        <p className="text-slate-500 mb-6">Use os checkboxes acima para selecionar as lojas que deseja visualizar</p>
                        <Button 
                          onClick={selectAllStores}
                          className="bg-[#4A90E2] hover:bg-[#3A7BC8] text-white"
                        >
                          <Check className="w-4 h-4 mr-2" />
                          Selecionar Todas as Lojas
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                )}
                
                {selectedStoreIds.length > 0 && (
                  <>
                {/* Alerts removed from dashboard - user request 06/03/2026 */}


                {/* Sales Target Settings Modal */}
                <Dialog open={showSettingsModal} onOpenChange={setShowSettingsModal}>
                  <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                      <DialogTitle>Configurações de Meta de Vendas</DialogTitle>
                      <DialogDescription>
                        Configure os parâmetros para análise de ritmo de vendas
                      </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                      <div>
                        <Label htmlFor="sell_through_days">Período para vender todo estoque (dias)</Label>
                        <Input
                          id="sell_through_days"
                          type="number"
                          min="1"
                          max="30"
                          value={salesTargetSettings.sell_through_days}
                          onChange={(e) => setSalesTargetSettings(prev => ({
                            ...prev,
                            sell_through_days: parseInt(e.target.value) || 7
                          }))}
                          className="mt-1"
                        />
                        <p className="text-xs text-slate-500 mt-1">
                          Quantos dias você deseja para vender todo o estoque de novos produtos
                        </p>
                      </div>
                      <div>
                        <Label htmlFor="alert_consecutive_days">Dias consecutivos para alerta</Label>
                        <Input
                          id="alert_consecutive_days"
                          type="number"
                          min="1"
                          max="7"
                          value={salesTargetSettings.alert_consecutive_days}
                          onChange={(e) => setSalesTargetSettings(prev => ({
                            ...prev,
                            alert_consecutive_days: parseInt(e.target.value) || 2
                          }))}
                          className="mt-1"
                        />
                        <p className="text-xs text-slate-500 mt-1">
                          Quantos dias consecutivos abaixo da meta para acionar o alerta
                        </p>
                      </div>
                    </div>
                    <div className="flex justify-end gap-2">
                      <Button variant="outline" onClick={() => setShowSettingsModal(false)}>
                        Cancelar
                      </Button>
                      <Button 
                        onClick={() => updateSalesTargetSettings(salesTargetSettings)}
                        className="bg-purple-600 hover:bg-purple-700"
                      >
                        Salvar
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>

                {/* Dashboard Tabs */}
                <div className="border-b border-slate-200">
                  <nav className="flex gap-1">
                    <button
                      onClick={() => setDashboardTab("vendas_diarias")}
                      className={`px-4 py-2.5 text-sm font-medium rounded-t-lg transition-colors ${
                        dashboardTab === "vendas_diarias"
                          ? 'bg-emerald-600 text-white'
                          : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'
                      }`}
                    >
                      <BarChart3 className="w-4 h-4 inline mr-2" />
                      Vendas Diárias
                    </button>
                    <button
                      onClick={() => setDashboardTab("importar")}
                      className={`px-4 py-2.5 text-sm font-medium rounded-t-lg transition-colors ${
                        dashboardTab === "importar"
                          ? 'bg-emerald-600 text-white'
                          : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'
                      }`}
                    >
                      <Upload className="w-4 h-4 inline mr-2" />
                      Importar Dados
                    </button>
                  </nav>
                </div>

                {/* Tab Content: Vendas Diárias */}
                {dashboardTab === "vendas_diarias" && (
                  <>
                    {/* Period Filter - At the top */}
                    <div className="p-4 bg-slate-50 rounded-xl border border-slate-200 mb-4">
                      <div className="flex flex-wrap gap-3 items-end justify-between">
                        <div className="flex items-center gap-2">
                          <Calendar className="w-5 h-5 text-slate-500" />
                          <span className="font-medium text-slate-700">Período de Análise:</span>
                          <span className="text-sm text-slate-500">
                            {salesDashboard?.period?.description || "Todos os dados"}
                          </span>
                        </div>
                        <div className="flex flex-wrap gap-3 items-end">
                          <div className="space-y-1">
                            <Label className="text-xs text-slate-600">Data Início</Label>
                            <Input
                              type="date"
                              value={salesDateFrom}
                              onChange={(e) => setSalesDateFrom(e.target.value)}
                              className="w-40 h-9"
                            />
                          </div>
                          <div className="space-y-1">
                            <Label className="text-xs text-slate-600">Data Fim</Label>
                            <Input
                              type="date"
                              value={salesDateTo}
                              onChange={(e) => setSalesDateTo(e.target.value)}
                              className="w-40 h-9"
                            />
                          </div>
                          <Button onClick={() => { syncAllStores(true); }} variant="default" size="sm" className="bg-[#4A90E2] hover:bg-[#3a7bc8]" disabled={syncingAllStores}>
                            {syncingAllStores ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1" />} 
                            {syncingAllStores ? "Sincronizando..." : "Atualizar Dados"}
                          </Button>
                        </div>
                      </div>
                    </div>

                    {/* Daily Comparison Cards */}
                    {dailyComparison && dailyComparison.has_data && (
                      <div className="space-y-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div>
                              <h3 className="text-lg font-semibold text-slate-800">Faturamento do Dia</h3>
                              <p className="text-sm text-slate-500">{dailyComparison.reference_info}</p>
                            </div>
                            <Popover>
                              <PopoverTrigger asChild>
                                <Button variant="outline" size="sm" className="h-8 gap-2">
                                  <Calendar className="w-4 h-4" />
                                  <span>{dailyComparisonDate ? dailyComparisonDate.toLocaleDateString('pt-BR') : dailyComparison.today?.date || 'Selecionar'}</span>
                                </Button>
                              </PopoverTrigger>
                              <PopoverContent className="w-auto p-0" align="start">
                                <CalendarComponent
                                  mode="single"
                                  selected={dailyComparisonDate}
                                  onSelect={(date) => {
                                    setDailyComparisonDate(date);
                                    loadDailyComparison(selectedStoreIds, date);
                                  }}
                                  initialFocus
                                />
                                {dailyComparisonDate && (
                                  <div className="p-2 border-t">
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="w-full"
                                      onClick={() => {
                                        setDailyComparisonDate(null);
                                        loadDailyComparison(selectedStoreIds, null);
                                      }}
                                    >
                                      Voltar para hoje
                                    </Button>
                                  </div>
                                )}
                              </PopoverContent>
                            </Popover>
                          </div>
                          <Button onClick={() => loadDailyComparison(selectedStoreIds, dailyComparisonDate)} variant="ghost" size="sm" disabled={loadingComparison}>
                            <RefreshCw className={`w-4 h-4 mr-1 ${loadingComparison ? 'animate-spin' : ''}`} /> Atualizar
                          </Button>
                        </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                      {/* Selected Day (was Today) */}
                      <div className="p-5 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-2xl text-white shadow-lg shadow-emerald-500/30">
                        <div className="flex items-center gap-2 mb-2">
                          <Calendar className="w-4 h-4 opacity-80" />
                          <span className="text-sm font-medium opacity-90">{dailyComparison.today.date} ({dailyComparison.today.weekday})</span>
                        </div>
                        <div className="space-y-1">
                          <div className="flex justify-between items-center">
                            <span className="text-sm opacity-80">Bruto:</span>
                            <span className="text-xl font-bold">R$ {dailyComparison.today.total_bruto?.toLocaleString('pt-BR', {minimumFractionDigits: 2})}</span>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="text-sm opacity-80">Líquido:</span>
                            <span className="text-xl font-bold">R$ {dailyComparison.today.total_liquido?.toLocaleString('pt-BR', {minimumFractionDigits: 2})}</span>
                          </div>
                        </div>
                        <div className="mt-3 pt-2 border-t border-white/20 text-sm opacity-80 flex flex-wrap gap-2">
                          <span>{dailyComparison.today.pedidos} pedidos</span>
                          <span>•</span>
                          <span>{dailyComparison.today.itens} itens</span>
                          <span>•</span>
                          <span>TM: R$ {dailyComparison.today.ticket_medio?.toLocaleString('pt-BR', {minimumFractionDigits: 2})}</span>
                        </div>
                      </div>

                      {/* Yesterday */}
                      <div className="p-5 bg-white rounded-2xl border border-slate-200 shadow-sm">
                        <div className="flex items-center gap-2 mb-2">
                          <TrendingDown className="w-4 h-4 text-slate-400" />
                          <span className="text-sm font-medium text-slate-500">Ontem</span>
                        </div>
                        <div className="space-y-1">
                          <div className="flex justify-between items-center">
                            <span className="text-xs text-slate-500">Bruto:</span>
                            <span className="text-lg font-bold text-slate-800">R$ {dailyComparison.yesterday.total_bruto?.toLocaleString('pt-BR', {minimumFractionDigits: 2})}</span>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="text-xs text-slate-500">Líquido:</span>
                            <span className="text-lg font-bold text-slate-800">R$ {dailyComparison.yesterday.total_liquido?.toLocaleString('pt-BR', {minimumFractionDigits: 2})}</span>
                          </div>
                        </div>
                        <div className={`mt-2 text-sm font-semibold flex items-center gap-1 ${dailyComparison.yesterday.variation >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                          {dailyComparison.yesterday.variation >= 0 ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
                          {dailyComparison.yesterday.variation >= 0 ? '+' : ''}{dailyComparison.yesterday.variation}%
                        </div>
                        <p className="text-xs text-slate-400 mt-1">{dailyComparison.yesterday.pedidos} pedidos • {dailyComparison.yesterday.itens || 0} itens</p>
                      </div>

                      {/* Same Day Last Week */}
                      <div className="p-5 bg-white rounded-2xl border border-slate-200 shadow-sm">
                        <div className="flex items-center gap-2 mb-2">
                          <Calendar className="w-4 h-4 text-slate-400" />
                          <span className="text-sm font-medium text-slate-500">{dailyComparison.same_day_last_week.weekday} passada</span>
                        </div>
                        <div className="space-y-1">
                          <div className="flex justify-between items-center">
                            <span className="text-xs text-slate-500">Bruto:</span>
                            <span className="text-lg font-bold text-slate-800">R$ {dailyComparison.same_day_last_week.total_bruto?.toLocaleString('pt-BR', {minimumFractionDigits: 2})}</span>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="text-xs text-slate-500">Líquido:</span>
                            <span className="text-lg font-bold text-slate-800">R$ {dailyComparison.same_day_last_week.total_liquido?.toLocaleString('pt-BR', {minimumFractionDigits: 2})}</span>
                          </div>
                        </div>
                        <div className={`mt-2 text-sm font-semibold flex items-center gap-1 ${dailyComparison.same_day_last_week.variation >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                          {dailyComparison.same_day_last_week.variation >= 0 ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
                          {dailyComparison.same_day_last_week.variation >= 0 ? '+' : ''}{dailyComparison.same_day_last_week.variation}%
                        </div>
                        <p className="text-xs text-slate-400 mt-1">{dailyComparison.same_day_last_week.pedidos} pedidos</p>
                      </div>

                      {/* 7 Day Average */}
                      <div className="p-5 bg-white rounded-2xl border border-slate-200 shadow-sm">
                        <div className="flex items-center gap-2 mb-2">
                          <BarChart3 className="w-4 h-4 text-slate-400" />
                          <span className="text-sm font-medium text-slate-500">Média 7 dias</span>
                        </div>
                        <div className="space-y-1">
                          <div className="flex justify-between items-center">
                            <span className="text-xs text-slate-500">Bruto:</span>
                            <span className="text-lg font-bold text-slate-800">R$ {dailyComparison.average_7_days.total_bruto?.toLocaleString('pt-BR', {minimumFractionDigits: 2})}</span>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="text-xs text-slate-500">Líquido:</span>
                            <span className="text-lg font-bold text-slate-800">R$ {dailyComparison.average_7_days.total_liquido?.toLocaleString('pt-BR', {minimumFractionDigits: 2})}</span>
                          </div>
                        </div>
                        <div className={`mt-2 text-sm font-semibold flex items-center gap-1 ${dailyComparison.average_7_days.variation >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                          {dailyComparison.average_7_days.variation >= 0 ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
                          {dailyComparison.average_7_days.variation >= 0 ? '+' : ''}{dailyComparison.average_7_days.variation}%
                        </div>
                        <p className="text-xs text-slate-400 mt-1">TM: R$ {dailyComparison.average_7_days.ticket_medio?.toLocaleString('pt-BR', {minimumFractionDigits: 2})}</p>
                      </div>
                    </div>
                    
                    {/* Markup Metrics Card - Only shows real data from sales with cost */}
                    {markupMetrics && markupMetrics.summary && markupMetrics.summary.vendas_com_custo > 0 && (
                      <div className="mt-4 p-4 bg-gradient-to-r from-purple-50 to-indigo-50 rounded-xl border border-purple-200">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-2">
                            <div className="p-2 bg-purple-100 rounded-lg">
                              <TrendingUp className="w-4 h-4 text-purple-600" />
                            </div>
                            <div>
                              <h4 className="font-semibold text-purple-800">Markup Real</h4>
                              <p className="text-xs text-purple-600">
                                Somente vendas com custo em NF-e ({markupMetrics.summary.vendas_com_custo?.toLocaleString('pt-BR')} itens)
                              </p>
                            </div>
                          </div>
                          {loadingMarkup && <RefreshCw className="w-4 h-4 animate-spin text-purple-400" />}
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                          <div className="bg-white/60 rounded-lg p-3">
                            <p className="text-xs text-purple-600">Venda Líquida</p>
                            <p className="text-lg font-bold text-purple-800">
                              R$ {markupMetrics.summary.total_receita?.toLocaleString('pt-BR', {minimumFractionDigits: 2})}
                            </p>
                          </div>
                          <div className="bg-white/60 rounded-lg p-3">
                            <p className="text-xs text-purple-600">Custo NF-e</p>
                            <p className="text-lg font-bold text-purple-800">
                              R$ {markupMetrics.summary.total_custo?.toLocaleString('pt-BR', {minimumFractionDigits: 2})}
                            </p>
                          </div>
                          <div className="bg-white/60 rounded-lg p-3">
                            <p className="text-xs text-purple-600">Lucro Bruto</p>
                            <p className={`text-lg font-bold ${markupMetrics.summary.lucro_bruto >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                              R$ {markupMetrics.summary.lucro_bruto?.toLocaleString('pt-BR', {minimumFractionDigits: 2})}
                            </p>
                          </div>
                          <div className="bg-white/60 rounded-lg p-3">
                            <p className="text-xs text-purple-600">Markup (V/C)</p>
                            <p className={`text-lg font-bold ${markupMetrics.summary.markup_fator >= 1 ? 'text-emerald-600' : 'text-red-600'}`}>
                              {markupMetrics.summary.markup_fator?.toFixed(2)}x
                            </p>
                          </div>
                          <div className="bg-white/60 rounded-lg p-3">
                            <p className="text-xs text-purple-600">Margem</p>
                            <p className={`text-lg font-bold ${markupMetrics.summary.margem_media >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                              {markupMetrics.summary.margem_media?.toFixed(1)}%
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Payment Methods Breakdown - Below Faturamento do Dia */}
                <PaymentMethodsCard 
                  paymentMethods={paymentMethods}
                  loading={loadingPaymentMethods}
                  periodDescription={salesDashboard?.period?.description || (salesDateFrom || salesDateTo 
                    ? `${salesDateFrom ? new Date(salesDateFrom + 'T12:00:00').toLocaleDateString('pt-BR') : 'Início'} - ${salesDateTo ? new Date(salesDateTo + 'T12:00:00').toLocaleDateString('pt-BR') : 'Hoje'}`
                    : 'Todos os dados')}
                />

                {/* Loading State */}
                {loadingComparison && !dailyComparison && (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
                    <span className="ml-3 text-slate-600">Carregando dados...</span>
                  </div>
                )}

                {/* Evolution Chart - Last 30 days */}
                {chartData.length > 0 && (
                  <Card className="shadow-sm border-slate-200">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-lg flex items-center gap-2">
                        <TrendingUp className="w-5 h-5 text-emerald-600" />
                        Evolução - Últimos 30 dias
                        {selectedStoreIds.length === 1 && (
                          <span className="text-sm font-normal text-slate-500">
                            ({stores.find(s => s.id === selectedStoreIds[0])?.name})
                          </span>
                        )}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="h-80">
                        <ResponsiveContainer width="100%" height="100%">
                          <LineChart data={chartData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                            <XAxis dataKey="data" tick={{ fontSize: 11 }} stroke="#94a3b8" />
                            <YAxis yAxisId="left" tick={{ fontSize: 11 }} stroke="#94a3b8" tickFormatter={(v) => `R$${(v/1000).toFixed(0)}k`} />
                            <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11 }} stroke="#94a3b8" />
                            <Tooltip 
                              contentStyle={{ backgroundColor: 'white', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '12px' }}
                              formatter={(value, name) => {
                                if (name === 'Bruto' || name === 'Líquido' || name === 'Ticket Médio') {
                                  return [`R$ ${value?.toLocaleString('pt-BR', {minimumFractionDigits: 2})}`, name];
                                }
                                return [value?.toLocaleString('pt-BR'), name];
                              }}
                              labelFormatter={(label) => `Data: ${label}`}
                            />
                            <Legend wrapperStyle={{ fontSize: '12px' }} />
                            <Line yAxisId="left" type="monotone" dataKey="bruto" name="Bruto" stroke="#10b981" strokeWidth={2} dot={{ r: 3 }} />
                            <Line yAxisId="left" type="monotone" dataKey="liquido" name="Líquido" stroke="#3b82f6" strokeWidth={2} dot={{ r: 3 }} />
                            <Line yAxisId="left" type="monotone" dataKey="ticket_medio" name="Ticket Médio" stroke="#8b5cf6" strokeWidth={2} dot={{ r: 3 }} />
                            <Line yAxisId="right" type="monotone" dataKey="itens" name="Itens" stroke="#f59e0b" strokeWidth={1} strokeDasharray="5 5" dot={false} />
                            <Line yAxisId="right" type="monotone" dataKey="pedidos" name="Pedidos" stroke="#ef4444" strokeWidth={1} strokeDasharray="5 5" dot={false} />
                          </LineChart>
                        </ResponsiveContainer>
                      </div>
                    </CardContent>
                  </Card>
                )}
                
                {/* Sales Heatmap - Shows sales by hour of day */}
                <SalesHeatmap 
                  storeId={selectedStoreIds.length === 1 ? selectedStoreIds[0] : null}
                  storeIds={selectedStoreIds.length > 1 ? selectedStoreIds : null}
                  days={30}
                />
                
                {/* Store Comparison Chart - Shows when multiple stores or all stores */}
                {selectedStoreIds.length !== 1 && storeComparisonData.length > 0 && (
                  <Card className="shadow-sm border-slate-200">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-lg flex items-center gap-2">
                        <BarChart3 className="w-5 h-5 text-purple-600" />
                        Comparativo de Lojas - Últimos 30 dias
                        <span className="text-sm font-normal text-slate-500">
                          (Faturamento Líquido)
                        </span>
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="h-80">
                        <ResponsiveContainer width="100%" height="100%">
                          <LineChart data={storeComparisonData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                            <XAxis dataKey="data" tick={{ fontSize: 11 }} stroke="#94a3b8" />
                            <YAxis tick={{ fontSize: 11 }} stroke="#94a3b8" tickFormatter={(v) => `R$${(v/1000).toFixed(0)}k`} />
                            <Tooltip 
                              contentStyle={{ backgroundColor: 'white', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '12px' }}
                              formatter={(value, name) => {
                                const storeName = stores.find(s => s.id === name.replace('_liquido', ''))?.name || name;
                                return [`R$ ${value?.toLocaleString('pt-BR', {minimumFractionDigits: 2})}`, storeName];
                              }}
                              labelFormatter={(label) => `Data: ${label}`}
                            />
                            <Legend 
                              wrapperStyle={{ fontSize: '12px' }} 
                              formatter={(value) => stores.find(s => s.id === value.replace('_liquido', ''))?.name || value} 
                            />
                            {(selectedStoreIds.length === 0 || selectedStoreIds.includes('vencidinho_cangaiba')) && (
                              <Line type="monotone" dataKey="vencidinho_cangaiba_liquido" name="vencidinho_cangaiba_liquido" stroke="#4A90E2" strokeWidth={2} dot={{ r: 2 }} />
                            )}
                            {(selectedStoreIds.length === 0 || selectedStoreIds.includes('bestby_sjc')) && (
                              <Line type="monotone" dataKey="bestby_sjc_liquido" name="bestby_sjc_liquido" stroke="#e74c3c" strokeWidth={2} dot={{ r: 2 }} />
                            )}
                            {(selectedStoreIds.length === 0 || selectedStoreIds.includes('bestby_jacarei')) && (
                              <Line type="monotone" dataKey="bestby_jacarei_liquido" name="bestby_jacarei_liquido" stroke="#2ecc71" strokeWidth={2} dot={{ r: 2 }} />
                            )}
                            {(selectedStoreIds.length === 0 || selectedStoreIds.includes('bestby_panamby')) && (
                              <Line type="monotone" dataKey="bestby_panamby_liquido" name="bestby_panamby_liquido" stroke="#9b59b6" strokeWidth={2} dot={{ r: 2 }} />
                            )}
                          </LineChart>
                        </ResponsiveContainer>
                      </div>
                      
                      {/* Direction Indicators */}
                      <div className="mt-4 pt-4 border-t border-slate-200">
                        <p className="text-sm font-medium text-slate-600 mb-3">Tendência diária (vs dia anterior)</p>
                        <div className="flex flex-wrap gap-2">
                          {storeComparisonData.slice(1).map((day, idx) => {
                            const prevDay = storeComparisonData[idx];
                            const storeKeys = ['vencidinho_cangaiba_liquido', 'bestby_sjc_liquido', 'bestby_jacarei_liquido', 'bestby_panamby_liquido'];
                            const activeStores = storeKeys.filter(key => 
                              selectedStoreIds.length === 0 || selectedStoreIds.includes(key.replace('_liquido', ''))
                            );
                            
                            let upCount = 0;
                            let downCount = 0;
                            let equalCount = 0;
                            
                            activeStores.forEach(key => {
                              const curr = day[key] || 0;
                              const prev = prevDay[key] || 0;
                              if (curr > prev) upCount++;
                              else if (curr < prev) downCount++;
                              else equalCount++;
                            });
                            
                            const allSameDirection = upCount === activeStores.length || downCount === activeStores.length;
                            const bgColor = allSameDirection 
                              ? (upCount === activeStores.length ? 'bg-emerald-100 border-emerald-300' : 'bg-red-100 border-red-300')
                              : 'bg-slate-100 border-slate-300';
                            
                            return (
                              <div 
                                key={idx}
                                className={`flex flex-col items-center px-2 py-1 rounded-lg border ${bgColor} min-w-[50px]`}
                                title={`${day.data}: ${upCount} subindo, ${downCount} caindo`}
                              >
                                <span className="text-[10px] text-slate-500">{day.data?.split('/').slice(0,2).join('/')}</span>
                                <div className="flex items-center gap-1">
                                  {upCount > 0 && (
                                    <span className="flex items-center text-emerald-600 font-bold text-xs">
                                      {upCount}<TrendingUp className="w-3 h-3" />
                                    </span>
                                  )}
                                  {downCount > 0 && (
                                    <span className="flex items-center text-red-600 font-bold text-xs">
                                      {downCount}<TrendingDown className="w-3 h-3" />
                                    </span>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Dashboard Section */}
                {salesDashboard && salesDashboard.total_records > 0 && (
                  <Card className="shadow-sm border-slate-200">
                    <CardHeader>
                      <div className="flex items-center justify-between">
                        <div>
                          <CardTitle className="text-lg">Análise Detalhada</CardTitle>
                          <CardDescription>
                            Período: {salesDashboard.period?.description || "Todos os dados"}
                          </CardDescription>
                        </div>
                        <div className="flex gap-2 items-center">
                          <div className="space-y-1">
                            <select
                              value={salesGroupBy}
                              onChange={(e) => setSalesGroupBy(e.target.value)}
                              className="h-9 px-3 rounded-md border border-slate-200 text-sm"
                            >
                              <option value="day">Por Dia</option>
                              <option value="week">Por Semana</option>
                              <option value="month">Por Mês</option>
                            </select>
                          </div>
                          <Button onClick={() => { syncAllStores(true); }} variant="outline" size="sm" disabled={syncingAllStores}>
                            {syncingAllStores ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1" />}
                            {syncingAllStores ? "Sincronizando..." : "Atualizar"}
                          </Button>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-6">
                      {/* Summary Cards */}
                      <div className="grid grid-cols-2 md:grid-cols-7 gap-3">
                        <div className="p-3 bg-gradient-to-br from-blue-50 to-white rounded-xl border border-blue-100">
                          <p className="text-xs text-blue-600 mb-1">Total Itens</p>
                          <p className="text-xl font-bold text-blue-700">{salesDashboard.summary.total_vendas?.toLocaleString('pt-BR')}</p>
                        </div>
                        <div className="p-3 bg-gradient-to-br from-indigo-50 to-white rounded-xl border border-indigo-100">
                          <p className="text-xs text-indigo-600 mb-1">Total Pedidos</p>
                          <p className="text-xl font-bold text-indigo-700">{salesDashboard.summary.total_pedidos?.toLocaleString('pt-BR')}</p>
                        </div>
                        <div className="p-3 bg-gradient-to-br from-emerald-50 to-white rounded-xl border border-emerald-100">
                          <p className="text-xs text-emerald-600 mb-1">Valor Bruto</p>
                          <p className="text-xl font-bold text-emerald-700">R$ {salesDashboard.summary.total_bruto?.toLocaleString('pt-BR', {minimumFractionDigits: 2})}</p>
                        </div>
                        <div className="p-3 bg-gradient-to-br from-amber-50 to-white rounded-xl border border-amber-100">
                          <p className="text-xs text-amber-600 mb-1">Descontos</p>
                          <p className="text-xl font-bold text-amber-700">R$ {salesDashboard.summary.total_desconto?.toLocaleString('pt-BR', {minimumFractionDigits: 2})}</p>
                        </div>
                        <div className="p-3 bg-gradient-to-br from-teal-50 to-white rounded-xl border border-teal-100">
                          <p className="text-xs text-teal-600 mb-1">Valor Líquido</p>
                          <p className="text-xl font-bold text-teal-700">R$ {salesDashboard.summary.total_liquido?.toLocaleString('pt-BR', {minimumFractionDigits: 2})}</p>
                        </div>
                        <div className="p-3 bg-gradient-to-br from-purple-50 to-white rounded-xl border border-purple-100">
                          <p className="text-xs text-purple-600 mb-1">Ticket Médio</p>
                          <p className="text-xl font-bold text-purple-700">R$ {salesDashboard.summary.ticket_medio?.toLocaleString('pt-BR', {minimumFractionDigits: 2})}</p>
                        </div>
                        <div className="p-3 bg-gradient-to-br from-cyan-50 to-white rounded-xl border border-cyan-100">
                          <p className="text-xs text-cyan-600 mb-1">Itens/Cesta</p>
                          <p className="text-xl font-bold text-cyan-700">{salesDashboard.summary.itens_por_cesta || '-'}</p>
                        </div>
                      </div>

                      {/* Revenue Projection Card */}
                      {revenueProjection && revenueProjection.success && revenueProjection.days_with_sales > 0 && (
                        <div className="p-4 bg-gradient-to-br from-rose-50 via-orange-50 to-amber-50 rounded-xl border border-orange-200">
                          <div className="flex items-center gap-2 mb-3">
                            <TrendingUp className="w-5 h-5 text-orange-600" />
                            <h4 className="font-semibold text-slate-800">Projeção de Faturamento - {revenueProjection.current_month}</h4>
                            {revenueProjection.growth_indicator && (
                              <Badge variant="outline" className={`ml-auto ${
                                revenueProjection.growth_indicator === 'trending_up' ? 'bg-emerald-50 text-emerald-700 border-emerald-300' :
                                revenueProjection.growth_indicator === 'trending_down' ? 'bg-red-50 text-red-700 border-red-300' :
                                'bg-slate-50 text-slate-700 border-slate-300'
                              }`}>
                                {revenueProjection.growth_indicator === 'trending_up' ? '↑ Em alta' :
                                 revenueProjection.growth_indicator === 'trending_down' ? '↓ Em baixa' : '→ Estável'}
                              </Badge>
                            )}
                          </div>
                          
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
                            <div className="p-3 bg-white/70 rounded-lg border border-orange-100">
                              <p className="text-xs text-orange-600 mb-1">Faturamento até dia {revenueProjection.current_day}</p>
                              <p className="text-lg font-bold text-orange-700">
                                R$ {revenueProjection.revenue_until_today?.toLocaleString('pt-BR', {minimumFractionDigits: 2})}
                              </p>
                            </div>
                            <div className="p-3 bg-white/70 rounded-lg border border-amber-100">
                              <p className="text-xs text-amber-600 mb-1">Média Diária</p>
                              <p className="text-lg font-bold text-amber-700">
                                R$ {revenueProjection.daily_average?.toLocaleString('pt-BR', {minimumFractionDigits: 2})}
                              </p>
                              <p className="text-xs text-amber-500">({revenueProjection.days_with_sales} dias com vendas)</p>
                            </div>
                            <div className="p-3 bg-white/70 rounded-lg border border-rose-100">
                              <p className="text-xs text-rose-600 mb-1">Projeção Restante ({revenueProjection.remaining_days} dias)</p>
                              <p className="text-lg font-bold text-rose-700">
                                R$ {revenueProjection.projected_remaining?.toLocaleString('pt-BR', {minimumFractionDigits: 2})}
                              </p>
                            </div>
                            <div className="p-3 bg-gradient-to-br from-emerald-100 to-teal-100 rounded-lg border border-emerald-200">
                              <p className="text-xs text-emerald-700 mb-1 font-semibold">Total Projetado do Mês</p>
                              <p className="text-xl font-bold text-emerald-800">
                                R$ {revenueProjection.total_projected?.toLocaleString('pt-BR', {minimumFractionDigits: 2})}
                              </p>
                            </div>
                          </div>
                          
                          <div className="flex items-center gap-2 text-xs text-slate-500">
                            <Clock className="w-3 h-3" />
                            <span>Projeção baseada na média diária de vendas do mês atual</span>
                          </div>
                        </div>
                      )}
                      
                      {loadingRevenueProjection && (
                        <div className="p-4 bg-slate-50 rounded-xl border border-slate-200 flex items-center justify-center gap-2">
                          <Loader2 className="w-4 h-4 animate-spin text-slate-400" />
                          <span className="text-sm text-slate-500">Calculando projeção...</span>
                        </div>
                      )}

                      {/* Top Products Controls */}
                      <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                        <div className="flex items-center gap-4">
                          <span className="text-sm text-slate-600">Exibir top</span>
                          <Input
                            type="number"
                            min="1"
                            max="100"
                            value={topProductsLimit}
                            onChange={(e) => setTopProductsLimit(Math.min(100, Math.max(1, parseInt(e.target.value) || 10)))}
                            className="w-20 h-8 text-center"
                          />
                          <span className="text-sm text-slate-600">produtos (máx 100)</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => setShowProductSearch(!showProductSearch)}
                          >
                            <Search className="w-4 h-4 mr-1" />
                            Buscar Produto
                          </Button>
                        </div>
                      </div>
                      
                      {/* Product Search */}
                      {showProductSearch && salesDashboard?.all_products && (
                        <div className="p-3 bg-white border border-slate-200 rounded-lg">
                          <div className="flex items-center gap-2 mb-2">
                            <Search className="w-4 h-4 text-slate-400" />
                            <Input
                              placeholder="Digite o nome do produto..."
                              value={productSearch}
                              onChange={(e) => setProductSearch(e.target.value)}
                              className="flex-1"
                            />
                          </div>
                          {productSearch.length >= 2 && (
                            <div className="max-h-40 overflow-y-auto space-y-1">
                              {salesDashboard.all_products
                                .filter(p => p.toLowerCase().includes(productSearch.toLowerCase()))
                                .slice(0, 20)
                                .map((p, i) => (
                                  <div 
                                    key={i}
                                    onClick={() => { loadProductTicket(p); setProductSearch(""); }}
                                    className="p-2 text-sm text-slate-700 hover:bg-slate-100 rounded cursor-pointer"
                                  >
                                    {p}
                                  </div>
                                ))}
                            </div>
                          )}
                        </div>
                      )}

                      {/* Top Products (Value & Qty side by side) */}
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        {/* Top by Value */}
                        <div className="p-4 bg-white rounded-xl border border-slate-200">
                          <h4 className="font-semibold text-slate-800 mb-3 flex items-center gap-2">
                            <DollarSign className="w-4 h-4 text-emerald-600" />
                            Top {topProductsLimit} Produtos por Valor
                          </h4>
                          <div className={`space-y-2 ${(salesDashboard.by_product_value || salesDashboard.by_product)?.length > 10 ? 'max-h-[500px] overflow-y-auto' : ''}`}>
                            {(salesDashboard.by_product_value || salesDashboard.by_product)?.slice(0, topProductsLimit).map((p, i) => {
                              const percRep = salesDashboard.summary?.total_liquido > 0 
                                ? ((p.total_liquido / salesDashboard.summary.total_liquido) * 100).toFixed(1) 
                                : 0;
                              return (
                                <div 
                                  key={i} 
                                  onClick={() => loadProductTicket(p.produto)}
                                  className={`flex items-center justify-between p-2 rounded-lg cursor-pointer transition-colors ${
                                    selectedProduct === p.produto ? 'bg-emerald-100 border border-emerald-300' : 'bg-slate-50 hover:bg-slate-100'
                                  }`}
                                >
                                  <div className="flex items-center gap-2 flex-1 min-w-0">
                                    <span className="w-6 h-6 rounded-full bg-emerald-100 text-emerald-700 text-xs flex items-center justify-center font-bold flex-shrink-0">{i + 1}</span>
                                    <span className="text-sm text-slate-700 truncate">{p.produto}</span>
                                  </div>
                                  <div className="flex items-center gap-2 flex-shrink-0">
                                    <span className="text-xs text-slate-400">PM: R$ {p.preco_medio?.toLocaleString('pt-BR', {minimumFractionDigits: 2}) || '-'}</span>
                                    <span className="text-xs text-emerald-500 bg-emerald-50 px-1.5 py-0.5 rounded">{percRep}%</span>
                                    <span className="text-sm font-semibold text-emerald-600">R$ {p.total_liquido?.toLocaleString('pt-BR', {minimumFractionDigits: 2})}</span>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                          {/* Sum of top products */}
                          <div className="mt-3 pt-3 border-t border-slate-200 flex justify-between items-center">
                            <span className="text-sm font-medium text-slate-600">Soma Top {topProductsLimit}:</span>
                            <span className="text-lg font-bold text-emerald-700">
                              R$ {(salesDashboard.by_product_value || salesDashboard.by_product)?.slice(0, topProductsLimit).reduce((acc, p) => acc + (p.total_liquido || 0), 0).toLocaleString('pt-BR', {minimumFractionDigits: 2})}
                            </span>
                          </div>
                        </div>

                        {/* Top by Quantity */}
                        <div className="p-4 bg-white rounded-xl border border-slate-200">
                          <h4 className="font-semibold text-slate-800 mb-3 flex items-center gap-2">
                            <Package className="w-4 h-4 text-blue-600" />
                            Top {topProductsLimit} Produtos por Quantidade
                          </h4>
                          <div className={`space-y-2 ${(salesDashboard.by_product_qty || salesDashboard.by_product)?.length > 10 ? 'max-h-[500px] overflow-y-auto' : ''}`}>
                            {(salesDashboard.by_product_qty || salesDashboard.by_product)?.slice(0, topProductsLimit).map((p, i) => {
                              const qty = p.total_quantidade || p.quantidade || 0;
                              const percRep = salesDashboard.summary?.total_quantidade > 0 
                                ? ((qty / salesDashboard.summary.total_quantidade) * 100).toFixed(1) 
                                : 0;
                              return (
                                <div 
                                  key={i} 
                                  onClick={() => loadProductTicket(p.produto)}
                                  className={`flex items-center justify-between p-2 rounded-lg cursor-pointer transition-colors ${
                                    selectedProduct === p.produto ? 'bg-blue-100 border border-blue-300' : 'bg-slate-50 hover:bg-slate-100'
                                  }`}
                                >
                                  <div className="flex items-center gap-2 flex-1 min-w-0">
                                    <span className="w-6 h-6 rounded-full bg-blue-100 text-blue-700 text-xs flex items-center justify-center font-bold flex-shrink-0">{i + 1}</span>
                                    <span className="text-sm text-slate-700 truncate">{p.produto}</span>
                                  </div>
                                  <div className="flex items-center gap-2 flex-shrink-0">
                                    <span className="text-xs text-slate-400">PM: R$ {p.preco_medio?.toLocaleString('pt-BR', {minimumFractionDigits: 2}) || '-'}</span>
                                    <span className="text-xs text-blue-500 bg-blue-50 px-1.5 py-0.5 rounded">{percRep}%</span>
                                    <span className="text-sm font-semibold text-blue-600">{qty?.toLocaleString('pt-BR')} un</span>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                          {/* Sum of top products */}
                          <div className="mt-3 pt-3 border-t border-slate-200 flex justify-between items-center">
                            <span className="text-sm font-medium text-slate-600">Soma Top {topProductsLimit}:</span>
                            <span className="text-lg font-bold text-blue-700">
                              {(salesDashboard.by_product_qty || salesDashboard.by_product)?.slice(0, topProductsLimit).reduce((acc, p) => acc + (p.total_quantidade || p.quantidade || 0), 0).toLocaleString('pt-BR')} un
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Top by Markup */}
                      <div className="p-4 bg-white rounded-xl border border-slate-200">
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-3">
                          <h4 className="font-semibold text-slate-800 flex items-center gap-2">
                            <Percent className="w-4 h-4 text-emerald-600" />
                            {markupSortAsc ? 'Piores' : 'Top'} {markupProductsLimit} por Markup
                            <span className="text-xs text-slate-400 font-normal ml-1">
                              ({topMarkupProducts.length > 0 ? `${topMarkupProducts.length} com custo` : 'sem dados'})
                            </span>
                          </h4>
                          <div className="flex items-center gap-2">
                            <div className="flex items-center gap-1">
                              <span className="text-xs text-slate-500">Top</span>
                              <Input
                                type="number"
                                min="1"
                                max="100"
                                value={markupProductsLimit}
                                onChange={(e) => {
                                  const newLimit = Math.min(100, Math.max(1, parseInt(e.target.value) || 10));
                                  setMarkupProductsLimit(newLimit);
                                  loadTopMarkupProducts(selectedStoreIds, markupSortAsc, newLimit);
                                }}
                                className="w-14 h-7 text-xs text-center"
                              />
                            </div>
                            <Button
                              variant={markupSortAsc ? "default" : "outline"}
                              size="sm"
                              className={`h-7 text-xs ${markupSortAsc ? 'bg-red-600 hover:bg-red-700' : ''}`}
                              onClick={() => {
                                const newSortAsc = !markupSortAsc;
                                setMarkupSortAsc(newSortAsc);
                                loadTopMarkupProducts(selectedStoreIds, newSortAsc, markupProductsLimit);
                              }}
                            >
                              {markupSortAsc ? (
                                <>
                                  <TrendingDown className="w-3 h-3 mr-1" />
                                  Piores
                                </>
                              ) : (
                                <>
                                  <TrendingUp className="w-3 h-3 mr-1" />
                                  Melhores
                                </>
                              )}
                            </Button>
                          </div>
                        </div>
                        {loadingTopMarkupProducts ? (
                          <div className="flex items-center justify-center py-8 text-emerald-600">
                            <Loader2 className="w-5 h-5 animate-spin mr-2" />
                            <span className="text-sm">Calculando markup...</span>
                          </div>
                        ) : topMarkupProducts.length > 0 ? (
                          <>
                            <div className={`space-y-2 ${topMarkupProducts.length > 10 ? 'max-h-[500px] overflow-y-auto' : ''}`}>
                              {topMarkupProducts.map((p, i) => (
                                <div 
                                  key={i} 
                                  onClick={() => loadProductTicket(p.produto)}
                                  className={`flex items-center justify-between p-2 rounded-lg cursor-pointer transition-colors ${
                                    selectedProduct === p.produto 
                                      ? (markupSortAsc ? 'bg-red-100 border border-red-300' : 'bg-emerald-100 border border-emerald-300')
                                      : 'bg-slate-50 hover:bg-slate-100'
                                  }`}
                                >
                                  <div className="flex items-center gap-2 flex-1 min-w-0">
                                    <span className={`w-6 h-6 rounded-full text-xs flex items-center justify-center font-bold flex-shrink-0 ${
                                      markupSortAsc ? 'bg-red-100 text-red-700' : 'bg-emerald-100 text-emerald-700'
                                    }`}>{i + 1}</span>
                                    <span className="text-sm text-slate-700 truncate">{p.produto}</span>
                                  </div>
                                  <div className="flex items-center gap-2 flex-shrink-0">
                                    <span className="text-xs text-slate-400">
                                      C: R$ {p.cost_price?.toLocaleString('pt-BR', {minimumFractionDigits: 2})}
                                    </span>
                                    <span className="text-xs text-slate-400">
                                      V: R$ {p.avg_sale_price?.toLocaleString('pt-BR', {minimumFractionDigits: 2})}
                                    </span>
                                    <span className={`text-sm font-bold px-2 py-0.5 rounded ${
                                      p.markup_percent >= 50 
                                        ? 'bg-emerald-100 text-emerald-700' 
                                        : p.markup_percent >= 30 
                                          ? 'bg-green-100 text-green-700' 
                                          : p.markup_percent >= 15 
                                            ? 'bg-amber-100 text-amber-700' 
                                            : 'bg-red-100 text-red-700'
                                    }`}>
                                      {p.markup_percent?.toFixed(1)}%
                                    </span>
                                  </div>
                                </div>
                              ))}
                            </div>
                            {/* Sum of top markup products */}
                            <div className="mt-3 pt-3 border-t border-slate-200 flex justify-between items-center">
                              <span className="text-sm font-medium text-slate-600">
                                {markupSortAsc ? 'Perda potencial' : 'Lucro total'} Top {markupProductsLimit}:
                              </span>
                              <span className={`text-lg font-bold ${markupSortAsc ? 'text-red-600' : 'text-emerald-700'}`}>
                                R$ {topMarkupProducts.reduce((acc, p) => acc + ((p.lucro_unitario || 0) * (p.total_qty || 0)), 0).toLocaleString('pt-BR', {minimumFractionDigits: 2})}
                              </span>
                            </div>
                          </>
                        ) : (
                          <div className="text-center py-8 text-slate-500">
                            <Percent className="w-8 h-8 mx-auto mb-2 text-slate-300" />
                            <p className="text-sm">Nenhum produto com custo disponível</p>
                            <p className="text-xs text-slate-400">Importe NF-es para calcular markup</p>
                          </div>
                        )}
                      </div>

                      {/* Product Analysis (when a product is selected) */}
                      {selectedProduct && (
                        <div className="p-4 bg-gradient-to-r from-purple-50 to-indigo-50 rounded-xl border border-purple-200">
                          <div className="flex items-center justify-between mb-3">
                            <h4 className="font-semibold text-slate-800 flex items-center gap-2">
                              <ShoppingCart className="w-4 h-4 text-purple-600" />
                              Análise: <span className="text-purple-700">{selectedProduct}</span>
                            </h4>
                            <Button variant="ghost" size="sm" onClick={() => { setSelectedProduct(null); setProductTicket(null); setSelectedProductMarkup(null); setSelectedProductStock(null); setBasketAnalysis(null); }}>
                              <X className="w-4 h-4" />
                            </Button>
                          </div>
                          {loadingProductTicket ? (
                            <div className="flex items-center gap-2 text-purple-600">
                              <Loader2 className="w-4 h-4 animate-spin" />
                              <span className="text-sm">Analisando...</span>
                            </div>
                          ) : productTicket?.success ? (
                            <div className="space-y-4">
                              {/* Stats Cards */}
                              <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
                                <div className="p-3 bg-white rounded-lg">
                                  <p className="text-xs text-slate-500">Pedidos c/ produto</p>
                                  <div className="flex items-baseline gap-1">
                                    <p className="text-xl font-bold text-purple-700">{productTicket.pedidos_com_produto}</p>
                                    <span className="text-xs text-slate-400">/ {productTicket.total_pedidos_geral}</span>
                                    <span className="text-xs text-purple-500 bg-purple-50 px-1 rounded">{productTicket.perc_pedidos}%</span>
                                  </div>
                                </div>
                                <div className="p-3 bg-white rounded-lg border-2 border-purple-300">
                                  <p className="text-xs text-purple-600 font-medium">Ticket Médio</p>
                                  <p className="text-xl font-bold text-purple-700">R$ {productTicket.ticket_medio_pedidos?.toLocaleString('pt-BR', {minimumFractionDigits: 2})}</p>
                                </div>
                                <div className="p-3 bg-white rounded-lg">
                                  <p className="text-xs text-slate-500">Fat. Total Pedidos</p>
                                  <p className="text-xl font-bold text-purple-700">R$ {productTicket.total_faturamento_pedidos?.toLocaleString('pt-BR', {minimumFractionDigits: 2})}</p>
                                </div>
                                <div className="p-3 bg-white rounded-lg border-2 border-amber-300">
                                  <p className="text-xs text-amber-600 font-medium">Preço Médio</p>
                                  <p className="text-xl font-bold text-amber-700">R$ {productTicket.preco_medio?.toLocaleString('pt-BR', {minimumFractionDigits: 2})}</p>
                                </div>
                                <div className="p-3 bg-white rounded-lg">
                                  <p className="text-xs text-slate-500">Qtd Vendida</p>
                                  <p className="text-xl font-bold text-purple-700">{productTicket.qtd_vendida?.toLocaleString('pt-BR')}</p>
                                </div>
                                <div className="p-3 bg-white rounded-lg">
                                  <p className="text-xs text-slate-500">Valor do Produto</p>
                                  <div className="flex items-baseline gap-1">
                                    <p className="text-xl font-bold text-purple-700">R$ {productTicket.valor_total_produto?.toLocaleString('pt-BR', {minimumFractionDigits: 2})}</p>
                                    <span className="text-xs text-purple-500 bg-purple-50 px-1 rounded">{productTicket.perc_faturamento}%</span>
                                  </div>
                                </div>
                              </div>
                              
                              {/* Price Breakdown */}
                              {productTicket.precos_vendidos && productTicket.precos_vendidos.length > 0 && (
                                <div className="p-3 bg-white rounded-lg">
                                  <h5 className="text-sm font-semibold text-slate-700 mb-2 flex items-center gap-2">
                                    <DollarSign className="w-4 h-4 text-amber-600" />
                                    Preços Praticados
                                  </h5>
                                  <div className="space-y-1 max-h-40 overflow-y-auto">
                                    {productTicket.precos_vendidos.map((p, i) => (
                                      <div key={i} className="flex items-center justify-between p-2 bg-slate-50 rounded">
                                        <span className="text-sm font-medium text-slate-700">
                                          R$ {p.preco_unitario?.toLocaleString('pt-BR', {minimumFractionDigits: 2})}
                                        </span>
                                        <div className="flex items-center gap-4">
                                          <span className="text-sm text-slate-600">
                                            <span className="font-semibold text-amber-600">{p.quantidade?.toLocaleString('pt-BR')}</span> vendidos
                                          </span>
                                          <span className="text-xs text-slate-400">
                                            ({p.num_vendas} {p.num_vendas === 1 ? 'venda' : 'vendas'})
                                          </span>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}
                              
                              {/* Markup Card */}
                              <div className="p-3 bg-gradient-to-r from-emerald-50 to-teal-50 rounded-lg border border-emerald-200">
                                <h5 className="text-sm font-semibold text-slate-700 mb-2 flex items-center gap-2">
                                  <Percent className="w-4 h-4 text-emerald-600" />
                                  Markup do Produto
                                </h5>
                                {loadingSelectedProductMarkup ? (
                                  <div className="flex items-center gap-2 text-emerald-600 py-2">
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                    <span className="text-sm">Calculando...</span>
                                  </div>
                                ) : selectedProductMarkup?.has_cost_data ? (
                                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                    <div className="p-2 bg-white rounded-lg">
                                      <p className="text-xs text-slate-500">Custo</p>
                                      <p className="text-lg font-bold text-red-600">
                                        R$ {selectedProductMarkup.cost_price?.toLocaleString('pt-BR', {minimumFractionDigits: 2})}
                                      </p>
                                      {selectedProductMarkup.cost_date && (
                                        <p className="text-[10px] text-slate-400">NF {selectedProductMarkup.cost_date.slice(0, 10)}</p>
                                      )}
                                    </div>
                                    <div className="p-2 bg-white rounded-lg">
                                      <p className="text-xs text-slate-500">Venda Média</p>
                                      <p className="text-lg font-bold text-emerald-600">
                                        R$ {selectedProductMarkup.avg_sale_price?.toLocaleString('pt-BR', {minimumFractionDigits: 2})}
                                      </p>
                                      <p className="text-[10px] text-slate-400">{selectedProductMarkup.total_sales_count} vendas</p>
                                    </div>
                                    <div className="p-2 bg-white rounded-lg border-2 border-emerald-300">
                                      <p className="text-xs text-emerald-600 font-medium">Markup</p>
                                      <p className={`text-xl font-bold ${
                                        selectedProductMarkup.markup_percent >= 30 
                                          ? 'text-emerald-600' 
                                          : selectedProductMarkup.markup_percent >= 15 
                                            ? 'text-amber-600' 
                                            : 'text-red-600'
                                      }`}>
                                        {selectedProductMarkup.markup_percent?.toFixed(1)}%
                                      </p>
                                      <p className="text-[10px] text-slate-400">Fator: {selectedProductMarkup.markup_fator?.toFixed(2)}x</p>
                                    </div>
                                    <div className="p-2 bg-white rounded-lg">
                                      <p className="text-xs text-slate-500">Lucro Unit.</p>
                                      <p className="text-lg font-bold text-emerald-600">
                                        R$ {selectedProductMarkup.lucro_unitario?.toLocaleString('pt-BR', {minimumFractionDigits: 2})}
                                      </p>
                                    </div>
                                  </div>
                                ) : (
                                  <div className="p-2 bg-white/50 rounded-lg text-center">
                                    <p className="text-xs text-slate-500">Sem dados de custo disponível</p>
                                    <p className="text-[10px] text-slate-400">Importe uma NF-e com este produto</p>
                                  </div>
                                )}
                              </div>
                              
                              {/* Stock & Sales Rhythm Card */}
                              <div className="p-3 bg-gradient-to-r from-blue-50 to-cyan-50 rounded-lg border border-blue-200">
                                <h5 className="text-sm font-semibold text-slate-700 mb-2 flex items-center gap-2">
                                  <Package className="w-4 h-4 text-blue-600" />
                                  Estoque e Ritmo de Vendas
                                </h5>
                                {loadingProductStock ? (
                                  <div className="flex items-center gap-2 text-blue-600 py-2">
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                    <span className="text-sm">Carregando...</span>
                                  </div>
                                ) : selectedProductStock?.success ? (
                                  <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                                    <div className="p-2 bg-white rounded-lg">
                                      <p className="text-xs text-slate-500">Estoque Atual</p>
                                      <StockDualValue product={selectedProductStock} size="md" />
                                      <p className="text-[10px] text-slate-400">{selectedProductStock.unit || 'UN'}</p>
                                    </div>
                                    <div className="p-2 bg-white rounded-lg">
                                      <p className="text-xs text-slate-500">Vendas 7 dias</p>
                                      <p className="text-xl font-bold text-blue-600">
                                        {selectedProductStock.sales_last_7_days?.toLocaleString('pt-BR')}
                                      </p>
                                      <p className="text-[10px] text-slate-400">{selectedProductStock.sales_count_7_days || 0} vendas</p>
                                    </div>
                                    <div className="p-2 bg-white rounded-lg">
                                      <p className="text-xs text-slate-500">Média/dia</p>
                                      <p className="text-xl font-bold text-blue-600">
                                        {selectedProductStock.daily_average?.toFixed(1)}
                                      </p>
                                      <p className="text-[10px] text-slate-400">un/dia</p>
                                    </div>
                                    <div className="p-2 bg-white rounded-lg border-2 border-blue-300">
                                      <p className="text-xs text-blue-600 font-medium">Dias de Estoque</p>
                                      <p className={`text-xl font-bold ${
                                        selectedProductStock.days_of_stock > 14 
                                          ? 'text-emerald-600' 
                                          : selectedProductStock.days_of_stock > 7 
                                            ? 'text-amber-600' 
                                            : 'text-red-600'
                                      }`}>
                                        {selectedProductStock.days_of_stock?.toFixed(0) || '∞'}
                                      </p>
                                      <p className="text-[10px] text-slate-400">previsão</p>
                                    </div>
                                    <div className="p-2 bg-white rounded-lg">
                                      <p className="text-xs text-slate-500">Vendas 30 dias</p>
                                      <p className="text-xl font-bold text-slate-700">
                                        {selectedProductStock.sales_last_30_days?.toLocaleString('pt-BR')}
                                      </p>
                                      <p className="text-[10px] text-slate-400">{selectedProductStock.sales_count_30_days || 0} vendas</p>
                                    </div>
                                  </div>
                                ) : (
                                  <div className="p-2 bg-white/50 rounded-lg text-center">
                                    <p className="text-xs text-slate-500">Sem dados de estoque disponível</p>
                                    <p className="text-[10px] text-slate-400">Importe uma NF-e com este produto</p>
                                  </div>
                                )}
                              </div>
                              
                              {/* Daily Sales Chart Card */}
                              <div className="p-3 bg-gradient-to-r from-indigo-50 to-violet-50 rounded-lg border border-indigo-200" data-testid="daily-sales-card">
                                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-3">
                                  <h5 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                                    <TrendingUp className="w-4 h-4 text-indigo-600" />
                                    Vendas Diárias
                                  </h5>
                                  {/* Period Selector */}
                                  <div className="flex items-center gap-2">
                                    <select 
                                      value={productDailyPeriod}
                                      onChange={(e) => {
                                        const newPeriod = e.target.value;
                                        setProductDailyPeriod(newPeriod);
                                        if (newPeriod !== "custom") {
                                          loadProductDailySalesForPeriod(newPeriod);
                                        }
                                      }}
                                      className="text-xs border border-indigo-200 rounded-md px-2 py-1 bg-white text-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                      data-testid="daily-period-selector"
                                    >
                                      <option value="7">Últimos 7 dias</option>
                                      <option value="15">Últimos 15 dias</option>
                                      <option value="30">Últimos 30 dias</option>
                                      <option value="60">Últimos 60 dias</option>
                                      <option value="90">Últimos 90 dias</option>
                                      <option value="custom">Personalizado</option>
                                    </select>
                                    {productDailyPeriod === "custom" && (
                                      <Popover>
                                        <PopoverTrigger asChild>
                                          <Button variant="outline" size="sm" className="h-7 text-xs">
                                            {productDailyCustomStart && productDailyCustomEnd 
                                              ? `${new Date(productDailyCustomStart).toLocaleDateString('pt-BR', {day: '2-digit', month: '2-digit'})} - ${new Date(productDailyCustomEnd).toLocaleDateString('pt-BR', {day: '2-digit', month: '2-digit'})}`
                                              : "Selecionar"
                                            }
                                            <ChevronDown className="w-3 h-3 ml-1" />
                                          </Button>
                                        </PopoverTrigger>
                                        <PopoverContent className="w-auto p-3" align="end">
                                          <div className="space-y-3">
                                            <div className="grid grid-cols-2 gap-2">
                                              <div>
                                                <Label className="text-xs text-slate-600">Data Inicial</Label>
                                                <input 
                                                  type="date" 
                                                  value={productDailyCustomStart || ''}
                                                  onChange={(e) => setProductDailyCustomStart(e.target.value)}
                                                  className="w-full text-xs border rounded px-2 py-1"
                                                />
                                              </div>
                                              <div>
                                                <Label className="text-xs text-slate-600">Data Final</Label>
                                                <input 
                                                  type="date" 
                                                  value={productDailyCustomEnd || ''}
                                                  onChange={(e) => setProductDailyCustomEnd(e.target.value)}
                                                  className="w-full text-xs border rounded px-2 py-1"
                                                />
                                              </div>
                                            </div>
                                            <Button 
                                              size="sm"
                                              className="w-full bg-indigo-600 hover:bg-indigo-700 text-xs"
                                              onClick={() => {
                                                if (productDailyCustomStart && productDailyCustomEnd) {
                                                  loadProductDailySalesForPeriod("custom", productDailyCustomStart, productDailyCustomEnd);
                                                }
                                              }}
                                              disabled={!productDailyCustomStart || !productDailyCustomEnd}
                                            >
                                              Aplicar
                                            </Button>
                                          </div>
                                        </PopoverContent>
                                      </Popover>
                                    )}
                                  </div>
                                </div>
                                
                                {loadingProductDailySales ? (
                                  <div className="flex items-center gap-2 text-indigo-600 py-8 justify-center">
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                    <span className="text-sm">Carregando vendas diárias...</span>
                                  </div>
                                ) : productDailySales?.success && productDailySales?.daily_sales?.length > 0 ? (
                                  <div className="space-y-3">
                                    {/* Summary Stats */}
                                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                                      <div className="p-2 bg-white rounded-lg border border-indigo-100">
                                        <p className="text-[10px] text-slate-500">Total Quantidade</p>
                                        <p className="text-lg font-bold text-indigo-600">
                                          {productDailySales.total_quantity?.toFixed(0) || 0} <span className="text-xs font-normal">un</span>
                                        </p>
                                      </div>
                                      <div className="p-2 bg-white rounded-lg border border-indigo-100">
                                        <p className="text-[10px] text-slate-500">Total Faturamento</p>
                                        <p className="text-lg font-bold text-emerald-600">
                                          R$ {productDailySales.total_revenue?.toLocaleString('pt-BR', {minimumFractionDigits: 0, maximumFractionDigits: 0}) || 0}
                                        </p>
                                      </div>
                                      <div className="p-2 bg-white rounded-lg border border-indigo-100">
                                        <p className="text-[10px] text-slate-500">Média Diária</p>
                                        <p className="text-lg font-bold text-blue-600">
                                          {productDailySales.avg_daily_quantity?.toFixed(1) || 0} <span className="text-xs font-normal">un</span>
                                        </p>
                                      </div>
                                      <div className="p-2 bg-white rounded-lg border border-indigo-100">
                                        <p className="text-[10px] text-slate-500">Dias com Vendas</p>
                                        <p className="text-lg font-bold text-amber-600">
                                          {productDailySales.days_with_sales || 0}<span className="text-xs font-normal text-slate-400">/{productDailySales.total_days || 0}</span>
                                        </p>
                                      </div>
                                    </div>
                                    
                                    {/* Chart */}
                                    <div className="h-[200px] mt-2">
                                      <ResponsiveContainer width="100%" height="100%">
                                        <ComposedChart 
                                          data={productDailySales.daily_sales}
                                          margin={{ top: 5, right: 5, left: -10, bottom: 25 }}
                                        >
                                          <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                                          <XAxis 
                                            dataKey="date" 
                                            tickFormatter={(date) => {
                                              const d = new Date(date);
                                              return `${d.getDate()}/${d.getMonth()+1}`;
                                            }}
                                            tick={{ fontSize: 9, fill: '#666' }}
                                            angle={-45}
                                            textAnchor="end"
                                            height={40}
                                            interval={Math.max(0, Math.floor(productDailySales.daily_sales.length / 10) - 1)}
                                          />
                                          <YAxis 
                                            yAxisId="left"
                                            tick={{ fontSize: 9, fill: '#666' }}
                                            width={30}
                                          />
                                          <YAxis 
                                            yAxisId="right"
                                            orientation="right"
                                            tick={{ fontSize: 9, fill: '#666' }}
                                            tickFormatter={(v) => `R$${v >= 1000 ? (v/1000).toFixed(0) + 'k' : v}`}
                                            width={40}
                                          />
                                          <Tooltip 
                                            contentStyle={{ 
                                              backgroundColor: 'white', 
                                              border: '1px solid #e0e0e0',
                                              borderRadius: '8px',
                                              fontSize: '11px'
                                            }}
                                            formatter={(value, name) => {
                                              if (name === 'quantity') return [`${value.toFixed(1)} un`, 'Quantidade'];
                                              if (name === 'revenue') return [`R$ ${value.toLocaleString('pt-BR', {minimumFractionDigits: 2})}`, 'Faturamento'];
                                              return [value, name];
                                            }}
                                            labelFormatter={(label) => {
                                              const date = new Date(label);
                                              return date.toLocaleDateString('pt-BR', { weekday: 'short', day: '2-digit', month: 'short' });
                                            }}
                                          />
                                          <Bar 
                                            yAxisId="left"
                                            dataKey="quantity" 
                                            fill="#6366f1" 
                                            radius={[2, 2, 0, 0]}
                                            name="quantity"
                                          />
                                          <Line 
                                            yAxisId="right"
                                            type="monotone" 
                                            dataKey="revenue" 
                                            stroke="#10b981" 
                                            strokeWidth={2}
                                            dot={false}
                                            name="revenue"
                                          />
                                        </ComposedChart>
                                      </ResponsiveContainer>
                                    </div>
                                    
                                    {/* Period Info */}
                                    <p className="text-[10px] text-slate-400 text-right">
                                      Período: {productDailySales.period?.start && new Date(productDailySales.period.start).toLocaleDateString('pt-BR')} a {productDailySales.period?.end && new Date(productDailySales.period.end).toLocaleDateString('pt-BR')}
                                    </p>
                                  </div>
                                ) : (
                                  <div className="p-2 bg-white/50 rounded-lg text-center py-6">
                                    <p className="text-xs text-slate-500">Sem vendas no período selecionado</p>
                                  </div>
                                )}
                              </div>
                              
                              {/* Basket Analysis Card - Products frequently bought together */}
                              <div className="p-3 bg-gradient-to-r from-orange-50 to-amber-50 rounded-lg border border-orange-200">
                                <h5 className="text-sm font-semibold text-slate-700 mb-2 flex items-center gap-2">
                                  <ShoppingCart className="w-4 h-4 text-orange-600" />
                                  Produtos Comprados Juntos
                                </h5>
                                {loadingBasketAnalysis ? (
                                  <div className="flex items-center gap-2 text-orange-600 py-2">
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                    <span className="text-sm">Analisando cesta...</span>
                                  </div>
                                ) : basketAnalysis?.has_data && basketAnalysis?.basket_products?.length > 0 ? (
                                  <div className="space-y-2">
                                    <p className="text-xs text-slate-500 mb-2">
                                      Top 5 produtos mais frequentes nas mesmas compras ({basketAnalysis.total_orders} pedidos analisados)
                                    </p>
                                    <div className="space-y-1.5">
                                      {basketAnalysis.basket_products.map((bp, idx) => (
                                        <div key={bp.ean} className="flex items-center justify-between p-2 bg-white rounded-lg hover:bg-orange-50 transition-colors">
                                          <div className="flex items-center gap-2 flex-1 min-w-0">
                                            <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                                              idx === 0 ? 'bg-orange-500 text-white' : 
                                              idx === 1 ? 'bg-orange-400 text-white' : 
                                              idx === 2 ? 'bg-orange-300 text-orange-800' : 
                                              'bg-orange-100 text-orange-700'
                                            }`}>
                                              {idx + 1}
                                            </span>
                                            <div className="min-w-0 flex-1">
                                              <p className="text-sm font-medium text-slate-700 truncate">{bp.produto}</p>
                                              <p className="text-[10px] text-slate-400">EAN: {bp.ean}</p>
                                            </div>
                                          </div>
                                          <div className="flex items-center gap-3 flex-shrink-0">
                                            <div className="text-right">
                                              <p className="text-sm font-bold text-orange-600">{bp.affinity_score}%</p>
                                              <p className="text-[10px] text-slate-400">afinidade</p>
                                            </div>
                                            <div className="text-right">
                                              <p className="text-sm font-semibold text-slate-600">{bp.co_occurrence_count}x</p>
                                              <p className="text-[10px] text-slate-400">juntos</p>
                                            </div>
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                    <p className="text-[10px] text-slate-400 italic mt-2">
                                      Afinidade = % de pedidos com o produto selecionado que também contêm este item
                                    </p>
                                  </div>
                                ) : (
                                  <div className="p-2 bg-white/50 rounded-lg text-center">
                                    <p className="text-xs text-slate-500">Sem dados de cesta disponível</p>
                                    <p className="text-[10px] text-slate-400">Não há pedidos suficientes para análise</p>
                                  </div>
                                )}
                              </div>
                              
                              <p className="text-xs text-slate-500 italic">
                                * Ticket Médio = Fat. total dos {productTicket.pedidos_com_produto} pedidos com este produto / {productTicket.pedidos_com_produto} pedidos
                              </p>
                            </div>
                          ) : (
                            <p className="text-sm text-slate-500">Não foi possível carregar os dados</p>
                          )}
                        </div>
                      )}

                      {/* Ticket Analysis */}
                      {ticketAnalysis?.has_data && (
                        <div className="p-4 bg-white rounded-xl border border-slate-200">
                          <h4 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
                            <BarChart3 className="w-4 h-4 text-[#4A90E2]" />
                            Análise de Ticket Médio
                            <span className="text-xs text-slate-400 font-normal ml-2">
                              (Ticket Médio: R$ {ticketAnalysis.ticket_medio?.toLocaleString('pt-BR', {minimumFractionDigits: 2})})
                            </span>
                          </h4>
                          
                          {/* Above/Below Average */}
                          <div className="grid grid-cols-2 gap-4 mb-6">
                            <div className="p-4 rounded-xl bg-emerald-50 border border-emerald-200">
                              <div className="flex items-center gap-2 mb-2">
                                <TrendingUp className="w-5 h-5 text-emerald-600" />
                                <span className="text-sm font-medium text-emerald-700">Acima da Média</span>
                              </div>
                              <p className="text-3xl font-bold text-emerald-600">{ticketAnalysis.above_average}</p>
                              <p className="text-sm text-emerald-600/70">{ticketAnalysis.above_average_perc}% dos pedidos</p>
                            </div>
                            
                            <div className="p-4 rounded-xl bg-amber-50 border border-amber-200">
                              <div className="flex items-center gap-2 mb-2">
                                <TrendingDown className="w-5 h-5 text-amber-600" />
                                <span className="text-sm font-medium text-amber-700">Abaixo da Média</span>
                              </div>
                              <p className="text-3xl font-bold text-amber-600">{ticketAnalysis.below_average}</p>
                              <p className="text-sm text-amber-600/70">{ticketAnalysis.below_average_perc}% dos pedidos</p>
                            </div>
                          </div>

                          {/* Ranges Distribution with Pareto */}
                          <h5 className="text-sm font-medium text-slate-700 mb-3">Distribuição por Faixa de Valor (Pareto)</h5>
                          
                          {/* Table Header */}
                          <div className="grid grid-cols-12 gap-2 mb-2 px-2 text-xs font-semibold text-slate-500 border-b border-slate-200 pb-2">
                            <div className="col-span-3">Faixa</div>
                            <div className="col-span-2 text-center">Pedidos</div>
                            <div className="col-span-1 text-center">%</div>
                            <div className="col-span-2 text-center">Acum. Qtd</div>
                            <div className="col-span-2 text-center">Acum. Valor</div>
                            <div className="col-span-2">Barra</div>
                          </div>
                          
                          <div className="space-y-2">
                            {ticketAnalysis.ranges?.map((range, index) => {
                              const maxCount = Math.max(...(ticketAnalysis.ranges?.map(r => r.count) || [1]));
                              const barWidth = maxCount > 0 ? (range.count / maxCount) * 100 : 0;
                              const colors = [
                                "bg-rose-400", "bg-orange-400", "bg-amber-400", "bg-yellow-400",
                                "bg-lime-400", "bg-green-400", "bg-emerald-400", "bg-teal-400",
                                "bg-cyan-400", "bg-sky-400", "bg-[#4A90E2]"
                              ];
                              const isSelected = selectedTicketRange?.label === range.label;
                              
                              return (
                                <div 
                                  key={index} 
                                  onClick={() => loadTicketRangeProducts(range)}
                                  className={`grid grid-cols-12 gap-2 items-center px-2 py-1.5 rounded-lg cursor-pointer transition-all group ${
                                    isSelected 
                                      ? 'bg-blue-50 border border-blue-300 shadow-sm' 
                                      : 'hover:bg-slate-50'
                                  }`}
                                >
                                  <div className={`col-span-3 text-sm font-medium truncate ${isSelected ? 'text-blue-700' : 'text-slate-700'}`}>
                                    {range.label}
                                  </div>
                                  <div className="col-span-2 text-sm text-slate-600 text-center">{range.count}</div>
                                  <div className="col-span-1 text-xs text-slate-400 text-center">{range.percentage}%</div>
                                  <div className="col-span-2 text-center">
                                    <span className="text-sm font-semibold text-purple-600">{range.accumulated_count_perc}%</span>
                                  </div>
                                  <div className="col-span-2 text-center">
                                    <span className="text-sm font-semibold text-emerald-600">{range.accumulated_value_perc}%</span>
                                  </div>
                                  <div className="col-span-2 flex items-center gap-1">
                                    <div className="flex-1 bg-slate-100 rounded-full h-2.5 overflow-hidden">
                                      <div 
                                        className={`h-full rounded-full transition-all duration-500 ${colors[index] || 'bg-slate-400'}`}
                                        style={{ width: `${barWidth}%` }}
                                      />
                                    </div>
                                    <ChevronRight className={`w-4 h-4 transition-colors ${isSelected ? 'text-blue-500' : 'text-slate-300 group-hover:text-slate-500'}`} />
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                          
                          {/* Legend */}
                          <div className="mt-4 pt-3 border-t border-slate-200 flex items-center justify-center gap-6 text-xs text-slate-500">
                            <span className="flex items-center gap-1">
                              <div className="w-3 h-3 rounded bg-purple-500"></div>
                              Acum. Qtd = % acumulado de pedidos
                            </span>
                            <span className="flex items-center gap-1">
                              <div className="w-3 h-3 rounded bg-emerald-500"></div>
                              Acum. Valor = % acumulado do faturamento
                            </span>
                            <span className="text-slate-400 ml-2">• Clique em uma faixa para ver os top produtos</span>
                          </div>
                          
                          {/* Ticket Range Products Analysis */}
                          {selectedTicketRange && (
                            <div className="mt-4 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border border-blue-200">
                              <div className="flex items-center justify-between mb-3">
                                <h5 className="font-semibold text-slate-800 flex items-center gap-2">
                                  <ShoppingCart className="w-4 h-4 text-blue-600" />
                                  Top 10 Produtos - Tickets <span className="text-blue-600">{selectedTicketRange.label}</span>
                                </h5>
                                <Button variant="ghost" size="sm" onClick={() => { setSelectedTicketRange(null); setTicketRangeProducts(null); }}>
                                  <X className="w-4 h-4" />
                                </Button>
                              </div>
                              
                              {loadingTicketRangeProducts ? (
                                <div className="flex items-center gap-2 text-blue-600 py-4">
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                  <span className="text-sm">Carregando...</span>
                                </div>
                              ) : ticketRangeProducts?.has_data ? (
                                <div className="space-y-4">
                                  {/* Summary */}
                                  <div className="flex gap-4 text-sm">
                                    <div className="px-3 py-1.5 bg-white rounded-lg">
                                      <span className="text-slate-500">Pedidos:</span>
                                      <span className="ml-1 font-semibold text-blue-700">{ticketRangeProducts.orders_count}</span>
                                    </div>
                                    <div className="px-3 py-1.5 bg-white rounded-lg">
                                      <span className="text-slate-500">Faturamento:</span>
                                      <span className="ml-1 font-semibold text-emerald-700">R$ {ticketRangeProducts.total_value?.toLocaleString('pt-BR', {minimumFractionDigits: 2})}</span>
                                    </div>
                                  </div>
                                  
                                  {/* Two columns - by value and by quantity */}
                                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                                    {/* By Value */}
                                    <div className="bg-white rounded-lg p-3">
                                      <h6 className="text-sm font-medium text-slate-700 mb-2 flex items-center gap-1">
                                        <TrendingUp className="w-4 h-4 text-emerald-500" />
                                        Por Valor
                                      </h6>
                                      <div className="space-y-1 max-h-48 overflow-y-auto">
                                        {ticketRangeProducts.by_value?.map((p, i) => (
                                          <div key={i} className="flex items-center justify-between p-1.5 rounded hover:bg-slate-50 text-sm">
                                            <span className="text-slate-600 truncate flex-1">{i+1}. {p.produto}</span>
                                            <div className="flex items-center gap-2 ml-2">
                                              <span className="font-medium text-emerald-600">R$ {p.total_liquido?.toLocaleString('pt-BR', {minimumFractionDigits: 2})}</span>
                                              <span className="text-xs text-slate-400">{p.perc_value}%</span>
                                            </div>
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                    
                                    {/* By Quantity */}
                                    <div className="bg-white rounded-lg p-3">
                                      <h6 className="text-sm font-medium text-slate-700 mb-2 flex items-center gap-1">
                                        <Package className="w-4 h-4 text-purple-500" />
                                        Por Quantidade
                                      </h6>
                                      <div className="space-y-1 max-h-48 overflow-y-auto">
                                        {ticketRangeProducts.by_quantity?.map((p, i) => (
                                          <div key={i} className="flex items-center justify-between p-1.5 rounded hover:bg-slate-50 text-sm">
                                            <span className="text-slate-600 truncate flex-1">{i+1}. {p.produto}</span>
                                            <div className="flex items-center gap-2 ml-2">
                                              <span className="font-medium text-purple-600">{p.quantidade?.toLocaleString('pt-BR')} un</span>
                                              <span className="text-xs text-slate-400">{p.perc_qty}%</span>
                                            </div>
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              ) : (
                                <p className="text-sm text-slate-500 py-4">Nenhum dado encontrado para esta faixa</p>
                              )}
                            </div>
                          )}
                        </div>
                      )}

                      {/* Sales by Date (below Top Products) */}
                      <div className="p-4 bg-white rounded-xl border border-slate-200">
                        <h4 className="font-semibold text-slate-800 mb-3 flex items-center gap-2">
                          <Calendar className="w-4 h-4 text-blue-600" />
                          Vendas por {salesGroupBy === 'day' ? 'Dia' : salesGroupBy === 'week' ? 'Semana' : 'Mês'}
                          <span className="text-xs text-slate-400 font-normal">(mais recente primeiro)</span>
                        </h4>
                        <div className="space-y-2 max-h-64 overflow-y-auto">
                          {salesDashboard.by_date?.map((d, i) => (
                            <div key={i} className="flex items-center justify-between p-2 bg-slate-50 rounded-lg">
                              <span className="text-sm text-slate-600 font-medium">{d.data}</span>
                              <div className="flex items-center gap-4">
                                <span className="text-xs text-slate-500">{d.num_pedidos} ped. | TM: R$ {d.ticket_medio?.toLocaleString('pt-BR', {minimumFractionDigits: 2})}</span>
                                <span className="text-sm font-semibold text-blue-600">R$ {d.total_liquido?.toLocaleString('pt-BR', {minimumFractionDigits: 2})}</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* AI Chat */}
                      <div className="p-4 bg-gradient-to-br from-purple-50 to-white rounded-xl border border-purple-200">
                        <h4 className="font-semibold text-slate-800 mb-3 flex items-center gap-2">
                          <MessageSquare className="w-4 h-4 text-purple-600" />
                          Converse com seus Dados (IA)
                        </h4>
                        
                        {/* Chat Messages */}
                        <div className="bg-white rounded-lg border border-slate-200 p-3 mb-3 max-h-64 overflow-y-auto space-y-3">
                          {salesChatMessages.length === 0 ? (
                            <p className="text-sm text-slate-400 text-center py-4">
                              Faça perguntas sobre suas vendas. Ex: "Qual produto mais vendeu?" ou "Qual o faturamento total?"
                            </p>
                          ) : salesChatMessages.map((msg, i) => (
                            <div key={i} className={`p-3 rounded-lg ${msg.role === 'user' ? 'bg-purple-100 ml-8' : 'bg-slate-100 mr-8'}`}>
                              <p className="text-sm text-slate-700 whitespace-pre-wrap">{msg.content}</p>
                            </div>
                          ))}
                          {salesChatLoading && (
                            <div className="flex items-center gap-2 text-purple-600">
                              <Loader2 className="w-4 h-4 animate-spin" />
                              <span className="text-sm">Analisando dados...</span>
                            </div>
                          )}
                        </div>
                        
                        {/* Chat Input */}
                        <div className="flex gap-2">
                          <Input
                            value={salesChatInput}
                            onChange={(e) => setSalesChatInput(e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' && sendSalesChatMessage()}
                            placeholder="Pergunte algo sobre suas vendas..."
                            className="flex-1"
                          />
                          <Button onClick={sendSalesChatMessage} disabled={salesChatLoading} className="bg-purple-600 hover:bg-purple-700">
                            <Send className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}

                    {/* Empty State */}
                    {(!salesDashboard || salesDashboard.total_records === 0) && !salesPreview && !loadingComparison && (
                      <div className="text-center py-12 text-slate-500">
                        <BarChart3 className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                        <p className="text-lg font-medium">Nenhum dado de vendas</p>
                        <p className="text-sm">Importe um arquivo CSV/Excel para começar</p>
                      </div>
                    )}
                  </>
                )}
                {/* End of Tab Content: Vendas Diárias */}

                {/* Tab Content: Importar Dados */}
                {dashboardTab === "importar" && (
                  <ImportTab
                    stores={stores}
                    pgStores={pgStores}
                    importStoreId={importStoreId}
                    setImportStoreId={setImportStoreId}
                    onImportSuccess={() => {
                      loadSalesDashboard();
                      loadDailyComparison();
                      loadSalesImportHistory();
                      loadChartData();
                      loadTicketAnalysis();
                    }}
                    salesImportHistory={salesImportHistory}
                    onDeleteImport={async (importId) => {
                      if (!window.confirm("Tem certeza que deseja excluir esta importação? Todos os registros serão removidos.")) {
                        return;
                      }
                      try {
                        const response = await axios.delete(`${API}/sales/imports/${importId}`);
                        if (response.data.success) {
                          toast.success(response.data.message);
                          loadSalesImportHistory();
                          loadSalesDashboard();
                          loadDailyComparison();
                          loadChartData();
                          loadTicketAnalysis();
                        }
                      } catch (error) {
                        toast.error("Erro ao excluir importação");
                      }
                    }}
                  />
                )}
                </>
                )}
                  </>
                )}
              </div>
            )}

            {/* ==================== ESTOQUE VIRTUAL (Refactored) ==================== */}
            {activeSection === "estoque" && (
              <StockPage 
                onOpenPriceModal={(product) => {
                  openPriceModal(product);
                }}
              />
            )}

            {/* ==================== P&L GERENCIAL ==================== */}
            {activeSection === "pnl" && (
              <PnLPage />
            )}

            {/* ==================== COMPRAS (Previsões e Pedidos) ==================== */}
            {activeSection === "compras" && (
              <div className="space-y-4">
                <Tabs defaultValue="previsoes" className="w-full">
                  <TabsList className="grid w-full max-w-md grid-cols-2 bg-slate-100">
                    <TabsTrigger value="previsoes" className="data-[state=active]:bg-white">
                      <TrendingDown className="w-4 h-4 mr-2" />
                      Previsões
                    </TabsTrigger>
                    <TabsTrigger value="pedidos" className="data-[state=active]:bg-white">
                      <FileText className="w-4 h-4 mr-2" />
                      Pedidos
                    </TabsTrigger>
                  </TabsList>
                  
                  <TabsContent value="previsoes" className="mt-4">
                    <PurchasingTab storeId={selectedStoreIds.length === 1 ? selectedStoreIds[0] : 'vencidinho_cangaiba'} />
                  </TabsContent>
                  
                  <TabsContent value="pedidos" className="mt-4">
                    <PurchaseOrdersTab />
                  </TabsContent>
                </Tabs>
              </div>
            )}

            {/* ==================== COBRANÇAS PIX ==================== */}
            {activeSection === "cobrancas" && (
              <CobrancasPage />
            )}

            {/* ==================== CONTROLE DE CAIXA ==================== */}
            {activeSection === "caixa" && (
              <CashControlPage />
            )}

            {/* ==================== RESULTS (for Precificação) ==================== */}
            {activeSection === "precificacao" && results.length > 0 && (
              <Card className="shadow-[0_2px_8px_rgba(0,0,0,0.04)] border-slate-200 mt-6" data-testid="results-section">
                <CardHeader className="pb-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="text-xl font-semibold text-slate-900 tracking-tight font-[Manrope]">Resultados</CardTitle>
                      <CardDescription className="text-slate-500">{results.filter(r => r.status === "found").length} de {results.length} produtos encontrados</CardDescription>
                    </div>
                    <div className="flex gap-2">
                      <Badge variant="outline" className="border-emerald-200 text-emerald-700 bg-emerald-50"><CheckCircle className="w-3 h-3 mr-1" />{results.filter(r => r.status === "found").length} encontrados</Badge>
                      <Badge variant="outline" className="border-red-200 text-red-700 bg-red-50"><XCircle className="w-3 h-3 mr-1" />{results.filter(r => r.status === "not_found").length} não encontrados</Badge>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="rounded-xl border border-slate-200 overflow-hidden">
                    <Table data-testid="results-table">
                      <TableHeader>
                    <TableRow className="bg-slate-50 hover:bg-slate-50">
                      <TableHead className="font-semibold text-slate-700">Imagem</TableHead>
                      <TableHead className="font-semibold text-slate-700">EAN</TableHead>
                      <TableHead className="font-semibold text-slate-700">Nome do Produto</TableHead>
                      <TableHead className="font-semibold text-slate-700">Marca</TableHead>
                      <TableHead className="font-semibold text-slate-700">Custo NF-e</TableHead>
                      <TableHead className="font-semibold text-slate-700">Status</TableHead>
                      <TableHead className="font-semibold text-slate-700 text-right">Ações</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {results.map((result, index) => (
                      <TableRow key={result.id || index} className="hover:bg-slate-50/50" data-testid={`result-row-${index}`}>
                        <TableCell>
                          <div 
                            className="w-16 h-16 rounded-lg border border-slate-100 bg-white p-1 flex items-center justify-center overflow-hidden cursor-pointer hover:border-[#4A90E2] hover:shadow-lg transition-all group relative" 
                            onClick={() => result.image_url ? openImageViewer(result.image_url, result) : null}
                            onPaste={async (e) => {
                              const items = e.clipboardData?.items;
                              if (!items) return;
                              for (const item of items) {
                                if (item.type.startsWith('image/')) {
                                  const blob = item.getAsFile();
                                  const reader = new FileReader();
                                  reader.onload = async () => {
                                    const base64 = reader.result;
                                    // Update result with pasted image
                                    setResults(prev => prev.map((r, i) => 
                                      i === index ? { ...r, image_base64: base64, image_url: base64 } : r
                                    ));
                                    // Also update stock product if exists
                                    if (result.ean) {
                                      try {
                                        await axios.put(`${API}/stock/products/${result.ean}`, { image_base64: base64 });
                                        toast.success("Foto atualizada no estoque!");
                                      } catch (err) {
                                        console.warn("Could not update stock image:", err);
                                      }
                                    }
                                    toast.success("Foto colada com sucesso!");
                                  };
                                  reader.readAsDataURL(blob);
                                  break;
                                }
                              }
                            }}
                            tabIndex={0}
                            title={result.image_url ? "Clique para ampliar" : "Clique e cole (Ctrl+V) uma imagem"}
                            data-testid={`image-thumbnail-${index}`}
                          >
                            {result.image_url || result.image_base64 ? (
                              <>
                                <img src={result.image_base64 || result.image_url} alt={result.product_name || "Produto"} className="w-full h-full object-contain" />
                                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none"><ZoomIn className="w-5 h-5 text-white drop-shadow-lg" /></div>
                              </>
                            ) : (
                              <div className="text-center">
                                <Clipboard className="w-6 h-6 text-slate-300 mx-auto" />
                                <span className="text-[8px] text-slate-400">Cole foto</span>
                              </div>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <code className="text-sm font-mono bg-slate-100 px-2 py-1 rounded">{result.ean}</code>
                            {result.from_cache && (
                              <span className="text-xs px-1.5 py-0.5 bg-blue-100 text-blue-600 rounded" title="Resultado do cache (não consumiu consulta)">cache</span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="max-w-xs">
                            <p className="font-medium text-slate-900 truncate">{result.product_name || "-"}</p>
                            {result.expected_name && <p className="text-xs text-slate-500 truncate mt-1">Esperado: {result.expected_name}</p>}
                          </div>
                        </TableCell>
                        <TableCell><span className="text-slate-600">{result.brand || "-"}</span></TableCell>
                        <TableCell>
                          {result.nfe_unit_cost ? (
                            <div className="text-center">
                              <span className="text-emerald-600 font-semibold">R$ {result.nfe_unit_cost.toFixed(2)}</span>
                              {result.nfe_original_unit && result.nfe_original_unit !== "UN" && (
                                <p className="text-xs text-slate-400">convertido de {result.nfe_original_unit}</p>
                              )}
                            </div>
                          ) : (
                            <span className="text-slate-300">-</span>
                          )}
                        </TableCell>
                        <TableCell>{getStatusBadge(result.status, result.match_score)}</TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            {result.status === "found" && (
                              <Button variant="ghost" size="sm" onClick={() => openPriceModal(result)} className="text-emerald-600 hover:text-emerald-700 hover:bg-emerald-100" title="Pesquisar preços"><DollarSign className="w-4 h-4" /></Button>
                            )}
                            {result.status === "not_found" && (
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                onClick={() => searchImageByName(result.ean, result.match_analysis?.replace('Produto via IA (Gemini). Categoria:', '').trim() || result.expected_name || `Produto EAN ${result.ean}`)} 
                                className="text-amber-600 hover:text-amber-700 hover:bg-amber-100" 
                                title="Buscar imagem por nome"
                                disabled={searchingImage === result.ean}
                              >
                                {searchingImage === result.ean ? (
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                  <Search className="w-4 h-4" />
                                )}
                              </Button>
                            )}
                            {(result.image_url || result.image_base64) && (
                              <Button variant="ghost" size="sm" onClick={() => copyImageToClipboard(result.image_base64 || result.image_url)} className="text-slate-500 hover:text-slate-700 hover:bg-slate-100" title="Copiar imagem"><Copy className="w-4 h-4" /></Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
                <p className="text-xs text-slate-400 mt-3 text-center">Dica: Clique com botão direito na imagem → "Copiar imagem"</p>
              </CardContent>
            </Card>
            )}

            {/* Empty State for Precificação */}
            {activeSection === "precificacao" && results.length === 0 && !loading && (
              <div className="text-center py-16" data-testid="empty-state">
                <div className="w-24 h-24 mx-auto rounded-2xl bg-slate-100 flex items-center justify-center mb-6"><Barcode className="w-12 h-12 text-slate-400" /></div>
                <h3 className="text-lg font-medium text-slate-700 mb-2">Nenhuma busca realizada</h3>
                <p className="text-slate-500 max-w-md mx-auto">Digite um código EAN ou faça upload de uma planilha Excel para começar a buscar imagens de produtos.</p>
              </div>
            )}
          </main>

          {/* Footer */}
          <footer className="border-t border-slate-200 py-6 bg-white">
            <p className="text-center text-sm text-slate-500">Powered by Bluesoft Cosmos API</p>
          </footer>
        </div>
      </div>
    </div>
  );
}

export default App;
